/**
 * Subsea Enclosure Builder
 * Handles component selection, compatibility logic, and cart integration
 */

window.theme = window.theme || {};

theme.SubseaBuilder = (function() {
  'use strict';

  var selectors = {
    container: '[data-section-type="subsea-enclosure-builder"]',
    component: '[data-component]',
    summaryItem: '[data-summary]',
    summaryValue: '.subsea-builder__summary-value',
    totalPrice: '.subsea-builder__total-price',
    leadTime: '.subsea-builder__lead-time',
    addToCartBtn: '.subsea-builder__add-to-cart',
    productGrid: '.subsea-builder__product-grid'
  };

  var classes = {
    selected: 'is-selected',
    compatible: 'is-compatible',
    incompatible: 'is-incompatible'
  };

  // Helpers to normalize localized strings to stable keys
  function normalizeString(value) {
    if (!value || typeof value !== 'string') return '';
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip accents
      .trim();
  }

  // Synonym lists for broader language coverage
  var ALUMINUM_SYNONYMS = [
    // Latin scripts (diacritics stripped by normalizeString)
    'aluminum', 'aluminium', 'alu', 'alluminio', 'aluminio', 'aluminiu', 'aluminij', 'aluminium', 'aluminijum',
    'aluminio', // it/pt
    // German/Dutch
    'aluminium',
    // Polish/Czech/Hungarian/Romanian/Turkish (without diacritics after normalization)
    'aluminium', 'hlinik', 'aluminiu', 'aluminyum',
    // Vietnamese (already latin)
    'nhom',
    // Non-Latin scripts (left as-is; normalizeString keeps characters, only lowercases)
    'алюминий', // ru (not listed, but common)
    'алюміній', // uk
    'الألمنيوم', 'الالمنيوم', 'الألومنيوم', 'الالومنيوم', // ar variants
    '铝', // zh-Hans
    'アルミニウム', // ja
    '알루미늄', // ko
    'อัลูมิเนียม', // th
  ];

  var ACRYLIC_SYNONYMS = [
    // Latin scripts (diacritics stripped by normalizeString)
    'acrylic', 'acrylique', 'acrilico', 'acrilico', 'acrilic', 'acrilyc', 'acrilico', 'akryl', 'akrylat',
    'pmma', 'plexiglass', 'plexi', // common synonyms
    // German/Dutch/Polish
    'acryl', 'acrylat', 'akryl',
    // Portuguese/Italian/Spanish (diacritics stripped)
    'acrilico',
    // Romanian/Turkish/Hungarian
    'acrilic', 'akrilik', 'akril',
    // Vietnamese (latin)
    'acrylic',
    // Non-Latin scripts
    'акрил', // ru/uk variants
    'акриловий', // uk adj
    'أكريليك', 'اكريليك', // ar
    '丙烯酸', // zh-Hans
    'アクリル', // ja
    '아크릴', // ko
    'อะคริลิก', // th
  ];

  function normalizeMaterial(value) {
    var raw = (typeof value === 'string' ? value.toLowerCase().trim() : '');
    var v = normalizeString(value);
    // Map common translations/synonyms to canonical keys
    // Aluminum
    if (
      ALUMINUM_SYNONYMS.indexOf(v) !== -1 ||
      ALUMINUM_SYNONYMS.indexOf(raw) !== -1 ||
      v.indexOf('alumin') === 0 // matches aluminium/aluminum prefixes
    ) {
      return 'aluminum';
    }
    // Acrylic
    if (
      ACRYLIC_SYNONYMS.indexOf(v) !== -1 ||
      ACRYLIC_SYNONYMS.indexOf(raw) !== -1 ||
      v.indexOf('acryl') === 0
    ) {
      return 'acrylic';
    }
    return v || 'unknown';
  }

  function normalizeMaterialList(value) {
    // Accepts array or comma-separated string; returns array of normalized material keys
    if (!value) return [];
    var items = Array.isArray(value)
      ? value
      : (typeof value === 'string' ? value.split(',') : []);
    return items
      .map(function(item){ return normalizeMaterial(String(item || '')); })
      .filter(function(v){ return v; });
  }

  // Normalize flange type labels to stable keys
  var ASSEMBLY_SYNONYMS = [
    // Latin
    'assembly', 'assemblage', 'ensamblaje', 'assemblaggio', 'montagem', 'montaje', 'montaj', 'montagem', 'montarea',
    'baugruppe', // de
    // Non-Latin
    '組立', '組み立て', // ja
    '组装', '装配', // zh-hans
    'сборка', // ru
    'montajı', 'montaj', // tr
    'montagem', // pt
    'montaj', // ro
    'montáž', // cs
    '구성품', '조립', // ko
    'التجميع', 'مجموعة', // ar
    'ประกอบ', // th
  ];

  var FLANGE_ONLY_SYNONYMS = [
    // Latin
    'flange only', 'flange-only', 'flange', 'solo brida', 'solo bridas', 'nur flansch', 'seulement bride', 'seulement la bride', 'bride uniquement',
    'solo flangia', 'apenas flange', 'tikai atloks', 'numai flansa', 'csak perem', 'samo prirobnica',
    // Non-Latin (approximate common terms)
    '只限法兰', '仅法兰', // zh-hans
    'フランジのみ', // ja
    '플랜지만', // ko
    'только фланец', // ru
    'فقط شفة', // ar
    'เฉพาะหน้าแปลน', // th
  ];

  function normalizeFlangeType(value) {
    var raw = (typeof value === 'string' ? value.toLowerCase().trim() : '');
    var v = normalizeString(value);
    if (ASSEMBLY_SYNONYMS.indexOf(v) !== -1 || ASSEMBLY_SYNONYMS.indexOf(raw) !== -1 || v.indexOf('assembly') === 0) {
      return 'assembly';
    }
    if (FLANGE_ONLY_SYNONYMS.indexOf(v) !== -1 || FLANGE_ONLY_SYNONYMS.indexOf(raw) !== -1 || v.indexOf('flange') === 0) {
      return 'flange_only';
    }
    return v || 'unknown';
  }

  // Configuration: restrict which variants appear for certain products
  var ALLOWED_VARIANT_IDS = {
    aluminumFlange: [
      45313672839326, 45313672872094, 45313672904862
    ],
    opticalDomeEndCap: [
      45386063642782  // Only this variant for optical dome end-cap
    ],
    aluminumEndCap: [
      45386074423454, 45386074456222, 45386074488990, 45386074521758,
      45729425588382, 45386074554526, 45729425621150, 45386075570334,
      45386075603102, 45386075635870, 45386075668638, 45386075701406,
      45386075734174, 45729425653918, 45386075766942, 45386075799710
    ],
    acrylicEndCap: [
      46409199386782, 46409199419550, 46422760947870, 46422760980638,
      46422761013406, 46409199452318, 46422761111710, 46422761144478,
      46422761177246, 46422761210014, 46409199517854, 46422761406622,
      46422761537694, 46422761570462, 46422761603230
    ],
    polymerAssembly: [
      // Add polymer assembly variant IDs here when you have them
    ],
    electronicTraySet: [
      45386085466270, 45386085499038, 45386085531806, 45386085564574, 45386085597342, 45386085630110, 45386085662878, 45386085695646, 45386085728414, 45386085761182, 45386085793950, 45386085826718, 45386085859486
    ],
    insulatedPcbShelf: [
      45386058989726, 45386059022494, 45386059055262, 45386059088030, 45386059120798, 45611554635934, 45611554668702, 45611554701470, 45611554734238, 45611554767006, 45611554799774, 45611554865310, 45611554930846
    ],
    ventVacuumControl: {
      m10SelfSealingVent: [
        // M10 Self-Sealing Vent & Vacuum Plug - no variants, single product
      ],
      m8VentVacuumPlug: [
        45726820696222,  // M8 Vent variant
        45726820728990   // M8 Vacuum Plug variant
      ]
    }
  };

  function SubseaBuilder(container) {
    this.container = container;
    this.selectedComponents = {
      tube: null,
      end1: {
        flange: null,
        'end-cap': null
      },
      end2: {
        flange: null,
        'end-cap': null
      },
      interiorFittings: {
        electronicTray: undefined,
        pcbShelf: undefined,
        pcbShelfSecondary: undefined
      },
      ventVacuumControl: {
        vent: undefined,
        vacuumPlug: undefined
      }
    };
    this.totalPrice = 0;
    this.leadTimes = [];
    
    this.init();
  }

  SubseaBuilder.prototype = {
    init: function() {
      this.addProductBoxStyles();
      this.bindEvents();
      this.loadTubeProducts();
      this.updateSummary();
    },

    addProductBoxStyles: function() {
      // Add CSS styles for the new consistent product box format
      var styleId = 'subsea-builder-product-box-styles';
      if (document.getElementById(styleId)) return; // Already added
      
      var style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* New consistent product box styles */
        .subsea-builder__product-description {
          margin: 0.5em 0 1em 0;
          font-size: 0.9em;
          line-height: 1.4;
          max-width: 100%;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .subsea-builder__product-description table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.5em 0;
        }
        
        .subsea-builder__product-description td {
          padding: 2px 0;
          vertical-align: top;
        }
        
        .subsea-builder__product-description td[style*="font-weight:bold"] {
          font-weight: bold;
        }
        
        .subsea-builder__product-title {
          font-weight: bold;
          text-transform: uppercase;
        }
        
        
        .subsea-builder__spec-grid {
          display: grid;
          grid-template-columns: 15% 40% 45%;
          gap: 0.5em;
          margin: 0.5em 0;
          padding: 0.5em 0;
          border-top: 1px solid #e0e0e0;
          border-bottom: 1px solid #e0e0e0;
          padding-left: 0.5em;
        }
        
        .subsea-builder__spec {
          text-align: center;
          font-size: 0.85em;
          font-weight: 500;
          line-height: 1.2;
        }
        
        .subsea-builder__spec strong {
          font-weight: bold;
        }
        
        .subsea-builder__availability {
          margin: 0.5em 0;
          padding-left: 0.5em;
          max-width: 100%;
          overflow: hidden;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .subsea-builder__availability .product-lead-time__message {
          display: flex;
          align-items: center;
          gap: 0.5em;
          font-size: 0.85em;
          margin: 0.25em 0;
          justify-content: flex-start;
        }
        
        .subsea-builder__availability .product-lead-time__message--in-stock {
          color: #56B74C;
        }
        
        .subsea-builder__availability .product-lead-time__message--out-of-stock {
          color: #f4a142;
        }
        
        .subsea-builder__availability .icon--clock {
          width: 14px;
          height: 14px;
          fill: currentColor;
        }
        
        /* Product item styling handled by main CSS */
        
        /* Product image link styling */
        .subsea-builder__product-image-link {
          display: block;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        
        .subsea-builder__product-image-link:hover {
          opacity: 0.8;
        }
        
        .subsea-builder__product-image-link:focus {
          outline: 2px solid #007bff;
          outline-offset: 2px;
        }
        
        /* Fix for summary panel being truncated by header */
        .subsea-builder__summary {
          position: sticky;
          top: 150px; /* Adjust this value based on your header height */
          z-index: 10;
        }
        
        /* Alternative: If the above doesn't work, try this approach */
        .subsea-builder__summary-panel {
          position: sticky;
          top: 150px; /* Adjust this value based on your header height */
          z-index: 10;
        }
        
        /* Tube summary details styling */
        .subsea-builder__summary-details {
          margin-top: 0.5em;
          padding: 0.75em;
          background: #f8f9fa;
          border-radius: 4px;
          border-left: 3px solid #007cba;
        }
        
        .subsea-builder__tube-summary {
          margin: 0;
          font-size: 0.9em;
          line-height: 1.4;
          color: #555;
        }
        
        .subsea-builder__product-summary,
        .subsea-builder__product-summary-2 {
          margin: 0;
          font-size: 0.9em;
          line-height: 1.4;
          color: #555;
        }
        
        .subsea-builder__product-separator {
          height: 1px;
          background: #ddd;
          margin: 0.5em 0;
        }
        
        /* Special styling for tube summary metafield content */
        .subsea-builder__summary-value {
          position: relative;
        }
        
        .subsea-builder__summary-value[data-tube-metafield="true"] {
          background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);
          border: 1px solid #007cba;
          border-radius: 6px;
          padding: 12px 16px;
          margin: 8px 0;
          box-shadow: 0 2px 4px rgba(0, 124, 186, 0.1);
          font-weight: 500;
          color: #007cba;
          line-height: 1.5;
        }
        
        
      `;
      document.head.appendChild(style);
    },

    bindEvents: function() {
      // Add to cart button
      var addToCartBtn = this.container.querySelector(selectors.addToCartBtn);
      if (addToCartBtn) {
        addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
      }
    },

    loadTubeProducts: function() {
      var tubeComponent = this.container.querySelector('[data-component="tube"]');
      if (tubeComponent) {
        var productGrid = tubeComponent.querySelector(selectors.productGrid);
        if (productGrid) {
          this.loadTubeProductsFromData(productGrid);
        }
      }
    },

    loadTubeProductsFromData: function(container) {
      // Get the pre-loaded product data from the JSON script tag
      var productsDataElement = document.getElementById('subsea-builder-products-data');
      if (!productsDataElement) {
        container.innerHTML = '<div class="subsea-builder__error">' + ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.productDataNotFound) || "Product data not found. Please refresh the page.") + '</div>';
        return;
      }

      try {
        var productsData = JSON.parse(productsDataElement.textContent);
        
        // Store products data for Interior Fittings access
        this.productsData = productsData;
        
        // Store all variants for hierarchical selection
        this.allTubeVariants = [];
        
        // Backward compatibility (old keys) OR new nested structure under productsData.tubes
        var tubesSource = productsData.tubes ? productsData.tubes : {
          aluminum: productsData.aluminum_tube,
          acrylic: productsData.acrylic_tube
        };

        if (tubesSource && tubesSource.aluminum && tubesSource.aluminum.variants) {
          tubesSource.aluminum.variants.forEach(function(variant) {
            variant.product_title = tubesSource.aluminum.title;
            variant.product_handle = tubesSource.aluminum.handle;
            variant.product_image = tubesSource.aluminum.featured_image;
            // Attach a stable, language-agnostic material key
            variant._materialKey = 'aluminum';
            this.allTubeVariants.push(variant);
          }.bind(this));
        }

        if (tubesSource && tubesSource.acrylic && tubesSource.acrylic.variants) {
          tubesSource.acrylic.variants.forEach(function(variant) {
            variant.product_title = tubesSource.acrylic.title;
            variant.product_handle = tubesSource.acrylic.handle;
            variant.product_image = tubesSource.acrylic.featured_image;
            // Attach a stable, language-agnostic material key
            variant._materialKey = 'acrylic';
            this.allTubeVariants.push(variant);
          }.bind(this));
        }
        
        this.initializeTubeSelection();
        
      } catch (error) {
        console.error('Error parsing products data:', error);
        container.innerHTML = '<div class="subsea-builder__error">' + ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.errorLoadingProductData) || "Error loading product data. Please refresh the page.") + '</div>';
      }
    },

    populateTubeProducts: function(container, variants) {
      container.innerHTML = '';
      
      if (!variants || variants.length === 0) {
        container.innerHTML = '<div class="subsea-builder__error">' + ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noTubeProductsFound) || "No tube products found.") + '</div>';
        return;
      }
      
      
      variants.forEach(function(variant) {
        var productElement = this.createProductElement(variant, 'tube', null);
        container.appendChild(productElement);
      }.bind(this));
    },

    // Initialize hierarchical tube selection
    initializeTubeSelection: function() {
      // Show diameter selection first
      var diameterSubcategory = this.container.querySelector('[data-subcategory="diameter"]');
      if (diameterSubcategory) {
        diameterSubcategory.style.display = 'block';
      }
      
      // Bind diameter selection events
      var diameterButtons = this.container.querySelectorAll('[data-diameter]');
      diameterButtons.forEach(function(btn) {
        btn.addEventListener('click', this.handleDiameterSelection.bind(this));
      }.bind(this));
      
      // Bind material selection events
      var materialButtons = this.container.querySelectorAll('[data-material]');
      materialButtons.forEach(function(btn) {
        btn.addEventListener('click', this.handleMaterialSelection.bind(this));
      }.bind(this));
    },

    // Handle diameter selection
    handleDiameterSelection: function(event) {
      var selectedDiameter = event.target.getAttribute('data-diameter');
      
      // Check if this is a change (not initial selection)
      var isChange = this.selectedTubeOptions && this.selectedTubeOptions.diameter;
      
      // Update selection state
      var diameterButtons = this.container.querySelectorAll('[data-diameter]');
      diameterButtons.forEach(function(btn) {
        btn.classList.remove('is-selected');
      });
      event.target.classList.add('is-selected');
      
      // Store selected diameter
      this.selectedTubeOptions = this.selectedTubeOptions || {};
      this.selectedTubeOptions.diameter = selectedDiameter;
      
      // If this is a change, reset everything downstream
      if (isChange) {
        this.resetAllDownstreamSelections();
        this.resetTubeSelectionPartial();
        // Re-select the diameter
        event.target.classList.add('is-selected');
        this.selectedTubeOptions.diameter = selectedDiameter;
      }
      
      // Show material selection and update availability
      var materialSubcategory = this.container.querySelector('[data-subcategory="material"]');
      if (materialSubcategory) {
        materialSubcategory.style.display = 'block';
        this.updateMaterialAvailability(selectedDiameter);
      }
      
      // Hide length selection
      var lengthSubcategory = this.container.querySelector('[data-subcategory="length"]');
      if (lengthSubcategory) {
        lengthSubcategory.style.display = 'none';
      }
    },

    // Handle material selection
    handleMaterialSelection: function(event) {
      // Prevent selection if button is disabled
      if (event.target.disabled) {
        return;
      }
      
      var selectedMaterial = event.target.getAttribute('data-material');
      var selectedMaterialKey = normalizeMaterial(selectedMaterial);
      
      // Check if this is a change (not initial selection)
      var isChange = this.selectedTubeOptions && this.selectedTubeOptions.material;
      
      // Update selection state
      var materialButtons = this.container.querySelectorAll('[data-material]');
      materialButtons.forEach(function(btn) {
        btn.classList.remove('is-selected');
      });
      event.target.classList.add('is-selected');
      
      // Store selected material
      this.selectedTubeOptions = this.selectedTubeOptions || {};
      this.selectedTubeOptions.material = selectedMaterialKey;
      
      // If this is a change, reset everything downstream
      if (isChange) {
        this.resetAllDownstreamSelections();
        // Clear any selected tube when material changes
        this.clearSelectedTube();
        // Re-select the diameter and material
        var diameterButtons = this.container.querySelectorAll('[data-diameter]');
        diameterButtons.forEach(function(btn) {
          if (btn.getAttribute('data-diameter') === this.selectedTubeOptions.diameter) {
            btn.classList.add('is-selected');
          }
        }.bind(this));
        event.target.classList.add('is-selected');
      }
      
      // Show length selection with filtered variants
      this.showLengthOptions();
    },

    // Update material availability based on selected diameter
    updateMaterialAvailability: function(diameter) {
      
      // Get all available materials for this diameter
      var availableMaterials = new Set();
      this.allTubeVariants.forEach(function(variant) {
        var metafields = variant.metafields || {};
        var variantDiameter = metafields.custom_bsub_size;
        var variantMaterial = variant._materialKey || normalizeMaterial(metafields.custom_bsub_material);
        
        if (variantDiameter == diameter && variantMaterial) {
          availableMaterials.add(variantMaterial);
        }
      });
      
      
      // Update material buttons
      var materialButtons = this.container.querySelectorAll('[data-subcategory="material"] [data-material]');
      materialButtons.forEach(function(btn) {
        var material = btn.getAttribute('data-material');
        var materialKey = normalizeMaterial(material);
        var isAvailable = availableMaterials.has(materialKey);
        
        if (isAvailable) {
          // Enable button
          btn.disabled = false;
          btn.classList.remove('disabled');
          btn.style.opacity = '1';
          btn.style.cursor = 'pointer';
        } else {
          // Disable button
          btn.disabled = true;
          btn.classList.add('disabled');
          btn.style.opacity = '0.5';
          btn.style.cursor = 'not-allowed';
        }
      });
    },
    // Show length options based on selected diameter and material
    showLengthOptions: function() {
      var diameter = this.selectedTubeOptions.diameter;
      var materialKey = this.selectedTubeOptions.material;
      
      if (!diameter || !materialKey) return;
      
      // Filter variants by diameter and material
      var filteredVariants = this.allTubeVariants.filter(function(variant) {
        var metafields = variant.metafields || {};
        var variantDiameter = metafields.custom_bsub_size;
        var variantMaterial = variant._materialKey || normalizeMaterial(metafields.custom_bsub_material);
        
        return variantDiameter == diameter && variantMaterial === materialKey;
      });
      
      
      // Show length subcategory
      var lengthSubcategory = this.container.querySelector('[data-subcategory="length"]');
      if (lengthSubcategory) {
        lengthSubcategory.style.display = 'block';
      }
      
      // Make sure material subcategory stays visible
      var materialSubcategory = this.container.querySelector('[data-subcategory="material"]');
      if (materialSubcategory) {
        materialSubcategory.style.display = 'block';
      }
      
      // Populate length options
      var lengthGrid = this.container.querySelector('[data-subcategory="length"] .subsea-builder__product-grid');
      if (lengthGrid) {
        lengthGrid.innerHTML = '';
        
        if (filteredVariants.length === 0) {
          lengthGrid.innerHTML = '<div class="subsea-builder__error">' + ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noVariantsFound) || "No variants found for the selected diameter and material.") + '</div>';
          return;
        }
        
        filteredVariants.forEach(function(variant) {
          var productElement = this.createProductElement(variant, 'tube', null);
          lengthGrid.appendChild(productElement);
        }.bind(this));
      }
    },

    createProductElement: function(variant, type, endNumber) {
      var element = document.createElement('div');
      element.className = 'subsea-builder__product-item';
      element.dataset.variantId = variant.id;
      element.dataset.productType = type;
      
      
      // Get metafield values - try different possible paths
      var metafields = variant.metafields || {};
      var material = variant._materialKey || normalizeMaterial(metafields.custom_bsub_material || metafields.bsub_material || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.unknown) || "Unknown"));
      var diameter = metafields.custom_bsub_size || metafields.bsub_size || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.unknown) || "Unknown");
      var length = metafields.custom_bsub_length_mm || metafields.bsub_length_mm || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.unknown) || "Unknown");
      var depthRating = metafields.custom_bsub_depth_m || metafields.bsub_depth_m || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.unknown) || "Unknown");
      var inStockLeadTime = metafields.custom_in_stock_lead_time || metafields.in_stock_lead_time || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.contactForLeadTime) || "Contact for lead time");
      var outOfStockLeadTime = metafields.custom_out_of_stock_lead_time || metafields.out_of_stock_lead_time || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.contactForLeadTime) || "Contact for lead time");
      
      // Set data attributes for compatibility logic
      element.dataset.material = material;
      element.dataset.diameter = diameter;
      element.dataset.length = length;
      element.dataset.depthRating = depthRating;
      element.dataset.inStockLeadTime = inStockLeadTime;
      element.dataset.outOfStockLeadTime = outOfStockLeadTime;
      element.dataset.price = variant.price;
      element.dataset.sku = variant.sku;
      element.dataset.inventory = variant.inventory_quantity;
      element.dataset.inventoryPolicy = variant.inventory_policy;

      // Format price using Shopify's standard currency formatting (same as product pages)
      var formattedPrice = this.formatPrice(variant.price);
      
      // Check if this is a placeholder product (like "No Electronic Tray Set")
      var isPlaceholder = variant.id === 'none' || variant.title === 'none' || variant.sku === 'none';
      
      if (type === 'tube' && !isPlaceholder) {
        // New consistent format for tube products
        this.createTubeProductElement(element, variant, metafields, formattedPrice);
      } else if (isPlaceholder) {
        // Handle placeholder products
        this.createPlaceholderProductElement(element, variant, type, endNumber);
      } else {
        // Original format for other component types (flange, end-cap, etc.)
        this.createStandardProductElement(element, variant, type, metafields, formattedPrice, material, diameter, length, depthRating);
      }

      // Add click event for selection
      var selectBtn = element.querySelector('.select-component-btn');
      if (selectBtn) {
        selectBtn.addEventListener('click', this.handleComponentSelection.bind(this, variant, type, endNumber));
      }

      return element;
    },

    // New method for creating tube product elements with consistent format
    createTubeProductElement: function(element, variant, metafields, formattedPrice) {
      
      // Get product-level metafields for lead times (from the product, not variant)
      var productMetafields = this.getProductMetafields(variant);
      var productInStockLeadTime = productMetafields.custom_in_stock_lead_time || 'N/A';
      var productOutOfStockLeadTime = productMetafields.custom_out_of_stock_lead_time || 'N/A';
      
      // Get custom title and description from metafields with multiple fallbacks
      var customTitle = metafields.custom_tube_builder_product_title || 
                       metafields.tube_builder_product_title || 
                       variant.product_title || 
                       variant.title || 
                       'N/A';
      
      // Handle description - should now be base64 encoded string from Liquid template
      var customDescription = metafields.custom_tube_builder_product_description || 
                             metafields.tube_builder_product_description || 
                             '';
      
      
      // If it's still an object (fallback), try to extract the value
      if (typeof customDescription === 'object' && customDescription !== null) {
        
        // Check for common Shopify metafield object structures
        if (customDescription.value !== undefined) {
          customDescription = customDescription.value;
        } else if (customDescription.text !== undefined) {
          customDescription = customDescription.text;
        } else if (customDescription.content !== undefined) {
          customDescription = customDescription.content;
        } else if (customDescription.html !== undefined) {
          customDescription = customDescription.html;
        } else if (customDescription.raw !== undefined) {
          customDescription = customDescription.raw;
        } else {
          // If it's an object with an error, set to empty
          customDescription = '';
        }
      }
      
      // Ensure it's a string and decode if it's base64
      customDescription = String(customDescription || '');
      
      // Try to decode base64 if it looks like base64 content
      if (customDescription && this.isBase64(customDescription)) {
        try {
          customDescription = atob(customDescription);
        } catch (e) {
        }
      }
      
      
      // Get other required data
      var depthRatingM = (metafields.custom_bsub_depth_m != null && metafields.custom_bsub_depth_m !== '') ? metafields.custom_bsub_depth_m : 'N/A';
      var depthRating = depthRatingM !== 'N/A' ? 
        `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(depthRatingM)}m / ${this.formatNumber(Math.round(depthRatingM * 3.28084))}ft` : 
        'N/A';
      var sku = (variant.sku && variant.sku !== '') ? variant.sku : ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notAvailable) || "N/A");
      var priceDisplay = formattedPrice ? `<strong>${formattedPrice}</strong>` : ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notAvailable) || "N/A");
      
      // Create availability HTML based on product page logic
      var availabilityHtml = this.createAvailabilityHtml(variant, productInStockLeadTime, productOutOfStockLeadTime);
      
      // Create product URL with variant parameter
      var productUrl = this.createProductUrl(variant);
      
      element.innerHTML = `
        <div class="subsea-builder__product-image">
          <a href="${productUrl}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
            <img src="${this.getImageWithFallback(variant)}" alt="${customTitle}" class="subsea-builder__image">
          </a>
        </div>
        <div class="subsea-builder__product-info">
          <div class="subsea-builder__product-title">${customTitle}</div>
          ${customDescription ? `<div class="subsea-builder__product-description">${customDescription}</div>` : ''}
          <div class="subsea-builder__spec-grid">
            <div class="subsea-builder__spec">${priceDisplay}</div>
            <div class="subsea-builder__spec">${depthRating}</div>
            <div class="subsea-builder__spec">${sku}</div>
          </div>
          <div class="subsea-builder__availability">${availabilityHtml}</div>
          <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}</button>
        </div>
      `;
    },

    // New method for creating placeholder product elements
    createPlaceholderProductElement: function(element, variant, type, endNumber) {
      var componentName = type.replace('-', ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); });
      var placeholderTitle = 'No ' + componentName;
      var placeholderDescription = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.skipThisOptional) || "Skip this optional component";
      
      element.innerHTML = `
        <div class="subsea-builder__product-image">
          <img src="${this.getImageWithFallback(variant)}" alt="${placeholderTitle}" class="subsea-builder__image">
        </div>
        <div class="subsea-builder__product-info">
          <div class="subsea-builder__product-title">${placeholderTitle}</div>
          <div class="subsea-builder__product-description">${placeholderDescription}</div>
          <div class="subsea-builder__spec-grid">
            <div class="subsea-builder__spec">$0.00</div>
            <div class="subsea-builder__spec">N/A</div>
            <div class="subsea-builder__spec">N/A</div>
          </div>
          <div class="subsea-builder__availability">
            <div class="product-lead-time__message">N/A</div>
          </div>
          <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}</button>
        </div>
      `;
    },

    // Original method for other component types (flange, end-cap, etc.)
    createStandardProductElement: function(element, variant, type, metafields, formattedPrice, material, diameter, length, depthRating) {
      // Determine lead time display based on inventory
      var leadTimeDisplay = '';
      var labels = (window.theme && window.theme.strings && window.theme.strings.leadTime) || {};
      var unitLabel = function(count){ 
        var n = parseInt(count || 0, 10); 
        return n === 1 ? (labels.unitSingular || 'Unit') : (labels.unitPlural || 'Units'); 
      };
      var shipsIn = (labels.shipsIn || 'Ships in');
      var quickShip = (labels.quickShip || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.quickShip) || "Quick Ship"));
      var availableFor = (labels.availableFor || 'Available for');
      var additionalUnits = (labels.additionalUnits || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.additionalUnits) || "Additional Units"));
      var madeToOrder = (labels.madeToOrder || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.madeToOrder) || "Made to Order"));
      if (variant.inventory_quantity > 0) {
        leadTimeDisplay = `${variant.inventory_quantity} ${unitLabel(variant.inventory_quantity)} ${availableFor} ${quickShip} - ${shipsIn} ${metafields.custom_in_stock_lead_time || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.contactForLeadTime) || "Contact for lead time")}`;
      } else if (variant.inventory_policy === 'continue') {
        leadTimeDisplay = `${additionalUnits} ${madeToOrder} - ${shipsIn} ${metafields.custom_out_of_stock_lead_time || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.contactForLeadTime) || "Contact for lead time")}`;
      } else {
        leadTimeDisplay = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.outOfStock) || "Out of Stock";
      }

      element.innerHTML = `
        <div class="subsea-builder__product-image">
          <img src="${this.getImageWithFallback(variant)}" alt="${variant.title}" class="subsea-builder__image">
        </div>
        <div class="subsea-builder__product-info">
          <div class="subsea-builder__product-title">${variant.product_title || variant.title}</div>
          <div class="subsea-builder__product-details">
            <p><strong>${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.materialLabel) || "Material")}:</strong> ${material}</p>
            <p><strong>${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.diameterLabel) || "Diameter")}:</strong> ${diameter}mm</p>
            <p><strong>${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.lengthLabel) || "Length"}:</strong> ${length}mm</p>
            <p><strong>${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:"}</strong> ${depthRating}m</p>
            <p><strong>${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.priceLabel) || "Price"}:</strong> $${formattedPrice}</p>
            <p><strong>${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.skuLabel) || "SKU")}:</strong> ${variant.sku}</p>
            <p><strong>${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.leadTimeLabel) || "Lead Time")}:</strong> ${leadTimeDisplay}</p>
          </div>
          <button class="btn btn--secondary select-component-btn">
            ${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}
          </button>
        </div>
      `;
    },

    // Helper method to get product-level metafields
    getProductMetafields: function(variant) {
      if (!this.productsData) return {};
      
      // Search through all product categories to find the one containing this variant
      var productCategories = [
        'tubes',
        'flanges_endcaps', 
        'interior_fittings',
        'vent_vacuum_control'
      ];
      
      for (var i = 0; i < productCategories.length; i++) {
        var category = productCategories[i];
        if (this.productsData[category]) {
          // Search through all products in this category
          for (var productKey in this.productsData[category]) {
            var product = this.productsData[category][productKey];
            if (product && product.variants) {
              // Check if this variant belongs to this product
              for (var j = 0; j < product.variants.length; j++) {
                if (product.variants[j].id === variant.id) {
                  return product.product_metafields || {};
                }
              }
            }
          }
        }
      }
      
      return {};
    },

    // Helper method to create availability HTML based on product page logic
    createAvailabilityHtml: function(variant, inStockLeadTime, outOfStockLeadTime) {
      var invQty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : 0;
      var invPolicy = variant.inventory_policy || 'deny';
      var availabilityHtml = '';
      
      // Get labels from global theme object
      var labels = (window.theme && window.theme.strings && window.theme.strings.leadTime) || {};
      var unitLabel = function(count){ 
        var n = parseInt(count || 0, 10); 
        return n === 1 ? (labels.unitSingular || 'Unit') : (labels.unitPlural || 'Units'); 
      };
      var shipsIn = (labels.shipsIn || 'Ships in');
      var quickShip = (labels.quickShip || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.quickShip) || "Quick Ship"));
      var availableFor = (labels.availableFor || 'Available for');
      var additionalUnits = (labels.additionalUnits || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.additionalUnits) || "Additional Units"));
      var madeToOrder = (labels.madeToOrder || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.madeToOrder) || "Made to Order"));
      
      if (invPolicy === 'continue') {
        if (invQty > 0) {
          availabilityHtml += `<div class="product-lead-time__message product-lead-time__message--in-stock"><span class="icon icon--clock"></span>${invQty} ${unitLabel(invQty)} ${availableFor} ${quickShip} - ${shipsIn} ${inStockLeadTime}</div>`;
        }
        availabilityHtml += `<div class="product-lead-time__message product-lead-time__message--out-of-stock"><span class="icon icon--clock"></span>${additionalUnits} ${madeToOrder} - ${shipsIn} ${outOfStockLeadTime}</div>`;
      } else {
        if (invQty > 0) {
          availabilityHtml += `<div class="product-lead-time__message product-lead-time__message--in-stock"><span class="icon icon--clock"></span>${invQty} ${unitLabel(invQty)} ${availableFor} ${quickShip} - ${shipsIn} ${inStockLeadTime}</div>`;
        }
      }
      
      return availabilityHtml;
    },

    // Helper method to format numbers with commas
    formatNumber: function(num) {
      if (typeof num !== 'number' || isNaN(num)) {
        return num;
      }
      return num.toLocaleString();
    },

    // Helper method to check if a string is base64 encoded
    isBase64: function(str) {
      if (!str || typeof str !== 'string') return false;
      
      // Check if string contains only base64 characters and has proper length
      var base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      return base64Regex.test(str) && str.length % 4 === 0 && str.length > 0;
    },

    // Helper method to create product URL with variant parameter
    createProductUrl: function(variant) {
      // Get the product handle from the variant
      var productHandle = variant.product_handle;
      
      // Detect current locale prefix from URL
      var pathSeg = (window.location.pathname.split('/')[1] || '');
      var langPrefix = pathSeg.length <= 5 && /^[a-z]{2}(?:-[A-Z]{2})?$/.test(pathSeg) ? ('/' + pathSeg) : '';
      
      // Determine the base URL based on the product handle
      var baseUrl = '';
      if (productHandle === 'subsea-enclosure-aluminum-tube') {
        baseUrl = langPrefix + '/collections/enclosure-tubes-aluminum/products/subsea-enclosure-aluminum-tube';
      } else if (productHandle === 'subsea-enclosure-acrylic-tube') {
        baseUrl = langPrefix + '/collections/enclosure-tubes-acrylic/products/subsea-enclosure-acrylic-tube';
      } else if (productHandle === 'subsea-aluminum-flange') {
        baseUrl = langPrefix + '/products/subsea-aluminum-flange';
      } else if (productHandle === 'aluminum-end-cap') {
        baseUrl = langPrefix + '/products/aluminum-end-cap';
      } else if (productHandle === 'optical-dome-end-cap') {
        baseUrl = langPrefix + '/products/optical-dome-end-cap';
      } else if (productHandle === 'optical-glass-flange-end-cap') {
        baseUrl = langPrefix + '/products/optical-glass-flange-end-cap';
      } else if (productHandle === 'subsea-polymer-flange-end-cap') {
        baseUrl = langPrefix + '/products/subsea-polymer-flange-end-cap';
      } else if (productHandle === 'subsea-m10-self-sealing-vacuum-vent-plug') {
        baseUrl = langPrefix + '/collections/vent-vacuum-control/products/subsea-m10-self-sealing-vacuum-vent-plug';
      } else if (productHandle === 'subsea-m8-enclosure-vent-vacuum-plug') {
        baseUrl = langPrefix + '/collections/vent-vacuum-control/products/subsea-m8-enclosure-vent-vacuum-plug';
      } else if (productHandle === 'electronic-enclosure-tray-set-for-enclosure-tube') {
        baseUrl = langPrefix + '/collections/enclosure-tubes-interior-fittings/products/electronic-enclosure-tray-set-for-enclosure-tube';
      } else if (productHandle === 'pcb-insulated-shelf-for-enclosure-tube') {
        baseUrl = langPrefix + '/collections/enclosure-tubes-interior-fittings/products/pcb-insulated-shelf-for-enclosure-tube';
      } else {
        // Fallback for other products
        baseUrl = langPrefix + '/products/' + productHandle;
      }
      
      // Add variant parameter
      return baseUrl + '?variant=' + variant.id;
    },

    handleComponentSelection: function(variant, type, endNumber) {
      
      // Remove previous selection for this specific component type
      var selector;
      if (type === 'tube') {
        // For tube, remove ALL previous tube selections (not just one)
        var allSelectedTubes = this.container.querySelectorAll('[data-component="' + type + '"] .' + classes.selected);
        
        allSelectedTubes.forEach(function(selectedTube) {
          selectedTube.classList.remove(classes.selected);
          
          // Reset the button text and styling
          var prevButton = selectedTube.querySelector('.select-component-btn');
          if (prevButton) {
            prevButton.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisTube) || "Select This Tube";
          }
        });
      } else {
        // For flange/end-cap, remove previous selection for this specific end
        selector = '[data-component="' + type + '"][data-end="' + endNumber + '"] .' + classes.selected;
        var previousSelected = this.container.querySelector(selector);
        
        if (previousSelected) {
          previousSelected.classList.remove(classes.selected);
          // Also reset the button text and styling
          var prevButton = previousSelected.querySelector('.select-component-btn');
          if (prevButton) {
            // Reset button text based on component type
            if (type === 'flange') {
              prevButton.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisFlange) || "Select This Flange";
            } else if (type === 'end-cap') {
              prevButton.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisEndcap) || "Select This End-Cap";
            }
            // The is-selected class is on the parent element, not the button
          }
        }
      }

      // Add selection to current variant
      var variantElement;
      if (type === 'tube') {
        // For tubes, just use the variant ID
        variantElement = this.container.querySelector('[data-variant-id="' + variant.id + '"]');
      } else {
        // For flange/end-cap, use both variant ID and end number to avoid conflicts
        variantElement = this.container.querySelector('[data-variant-id="' + variant.id + '"][data-end="' + endNumber + '"]');
      }
      
      if (variantElement) {
        variantElement.classList.add(classes.selected);
        
        // Also update the button text and styling
        var button = variantElement.querySelector('.select-component-btn');
        if (button) {
          button.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selected) || "Selected";
          // The CSS selector is .subsea-builder__product-item.is-selected .select-component-btn
          // So we don't need to add is-selected to the button, just the parent
        }
      }

      // Store selection in the correct end structure
      if (type === 'tube') {
        this.selectedComponents[type] = variant;
        
        // Reset all downstream selections when tube changes
        this.resetAllDownstreamSelections();
        
        // Don't clear tube selection options - keep diameter and material visible
        // this.clearTubeSelectionOptions();
        
        // Debug: Check if diameter and material buttons are still selected
        var diameterButtons = this.container.querySelectorAll('[data-diameter]');
        var materialButtons = this.container.querySelectorAll('[data-material]');
        
        // Check only the buttons in the tube selection subcategories
        var tubeDiameterButtons = this.container.querySelectorAll('[data-subcategory="diameter"] [data-diameter]');
        var tubeMaterialButtons = this.container.querySelectorAll('[data-subcategory="material"] [data-material]');
        
        
        // Restore button selections if they were lost
        if (this.selectedTubeOptions && this.selectedTubeOptions.diameter) {
          var diameterBtn = this.container.querySelector('[data-subcategory="diameter"] [data-diameter="' + this.selectedTubeOptions.diameter + '"]');
          if (diameterBtn && !diameterBtn.classList.contains('is-selected')) {
            diameterBtn.classList.add('is-selected');
          }
        }
        
        if (this.selectedTubeOptions && this.selectedTubeOptions.material) {
          var materialBtn = this.container.querySelector('[data-subcategory="material"] [data-material="' + this.selectedTubeOptions.material + '"]');
          if (materialBtn && !materialBtn.classList.contains('is-selected')) {
            materialBtn.classList.add('is-selected');
          }
        }
        
        // Collapse tube selection to show only selected tube
        this.collapseTubeSelection();
      } else {
        // Ensure the end structure exists
        if (!this.selectedComponents['end' + endNumber]) {
          this.selectedComponents['end' + endNumber] = {
            flange: null,
            'end-cap': null
          };
        }
        this.selectedComponents['end' + endNumber][type] = variant;
      }

      // Debug: Log the selected component's metafields
      var endText = endNumber ? ' for end ' + endNumber : '';

      // Update summary
      this.updateSummary();

      // Show next component if available
      this.showNextComponent(type, endNumber);

      // Update compatibility for dependent components
      this.updateCompatibility(type, endNumber);
    },

    showNextComponent: function(selectedType, endNumber) {
      // Always reset downstream components first
      this.resetDownstreamFromComponent(selectedType, endNumber);
      
      if (selectedType === 'tube') {
        // Show only End 1 first
        var end1Flange = this.container.querySelector('[data-component="flange"][data-end="1"]');
        if (end1Flange) {
          // Restore original structure if it was collapsed
          if (!end1Flange.querySelector('.subsea-builder__component-title')) {
            end1Flange.innerHTML = `
              <h3 class="subsea-builder__component-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectFlangeEnd1Numbered) || "2. Select Your Flange (End 1)"}</h3>
              <div class="subsea-builder__component-content">
                <p class="subsea-builder__component-note">Select a tube first to see compatible flanges</p>
              </div>
            `;
          }
          end1Flange.style.display = 'block';
        }
        
        // Hide End 2 until End 1 is configured
        var end2Flange = this.container.querySelector('[data-component="flange"][data-end="2"]');
        if (end2Flange) end2Flange.style.display = 'none';
        
        // Hide interior fittings until End 2 is complete
        var electronicTray = this.container.querySelector('[data-component="electronic-tray"]');
        var pcbShelf = this.container.querySelector('[data-component="pcb-shelf"]');
        var pcbShelfSecondary = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
        if (electronicTray) electronicTray.style.display = 'none';
        if (pcbShelf) pcbShelf.style.display = 'none';
        if (pcbShelfSecondary) pcbShelfSecondary.style.display = 'none';
      } else if (selectedType === 'flange' && endNumber) {
        // Show the corresponding end-cap
        var endCap = this.container.querySelector('[data-component="end-cap"][data-end="' + endNumber + '"]');
        if (endCap) {
          // Restore original structure if it was collapsed
          if (!endCap.querySelector('.subsea-builder__component-title')) {
            var endCapNumber = endNumber === 1 ? '3' : '5';
            endCap.innerHTML = `
              <h3 class="subsea-builder__component-title">${endCapNumber}. Select Your End-Cap (End ${endNumber})</h3>
              <div class="subsea-builder__component-content">
                <p class="subsea-builder__component-note">Select a tube and flange first to see compatible end-caps</p>
              </div>
            `;
          }
          endCap.style.display = 'block';
        }
        
        // If End 1 is now complete, show End 2 and collapse End 1
        if (endNumber === 1) {
          var isComplete = this.isEndComplete(1);
          if (isComplete) {
            this.showEnd2();
            this.collapseEnd1Configuration();
          }
        }
        
        // If End 2 is now complete, collapse End 2 and show Interior Fittings
        if (endNumber === 2) {
          var isComplete = this.isEndComplete(2);
          if (isComplete) {
            this.collapseEnd2Configuration();
            this.showInteriorFittings();
          }
        }
      } else if (selectedType === 'end-cap' && endNumber) {
        // Check if End 1 is complete to show End 2 and collapse End 1
        if (endNumber === 1 && this.isEndComplete(1)) {
          this.showEnd2();
          this.collapseEnd1Configuration();
        }
        
        // Check if End 2 is complete to collapse End 2 and show Interior Fittings
        if (endNumber === 2 && this.isEndComplete(2)) {
          this.collapseEnd2Configuration();
          this.showInteriorFittings();
        }
      }
    },

    // Show Interior Fittings sections when End 2 is complete
    showInteriorFittings: function() {
      
      var electronicTray = this.container.querySelector('[data-component="electronic-tray"]');
      var pcbShelf = this.container.querySelector('[data-component="pcb-shelf"]');
      
      if (electronicTray) {
        // Always recreate the header with blue dot (regardless of whether it exists)
        electronicTray.innerHTML = `
          <h3 class="subsea-builder__component-title" style="position: relative;">
            <span style="display: inline-block; width: 8px; height: 8px; background: #007cba; border-radius: 50%; margin-right: 10px; flex-shrink: 0;"></span>
            ${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.electronicTraySetNumbered) || "6. Electronic Tray Set (Optional)"}
          </h3>
          <style>
            .subsea-builder__component[data-component="electronic-tray"] .subsea-builder__component-title::before {
              display: none !important;
            }
          </style>
          <div class="subsea-builder__component-content">
            <p class="subsea-builder__component-note">Select a tube first to see available electronic tray sets</p>
          </div>
        `;
        electronicTray.style.display = 'block';
      }
      
      if (pcbShelf) {
        // Restore original structure if it was collapsed
        if (!pcbShelf.querySelector('.subsea-builder__component-title')) {
          pcbShelf.innerHTML = `
            <h3 class="subsea-builder__component-title">7. Insulated PCB Shelf (Optional)</h3>
            <div class="subsea-builder__component-content">
              <p class="subsea-builder__component-note">Select a tube first to see available PCB shelves</p>
            </div>
          `;
        }
        pcbShelf.style.display = 'block';
      }
      
      // Load interior fittings products
      this.loadInteriorFittingsProducts();
    },

    // Load Interior Fittings products
    loadInteriorFittingsProducts: function() {
      
      // Load Electronic Tray Set
      this.loadElectronicTraySet();
      
      // Hide PCB Shelf initially - it will only show after Electronic Tray Set is selected
      var pcbShelfSection = this.container.querySelector('[data-component="pcb-shelf"]');
      if (pcbShelfSection) {
        pcbShelfSection.style.display = 'none';
      }
      
      // Hide Secondary PCB Shelf initially - it will only show after Electronic Tray Set is selected
      var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
      if (pcbShelfSecondarySection) {
        pcbShelfSecondarySection.style.display = 'none';
      } else {
      }
    },
    // Load Electronic Tray Set products
    loadElectronicTraySet: function() {
      var electronicTraySection = this.container.querySelector('[data-component="electronic-tray"]');
      if (!electronicTraySection) return;
      
      var productData = this.productsData.flanges_endcaps.electronic_tray_set;
      if (!productData) {
        
        // Show message that product is not available yet
        var content = electronicTraySection.querySelector('.subsea-builder__component-content');
        if (content) {
          content.innerHTML = `
            <div class="subsea-builder__product-item">
              <div class="subsea-builder__product-info">
                <div class="subsea-builder__product-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.electronicTraySetLabel) || "Electronic Tray Set"}</div>
                <div class="subsea-builder__product-description">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.productNotAvailableComingSoon) || "Product not available yet - coming soon!"}</div>
              </div>
            </div>
          `;
        }
        return;
      }
      
      var content = electronicTraySection.querySelector('.subsea-builder__component-content');
      if (!content) return;
      
      // Create no selection option
      var noSelectionHTML = `
        <div class="subsea-builder__product-item" data-variant-id="none">
          <div class="subsea-builder__product-image">
            <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik01MCA5MEgxNTBWMTEwSDUwVjkwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K" alt="${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noElectronicTraySet) || "No Electronic Tray Set")}" class="subsea-builder__image">
          </div>
          <div class="subsea-builder__product-info">
            <div class="subsea-builder__product-title">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noElectronicTraySet) || "No Electronic Tray Set")}</div>
            <div class="subsea-builder__spec-grid">
              <div class="subsea-builder__spec"><strong>$0.00</strong></div>
              <div class="subsea-builder__spec">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A</div>
              <div class="subsea-builder__spec">N/A</div>
          </div>
            <div class="subsea-builder__product-description">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.skipThisOptional) || "Skip this optional component"}</div>
          <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}</button>
          </div>
        </div>
      `;
      
      // Create product options
      var productHTML = '';
      var selectedTube = this.selectedComponents.tube;
      if (selectedTube) {
        var compatibleVariants = this.getCompatibleInteriorFittings(productData.variants, selectedTube, 'electronicTraySet', 'electronic-tray');
        
        compatibleVariants.forEach(function(variant) {
          productHTML += this.createInteriorFittingElement(variant, 'electronic-tray', productData.featured_image);
        }.bind(this));
      }
      
      content.innerHTML = `<div class="subsea-builder__product-grid">${noSelectionHTML}${productHTML}</div>`;
      
      // Bind click events after a small delay to ensure HTML is rendered
      setTimeout(function() {
        this.bindInteriorFittingEvents('electronic-tray');
        this.restoreElectronicTraySelectionIfStillCompatible(productData, selectedTube);
      }.bind(this), 10);
    },

    restoreElectronicTraySelectionIfStillCompatible: function(productData, selectedTube) {
      if (!selectedTube || !productData || !productData.variants) return;

      var savedTray = this.selectedComponents.interiorFittings.electronicTray;
      if (!savedTray) return;

      var compatibleTrays = this.getCompatibleInteriorFittings(productData.variants, selectedTube, 'electronicTraySet', 'electronic-tray');
      var match = null;
      for (var i = 0; i < compatibleTrays.length; i++) {
        if (String(compatibleTrays[i].id) === String(savedTray.id)) {
          match = compatibleTrays[i];
          break;
        }
      }

      if (!match) {
        this.selectedComponents.interiorFittings.electronicTray = undefined;
        this.clearPcbShelfSelections();
        this.updateInteriorFittingSelection('electronic-tray', undefined);
        var pcbShelfSection = this.container.querySelector('[data-component="pcb-shelf"]');
        var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
        if (pcbShelfSection) pcbShelfSection.style.display = 'none';
        if (pcbShelfSecondarySection) pcbShelfSecondarySection.style.display = 'none';
        return;
      }

      this.selectedComponents.interiorFittings.electronicTray = match;
      this.updateInteriorFittingSelection('electronic-tray', match);

      var pcbShelfSection = this.container.querySelector('[data-component="pcb-shelf"]');
      if (pcbShelfSection) {
        pcbShelfSection.style.display = 'block';
      }
      this.updateSecondaryShelfVisibility(match);
      this.revalidateInteriorFittingsSelections();
    },

    // Load Insulated PCB Shelf products
    loadInsulatedPcbShelf: function() {
      var pcbShelfSection = this.container.querySelector('[data-component="pcb-shelf"]');
      if (!pcbShelfSection) return;

      // PCB shelves require a selected electronic tray set (not "none", not unchosen)
      if (!this.selectedComponents.interiorFittings.electronicTray) {
        return;
      }
      
      var productData = this.productsData.flanges_endcaps.insulated_pcb_shelf;
      if (!productData) {
        
        // Show message that product is not available yet
        var content = pcbShelfSection.querySelector('.subsea-builder__component-content');
        if (content) {
          content.innerHTML = `
            <div class="subsea-builder__product-item">
              <div class="subsea-builder__product-info">
                <div class="subsea-builder__product-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.pcbShelfLabel) || "PCB Shelf"}</div>
                <div class="subsea-builder__product-description">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.productNotAvailableComingSoon) || "Product not available yet - coming soon!"}</div>
              </div>
            </div>
          `;
        }
        return;
      }
      
      var content = pcbShelfSection.querySelector('.subsea-builder__component-content');
      if (!content) return;
      
      // Create no selection option
      var noSelectionHTML = `
        <div class="subsea-builder__product-item" data-variant-id="none">
          <div class="subsea-builder__product-image">
            <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik01MCA5MEgxNTBWMTEwSDUwVjkwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K" alt="${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noInsulatedPcbShelf) || "No Insulated PCB Shelf")}" class="subsea-builder__image">
          </div>
          <div class="subsea-builder__product-info">
            <div class="subsea-builder__product-title">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noInsulatedPcbShelf) || "No Insulated PCB Shelf")}</div>
            <div class="subsea-builder__spec-grid">
              <div class="subsea-builder__spec"><strong>$0.00</strong></div>
              <div class="subsea-builder__spec">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A</div>
              <div class="subsea-builder__spec">N/A</div>
          </div>
            <div class="subsea-builder__product-description">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.skipThisOptional) || "Skip this optional component"}</div>
          <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}</button>
          </div>
        </div>
      `;
      
      // Create product options
      var productHTML = '';
      var selectedTube = this.selectedComponents.tube;
      if (selectedTube) {
        var compatibleVariants = this.getCompatibleInteriorFittings(productData.variants, selectedTube, 'insulatedPcbShelf', 'pcb-shelf');
        
        compatibleVariants.forEach(function(variant) {
          productHTML += this.createInteriorFittingElement(variant, 'pcb-shelf', productData.featured_image);
        }.bind(this));

        if (compatibleVariants.length === 0) {
          try {
            var tubeDiameter = String(selectedTube.metafields?.custom_bsub_size || '');
            var tubeLength = String(selectedTube.metafields?.custom_bsub_length_mm || '');
            var selTray = this.selectedComponents.interiorFittings.electronicTray;
            var trayDia = selTray ? String(selTray.metafields?.custom_bsub_tray_diameter || '') : '';
            var trayLenMm = selTray ? this.getTrayLengthMm(selTray) : null;
            console.groupCollapsed('[SubseaBuilder] No PCB Shelf variants matched');
            console.log('Tube:', { diameter: tubeDiameter, length: tubeLength });
            console.log('Electronic tray:', { diameter: trayDia, lengthMm: trayLenMm });
            (productData.variants || []).forEach(function(v){
              var boardLen = this.getPcbBoardLengthMm(v);
              console.log({
                id: v.id,
                title: v.title,
                boardLengthMm: boardLen,
                withinTray: trayLenMm != null && boardLen != null ? boardLen <= trayLenMm : null
              });
            }.bind(this));
            console.groupEnd();
          } catch(e) {}
        }
      }
      
      content.innerHTML = `<div class="subsea-builder__product-grid">${noSelectionHTML}${productHTML}</div>`;
      
      this.sanitizePcbSelectionIfIncompatible('pcb-shelf');
      
      // Bind click events after a small delay to ensure HTML is rendered
      setTimeout(function() {
        this.bindInteriorFittingEvents('pcb-shelf');
      }.bind(this), 10);
    },

    // Load Insulated PCB Shelf Secondary products
    loadInsulatedPcbShelfSecondary: function() {
      var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
      if (!pcbShelfSecondarySection) {
        return;
      }

      if (!this.selectedComponents.interiorFittings.electronicTray) {
        return;
      }
      
      var productData = this.productsData.flanges_endcaps.insulated_pcb_shelf;
      if (!productData) {
        
        // Show message that product is not available yet
        var content = pcbShelfSecondarySection.querySelector('.subsea-builder__component-content');
        if (content) {
          content.innerHTML = `
            <div class="subsea-builder__product-item">
              <div class="subsea-builder__product-info">
                <div class="subsea-builder__product-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.pcbShelfSecondaryLabel) || "PCB Shelf - Secondary"}</div>
                <div class="subsea-builder__product-description">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.productNotAvailableComingSoon) || "Product not available yet - coming soon!"}</div>
              </div>
            </div>
          `;
        }
        return;
      }
      
      var content = pcbShelfSecondarySection.querySelector('.subsea-builder__component-content');
      if (!content) return;
      
      // Create no selection option
      var noSelectionHTML = `
        <div class="subsea-builder__product-item" data-variant-id="none">
          <div class="subsea-builder__product-image">
            <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik01MCA5MEgxNTBWMTEwSDUwVjkwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K" alt="${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noInsulatedPcbShelfSecondary) || "No Insulated PCB Shelf - Secondary")}" class="subsea-builder__image">
          </div>
          <div class="subsea-builder__product-info">
            <div class="subsea-builder__product-title">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noInsulatedPcbShelfSecondary) || "No Insulated PCB Shelf - Secondary")}</div>
            <div class="subsea-builder__spec-grid">
              <div class="subsea-builder__spec"><strong>$0.00</strong></div>
              <div class="subsea-builder__spec">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A</div>
              <div class="subsea-builder__spec">N/A</div>
          </div>
            <div class="subsea-builder__product-description">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.skipThisOptional) || "Skip this optional component"}</div>
          <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}</button>
          </div>
        </div>
      `;
      
      // Create product options
      var productHTML = '';
      var selectedTube = this.selectedComponents.tube;
      if (selectedTube) {
        var compatibleVariants = this.getCompatibleInteriorFittings(productData.variants, selectedTube, 'insulatedPcbShelf', 'pcb-shelf-secondary');
        
        compatibleVariants.forEach(function(variant) {
          productHTML += this.createInteriorFittingElement(variant, 'pcb-shelf-secondary', productData.featured_image);
        }.bind(this));
      }
      
      content.innerHTML = `<div class="subsea-builder__product-grid">${noSelectionHTML}${productHTML}</div>`;
      
      this.sanitizePcbSelectionIfIncompatible('pcb-shelf-secondary');
      
      // Bind click events after a small delay to ensure HTML is rendered
      setTimeout(function() {
        this.bindInteriorFittingEvents('pcb-shelf-secondary');
      }.bind(this), 10);
    },


    // Parse metafield arrays (handles both string arrays and comma-separated strings)
    parseFitsMetafield: function(metafield) {
      if (!metafield) return [];
      if (Array.isArray(metafield)) return metafield;
      if (typeof metafield === 'string') {
        return metafield.split(',').map(function(item) {
          return item.trim();
        });
      }
      return [];
    },

    // Liquid often outputs [] for missing list metafields; [] is truthy in JS and breaks "primary || fallback".
    isMeaningfulFitsMetafield: function(value) {
      if (value == null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    },

    // Normalize number-like strings (e.g., "115 mm", localized variants) to digits-only
    toNumericString: function(value) {
      if (value == null) return '';
      return String(value).replace(/[^0-9]/g, '').trim();
    },

    // Parse a length value (metafield, option label, etc.) to millimeters
    parseLengthMmFromValue: function(value) {
      if (value == null || value === '') return null;
      var digits = this.toNumericString(String(value));
      if (!digits) return null;
      var n = parseInt(digits, 10);
      return isNaN(n) ? null : n;
    },

    isPcbShelfComponentType: function(componentType) {
      return componentType === 'pcb-shelf' || componentType === 'pcb-shelf-secondary';
    },

    // Physical PCB board length from metafield or variant title (e.g. "115mm Diameter (Tube) / 400mm Length")
    getPcbBoardLengthMm: function(variant) {
      if (!variant) return null;
      var mf = variant.metafields || {};
      if (mf.custom_bsub_board_length_mm != null && mf.custom_bsub_board_length_mm !== '') {
        return this.parseLengthMmFromValue(mf.custom_bsub_board_length_mm);
      }
      if (mf.custom_bsub_shelf_length_mm != null && mf.custom_bsub_shelf_length_mm !== '') {
        return this.parseLengthMmFromValue(mf.custom_bsub_shelf_length_mm);
      }
      if (variant.title) {
        var titleParts = String(variant.title).split('/');
        if (titleParts.length >= 2) {
          var fromTitle = this.parseLengthMmFromValue(titleParts[titleParts.length - 1]);
          if (fromTitle != null) return fromTitle;
        }
      }
      var fitsLen = this.parseFitsMetafield(mf.custom_bsub_fits_length);
      if (fitsLen.length === 1) {
        return this.parseLengthMmFromValue(fitsLen[0]);
      }
      return null;
    },

    // Selected electronic tray set length from metafield or variant title (e.g. "115mm / 300mm")
    getTrayLengthMm: function(trayVariant) {
      if (!trayVariant) return null;
      var mf = trayVariant.metafields || {};
      if (mf.custom_bsub_tray_length != null && mf.custom_bsub_tray_length !== '') {
        return this.parseLengthMmFromValue(mf.custom_bsub_tray_length);
      }
      if (trayVariant.title) {
        var titleParts = String(trayVariant.title).split('/');
        if (titleParts.length >= 2) {
          return this.parseLengthMmFromValue(titleParts[titleParts.length - 1].trim());
        }
      }
      return null;
    },

    clearPcbShelfSelections: function() {
      this.selectedComponents.interiorFittings.pcbShelf = undefined;
      this.selectedComponents.interiorFittings.pcbShelfSecondary = undefined;
    },

    // Drop PCB selection if it no longer passes compatibility after upstream changes
    sanitizePcbSelectionIfIncompatible: function(componentType) {
      var key = componentType === 'pcb-shelf-secondary' ? 'pcbShelfSecondary' : 'pcbShelf';
      var selected = this.selectedComponents.interiorFittings[key];
      if (selected == null) return;

      var selectedTube = this.selectedComponents.tube;
      var productData = this.productsData.flanges_endcaps && this.productsData.flanges_endcaps.insulated_pcb_shelf;
      if (!selectedTube || !productData || !productData.variants) {
        this.selectedComponents.interiorFittings[key] = undefined;
        this.updateInteriorFittingSelection(componentType, undefined);
        return;
      }

      var compatible = this.getCompatibleInteriorFittings(productData.variants, selectedTube, 'insulatedPcbShelf', componentType);
      var stillValid = compatible.some(function(v) {
        return String(v.id) === String(selected.id);
      });
      if (!stillValid) {
        this.selectedComponents.interiorFittings[key] = undefined;
        this.updateInteriorFittingSelection(componentType, undefined);
      } else {
        this.updateInteriorFittingSelection(componentType, selected);
      }
    },

    // Revalidate tray + both PCB steps when interior fittings are visible
    revalidateInteriorFittingsSelections: function() {
      var selectedTube = this.selectedComponents.tube;
      var tray = this.selectedComponents.interiorFittings.electronicTray;
      if (!selectedTube) {
        this.selectedComponents.interiorFittings.electronicTray = undefined;
        this.clearPcbShelfSelections();
        return;
      }

      var trayProduct = this.productsData.flanges_endcaps && this.productsData.flanges_endcaps.electronic_tray_set;
      if (tray && trayProduct && trayProduct.variants) {
        var compatibleTrays = this.getCompatibleInteriorFittings(trayProduct.variants, selectedTube, 'electronicTraySet', 'electronic-tray');
        var trayStillValid = compatibleTrays.some(function(v) {
          return String(v.id) === String(tray.id);
        });
        if (!trayStillValid) {
          this.selectedComponents.interiorFittings.electronicTray = undefined;
          this.clearPcbShelfSelections();
          this.updateInteriorFittingSelection('electronic-tray', undefined);
          var pcbShelfSection = this.container.querySelector('[data-component="pcb-shelf"]');
          var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
          if (pcbShelfSection) pcbShelfSection.style.display = 'none';
          if (pcbShelfSecondarySection) pcbShelfSecondarySection.style.display = 'none';
          return;
        }
      }

      var pcbShelfSection = this.container.querySelector('[data-component="pcb-shelf"]');
      if (tray && pcbShelfSection && pcbShelfSection.style.display !== 'none') {
        this.loadInsulatedPcbShelf();
        this.sanitizePcbSelectionIfIncompatible('pcb-shelf');
        var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
        if (pcbShelfSecondarySection && pcbShelfSecondarySection.style.display !== 'none') {
          this.loadInsulatedPcbShelfSecondary();
          this.sanitizePcbSelectionIfIncompatible('pcb-shelf-secondary');
        } else if (this.selectedComponents.interiorFittings.pcbShelfSecondary != null) {
          this.selectedComponents.interiorFittings.pcbShelfSecondary = undefined;
        }
      } else if (tray === undefined) {
        // Tray step not completed yet — reset in-progress PCB choices only
        this.clearPcbShelfSelections();
      }
      // tray === null: user chose "No Electronic Tray Set" — keep pcbShelf/pcbShelfSecondary as null

      this.updateSummary();
      this.calculateTotalPrice();
    },

    // Get lead time text for display
    getLeadTimeText: function(variant) {
      if (!variant || !variant.metafields) return (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.contactForLeadTime) || "Contact for lead time";
      
      var inStockLeadTime = variant.metafields.custom_in_stock_lead_time;
      var outOfStockLeadTime = variant.metafields.custom_out_of_stock_lead_time;
      
      if (inStockLeadTime) return inStockLeadTime;
      if (outOfStockLeadTime) return outOfStockLeadTime;
      
      return (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.contactForLeadTime) || "Contact for lead time";
    },

    // Get compatible Interior Fittings based on selected tube and Electronic Tray Set (for PCB Shelf)
    getCompatibleInteriorFittings: function(variants, selectedTube, whitelistKey, componentType) {
      var compatible = [];
      var debugCounts = { total: Array.isArray(variants) ? variants.length : 0, whitelisted: 0, droppedByWhitelist: 0, droppedByDiameter: 0, droppedByMaterial: 0, droppedByLength: 0, droppedByTrayDiameter: 0, droppedByTrayLength: 0, droppedByMissingTray: 0, droppedByBoardLength: 0 };
      var tubeDiameter = selectedTube.metafields?.custom_bsub_size;
      var tubeMaterial = normalizeMaterial(selectedTube.metafields?.custom_bsub_material);
      var tubeLength = selectedTube.metafields?.custom_bsub_length_mm;
      var tubeDiameterStr = (tubeDiameter != null && tubeDiameter !== '') ? String(tubeDiameter) : null;
      var tubeLengthStr = (tubeLength != null && tubeLength !== '') ? String(tubeLength) : null;
      var tubeDiameterDigits = tubeDiameterStr ? this.toNumericString(tubeDiameterStr) : null;
      var tubeLengthDigits = tubeLengthStr ? this.toNumericString(tubeLengthStr) : null;
      
      // For PCB Shelf, also check Electronic Tray Set compatibility
      var selectedElectronicTray = this.selectedComponents.interiorFittings.electronicTray;
      var electronicTrayDiameter = selectedElectronicTray?.metafields?.custom_bsub_tray_diameter;
      var electronicTrayLength = selectedElectronicTray?.metafields?.custom_bsub_tray_length;
      var electronicTrayDiameterStr = (electronicTrayDiameter != null && electronicTrayDiameter !== '') ? String(electronicTrayDiameter) : null;
      var electronicTrayLengthStr = (electronicTrayLength != null && electronicTrayLength !== '') ? String(electronicTrayLength) : null;
      var electronicTrayDiameterDigits = electronicTrayDiameterStr ? this.toNumericString(electronicTrayDiameterStr) : null;
      var electronicTrayLengthDigits = electronicTrayLengthStr ? this.toNumericString(electronicTrayLengthStr) : null;
      
      if (!variants || !Array.isArray(variants)) {
        return [];
      }
      
      variants.forEach(function(variant) {
        // Check whitelist
        if (whitelistKey && ALLOWED_VARIANT_IDS[whitelistKey]) {
          var variantIdNumber = Number(variant.id);
          if (ALLOWED_VARIANT_IDS[whitelistKey].indexOf(variantIdNumber) === -1) {
            debugCounts.droppedByWhitelist++;
            return; // Skip if not whitelisted
          }
          debugCounts.whitelisted++;
        }
        
        // Check diameter compatibility (skip when no list after parse — avoids [] from Liquid blocking fallback)
        var mf = variant.metafields || {};
        var fitsDiameter = this.isMeaningfulFitsMetafield(mf.custom_bsub_fits_diameter)
          ? mf.custom_bsub_fits_diameter
          : mf.custom_bsub_fits_tray_diameter;
        var fitDiaList = this.parseFitsMetafield(fitsDiameter).map(function(x){ return String(x); });
        if (fitDiaList.length && tubeDiameterStr) {
          var fitDiaDigits = fitDiaList.map(this.toNumericString.bind(this));
          var diameterOk = fitDiaList.indexOf(tubeDiameterStr) !== -1 || (tubeDiameterDigits && fitDiaDigits.indexOf(tubeDiameterDigits) !== -1);
          if (!diameterOk) {
            debugCounts.droppedByDiameter++;
            return; // Skip if diameter doesn't match
          }
        }
        
        // Check material compatibility (skip for PCB shelves)
        if (!this.isPcbShelfComponentType(componentType)) {
          var fitsMaterial = normalizeMaterialList(variant.metafields?.custom_bsub_fits_material);
          if (fitsMaterial && tubeMaterial) {
            var materialOk = fitsMaterial.indexOf(tubeMaterial) !== -1;
            if (!materialOk) {
              debugCounts.droppedByMaterial++;
              return; // Skip if material doesn't match
            }
          }
        }

        // PCB shelves: board length must not exceed selected tray length (or tube length)
        if (this.isPcbShelfComponentType(componentType)) {
          if (!selectedElectronicTray) {
            debugCounts.droppedByMissingTray++;
            return;
          }

          var boardLengthMm = this.getPcbBoardLengthMm(variant);
          if (boardLengthMm == null) {
            debugCounts.droppedByBoardLength++;
            return;
          }

          var trayLengthMm = this.getTrayLengthMm(selectedElectronicTray);
          if (trayLengthMm == null) {
            debugCounts.droppedByTrayLength++;
            return;
          }

          if (boardLengthMm > trayLengthMm) {
            debugCounts.droppedByTrayLength++;
            return;
          }

          var tubeLengthMm = this.parseLengthMmFromValue(tubeLength);
          if (tubeLengthMm != null && boardLengthMm > tubeLengthMm) {
            debugCounts.droppedByLength++;
            return;
          }

          var pcbFitsTrayDiameter = mf.custom_bsub_fits_tray_diameter;
          var parsedFitsDiameter = this.parseFitsMetafield(pcbFitsTrayDiameter).map(function(x){ return String(x); });
          if (parsedFitsDiameter.length && electronicTrayDiameterStr) {
            var parsedFitDiaDigits = parsedFitsDiameter.map(this.toNumericString.bind(this));
            var trayDiameterOk = parsedFitsDiameter.indexOf(electronicTrayDiameterStr) !== -1 || (electronicTrayDiameterDigits && parsedFitDiaDigits.indexOf(electronicTrayDiameterDigits) !== -1);
            if (!trayDiameterOk) {
              debugCounts.droppedByTrayDiameter++;
              return;
            }
          }

          compatible.push(variant);
          return;
        }
        
        // Electronic tray set: length must match tube length (exact metafield list)
        var fitsLength = this.isMeaningfulFitsMetafield(mf.custom_bsub_fits_length)
          ? mf.custom_bsub_fits_length
          : mf.custom_bsub_fits_tray_length;
        var fitLenList = this.parseFitsMetafield(fitsLength).map(function(x){ return String(x); });
        if (fitLenList.length && tubeLengthStr) {
          var fitLenDigits = fitLenList.map(this.toNumericString.bind(this));
          var lengthOk = fitLenList.indexOf(tubeLengthStr) !== -1 || (tubeLengthDigits && fitLenDigits.indexOf(tubeLengthDigits) !== -1);
          if (!lengthOk) {
            debugCounts.droppedByLength++;
            return; // Skip if length doesn't match
          }
        }
        
        compatible.push(variant);
      }.bind(this));
      
      if (this.isPcbShelfComponentType(componentType) && compatible.length === 0) {
        try {
          console.groupCollapsed('[SubseaBuilder] PCB Shelf filter counts');
          console.log(debugCounts);
          console.groupEnd();
        } catch (e) {}
      }
      return compatible;
    },

    // Create Interior Fitting element
    createInteriorFittingElement: function(variant, componentType, productImage) {
      var price = this.formatPrice(variant.price);
      var leadTime = this.getLeadTimeText(variant);
      
      // Ensure product_handle is set for URL generation
      if (componentType === 'electronic-tray') {
        if (!variant.product_handle) {
          variant.product_handle = 'electronic-enclosure-tray-set-for-enclosure-tube';
        }
      } else if (componentType === 'pcb-shelf' || componentType === 'pcb-shelf-secondary') {
        if (!variant.product_handle) {
          variant.product_handle = 'pcb-insulated-shelf-for-enclosure-tube';
        }
      }
      
      
      // Build compatibility info
      var compatibilityInfo = [];
      if (variant.metafields?.custom_bsub_fits_material) {
        var materials = normalizeMaterialList(variant.metafields.custom_bsub_fits_material);
        if (materials.length > 0) {
          compatibilityInfo.push(`${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.materials) || "Materials:"} ${materials.join(', ')}`);
        }
      }
      if (variant.metafields?.custom_bsub_fits_diameter) {
        var diameters = this.parseFitsMetafield(variant.metafields.custom_bsub_fits_diameter);
        if (diameters.length > 0) {
          compatibilityInfo.push(`${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.diameters) || "Diameters:"} ${diameters.join(', ')}mm`);
        }
      }
      if (variant.metafields?.custom_bsub_fits_length) {
        var lengths = this.parseFitsMetafield(variant.metafields.custom_bsub_fits_length);
        if (lengths.length > 0) {
          compatibilityInfo.push(`${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.lengths) || "Lengths:"} ${lengths.join(', ')}mm`);
        }
      }
      
      // For PCB Shelf, show Electronic Tray Set compatibility
      if (componentType === 'pcb-shelf') {
        if (variant.metafields?.custom_bsub_fits_tray_diameter) {
          var trayDiameters = this.parseFitsMetafield(variant.metafields.custom_bsub_fits_tray_diameter);
          if (trayDiameters.length > 0) {
            compatibilityInfo.push(`${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.trayDiameters) || "Tray Diameters:"} ${trayDiameters.join(', ')}mm`);
          }
        }
        if (variant.metafields?.custom_bsub_fits_tray_length) {
          var trayLengths = this.parseFitsMetafield(variant.metafields.custom_bsub_fits_tray_length);
          if (trayLengths.length > 0) {
            compatibilityInfo.push(`${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.trayLengths) || "Tray Lengths:"} ${trayLengths.join(', ')}mm`);
          }
        }
      }
      
      // Get image source using consistent fallback logic
      var imageSrc = this.getImageWithFallback(variant, productImage);
      
      // Get metafields and product data (same as End 2)
      var mf = (variant && variant.metafields) ? variant.metafields : {};
      var productMetafields = this.getProductMetafields(variant);
      var inStockLeadTime = productMetafields.custom_in_stock_lead_time || 'N/A';
      var outOfStockLeadTime = productMetafields.custom_out_of_stock_lead_time || 'N/A';
      
      // Get custom title and description from metafields (same as End 1)
      var customTitle = mf.custom_tube_builder_product_title || 
                       variant.product_title || 
                       variant.title || 
                       'N/A';
      
      // Handle description - should be base64 encoded string from Liquid template
      var customDescription = mf.custom_tube_builder_product_description || '';
      
      // If it's still an object (fallback), try to extract the value
      if (typeof customDescription === 'object' && customDescription !== null) {
        if (customDescription.value !== undefined) {
          customDescription = customDescription.value;
        } else if (customDescription.text !== undefined) {
          customDescription = customDescription.text;
        } else if (customDescription.content !== undefined) {
          customDescription = customDescription.content;
        } else if (customDescription.html !== undefined) {
          customDescription = customDescription.html;
        } else if (customDescription.raw !== undefined) {
          customDescription = customDescription.raw;
      } else {
          customDescription = '';
        }
      }
      
      // Ensure it's a string and decode if it's base64
      customDescription = String(customDescription || '');
      
      // Try to decode base64 if it looks like base64 content
      if (customDescription && this.isBase64(customDescription)) {
        try {
          customDescription = atob(customDescription);
        } catch (e) {
        }
      }
      
      // Get other required data (same as End 2)
      var depthRatingM = (mf.custom_bsub_depth_m != null && mf.custom_bsub_depth_m !== '') ? mf.custom_bsub_depth_m : 'N/A';
      var depthRating = depthRatingM !== 'N/A' ? 
        `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(depthRatingM)}m / ${this.formatNumber(Math.round(depthRatingM * 3.28084))}ft` : 
        `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`;
      var sku = (variant.sku && variant.sku !== '') ? variant.sku : 'N/A';
      var priceDisplay = this.formatPrice(variant.price) ? `<strong>${this.formatPrice(variant.price)}</strong>` : 'N/A';
      
      // Create availability HTML based on product page logic (same as End 2)
      var availabilityHtml = this.createAvailabilityHtml(variant, inStockLeadTime, outOfStockLeadTime);
      
      // Create product URL with variant parameter (same as End 2)
      var productUrl = this.createProductUrl(variant);
      
      
      return `
        <div class="subsea-builder__product-item" data-variant-id="${variant.id}">
          <div class="subsea-builder__product-image">
            <a href="${productUrl}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
              <img src="${imageSrc}" alt="${customTitle}" class="subsea-builder__image">
            </a>
          </div>
          <div class="subsea-builder__product-info">
            <div class="subsea-builder__product-title">${customTitle}</div>
            ${customDescription ? `<div class="subsea-builder__product-description">${customDescription}</div>` : ''}
            <div class="subsea-builder__spec-grid">
              <div class="subsea-builder__spec">${priceDisplay}</div>
              <div class="subsea-builder__spec">${depthRating}</div>
              <div class="subsea-builder__spec">${sku}</div>
          </div>
            <div class="subsea-builder__availability">${availabilityHtml}</div>
          <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}</button>
          </div>
        </div>
      `;
    },

    // Bind Interior Fitting events
    bindInteriorFittingEvents: function(componentType) {
      var section = this.container.querySelector('[data-component="' + componentType + '"]');
      if (!section) {
        return;
      }
      
      var buttons = section.querySelectorAll('.select-component-btn');
      
      buttons.forEach(function(btn) {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          var parentItem = btn.closest('[data-variant-id]');
          var variantId = parentItem.getAttribute('data-variant-id');
          
          if (variantId === 'none') {
            // Handle no selection option
            this.handleInteriorFittingSelection(null, componentType);
          } else {
            // Find variant data
            var variant = this.findVariantById(variantId, componentType);
            if (variant) {
              this.handleInteriorFittingSelection(variant, componentType);
            } else {
            }
          }
        }.bind(this));
      }.bind(this));
    },

    // Handle Interior Fitting selection
    handleInteriorFittingSelection: function(variant, componentType) {
      
      // Mark as Interior Fitting for depth rating exclusion
      if (variant) {
        variant.isInteriorFitting = true;
      }
      
      // Store selection
      if (componentType === 'electronic-tray') {
        this.selectedComponents.interiorFittings.electronicTray = variant;
        
        // Reset Vent & Vacuum Control when interior fittings change
        this.resetAndHideVentVacuumControl();
        // If Electronic Tray Set was selected, show and reload PCB Shelf with new compatibility
        if (variant) {
          // Reset PCB Shelf selections to undefined since user needs to make a new choice
          this.selectedComponents.interiorFittings.pcbShelf = undefined;
          this.selectedComponents.interiorFittings.pcbShelfSecondary = undefined;
          
          var pcbShelfSection = this.container.querySelector('[data-component="pcb-shelf"]');
          var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
          if (pcbShelfSection) {
            pcbShelfSection.style.display = 'block';
          }
          if (pcbShelfSecondarySection) {
            pcbShelfSecondarySection.style.display = 'none';
          }
          this.loadInsulatedPcbShelf();
          
          // Check tray diameter to determine if secondary shelf should be shown
          this.updateSecondaryShelfVisibility(variant);
        } else {
          // If no electronic tray set was selected, hide both PCB Shelves and auto-select no PCB Shelf
          var pcbShelfSection = this.container.querySelector('[data-component="pcb-shelf"]');
          var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
          if (pcbShelfSection) {
            pcbShelfSection.style.display = 'none';
          }
          if (pcbShelfSecondarySection) {
            pcbShelfSecondarySection.style.display = 'none';
          }
          // Auto-select no PCB Shelf options
          this.selectedComponents.interiorFittings.pcbShelf = null;
          this.selectedComponents.interiorFittings.pcbShelfSecondary = null;
          this.updateInteriorFittingSelection('pcb-shelf', null);
          this.updateInteriorFittingSelection('pcb-shelf-secondary', null);
        }
      } else if (componentType === 'pcb-shelf') {
        this.selectedComponents.interiorFittings.pcbShelf = variant;
        
        // Reset Vent & Vacuum Control when interior fittings change
        this.resetAndHideVentVacuumControl();

        if (variant === null) {
          this.selectedComponents.interiorFittings.pcbShelfSecondary = null;
        } else {
          // Changing primary shelf invalidates secondary until re-selected
          this.selectedComponents.interiorFittings.pcbShelfSecondary = undefined;
        }
        var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
        if (pcbShelfSecondarySection) {
          pcbShelfSecondarySection.style.display = 'none';
        }
      } else if (componentType === 'pcb-shelf-secondary') {
        this.selectedComponents.interiorFittings.pcbShelfSecondary = variant;
        
        // Reset Vent & Vacuum Control when interior fittings change
        this.resetAndHideVentVacuumControl();
      }
      
      // Update visual feedback
      this.updateInteriorFittingSelection(componentType, variant);
      
      // Check if both Interior Fittings are complete and collapse section
      this.checkAndCollapseInteriorFittings();
      
      // Update summary and calculations
      this.updateSummary();
      this.calculateTotalPrice();
    },

    // Update Interior Fitting visual selection
    updateInteriorFittingSelection: function(componentType, variant) {
      var section = this.container.querySelector('[data-component="' + componentType + '"]');
      if (!section) return;
      
      // Remove previous selections
      var previousSelected = section.querySelectorAll('.is-selected');
      previousSelected.forEach(function(item) {
        item.classList.remove('is-selected');
        var btn = item.querySelector('.select-component-btn');
        if (btn) btn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option";
      });
      
      // Add selection to current item
      var targetId = variant ? variant.id : 'none';
      var selectedItem = section.querySelector('[data-variant-id="' + targetId + '"]');
      if (selectedItem) {
        selectedItem.classList.add('is-selected');
        var btn = selectedItem.querySelector('.select-component-btn');
        if (btn) btn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selected) || "Selected";
      }
    },
    // Update secondary shelf visibility based on tray diameter
    updateSecondaryShelfVisibility: function(electronicTray) {
      var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
      if (!pcbShelfSecondarySection) {
        return;
      }
      
      var trayDiameter = electronicTray?.metafields?.custom_bsub_tray_diameter;
      
      // Convert to number for consistent comparison
      var trayDiameterNum = parseInt(trayDiameter, 10);
      
      if (trayDiameterNum === 85) {
        // 85mm tray - only 1 shelf allowed, hide secondary and auto-select "No"
        pcbShelfSecondarySection.style.display = 'none';
        this.selectedComponents.interiorFittings.pcbShelfSecondary = null;
        this.updateInteriorFittingSelection('pcb-shelf-secondary', null);
      } else if (trayDiameterNum === 115 || trayDiameterNum === 165) {
        // 115mm or 165mm tray - up to 2 shelves allowed, but only show secondary after primary is selected
        // Don't show secondary shelf yet - it will be shown when primary shelf is selected
        pcbShelfSecondarySection.style.display = 'none';
        // Reset secondary shelf selection when tray changes
        this.selectedComponents.interiorFittings.pcbShelfSecondary = undefined;
      } else {
        // Unknown diameter - hide secondary and auto-select "No"
        pcbShelfSecondarySection.style.display = 'none';
        this.selectedComponents.interiorFittings.pcbShelfSecondary = null;
        this.updateInteriorFittingSelection('pcb-shelf-secondary', null);
      }
    },

    // Find variant by ID for Interior Fittings
    findVariantById: function(variantId, componentType) {
      var productKey;
      if (componentType === 'electronic-tray') {
        productKey = 'electronic_tray_set';
      } else if (componentType === 'pcb-shelf' || componentType === 'pcb-shelf-secondary') {
        productKey = 'insulated_pcb_shelf';
      } else {
        return null;
      }
      var productData = this.productsData.flanges_endcaps[productKey];
      
      if (!productData) {
        return null;
      }
      
      for (var i = 0; i < productData.variants.length; i++) {
        if (productData.variants[i].id == variantId) {
          return productData.variants[i];
        }
      }
      
      return null;
    },

    // Check if Interior Fittings are complete and collapse section
    checkAndCollapseInteriorFittings: function() {
      var electronicTray = this.selectedComponents.interiorFittings.electronicTray;
      var pcbShelf = this.selectedComponents.interiorFittings.pcbShelf;
      var pcbShelfSecondary = this.selectedComponents.interiorFittings.pcbShelfSecondary;
      
      // Check if selections are made (including "none" selections)
      // electronicTraySelected is true if electronicTray is defined (including null for "none")
      var electronicTraySelected = electronicTray !== undefined;
      var pcbShelfSelected = pcbShelf !== undefined;
      var pcbShelfSecondarySelected = pcbShelfSecondary !== undefined;
      
      // Special case: If Electronic Tray Set is "none", then both PCB Shelves are auto-selected as "none"
      if (electronicTray === null) {
        pcbShelfSelected = true; // We auto-selected "none" for PCB Shelf
        pcbShelfSecondarySelected = true; // We auto-selected "none" for PCB Shelf Secondary
      }
      
      // Check if secondary shelf should be available based on tray diameter
      var trayDiameter = electronicTray?.metafields?.custom_bsub_tray_diameter;
      var trayDiameterNum = parseInt(trayDiameter, 10);
      var shouldShowSecondary = trayDiameterNum === 115 || trayDiameterNum === 165;
      
      // Show secondary shelf if it should be available and primary shelf is selected
      if (shouldShowSecondary && pcbShelfSelected && pcbShelf !== null) {
        var pcbShelfSecondarySection = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
        if (pcbShelfSecondarySection) {
          pcbShelfSecondarySection.style.display = 'block';
          this.loadInsulatedPcbShelfSecondary();
          // Don't collapse yet - let user select secondary shelf or choose "No"
          // Only collapse if secondary shelf has been selected (including "No" selection)
          var canCollapse = electronicTraySelected && pcbShelfSelected && pcbShelfSecondarySelected;
        } else {
          var canCollapse = electronicTraySelected && pcbShelfSelected;
        }
      } else {
        // Can collapse as soon as electronic tray and primary shelf are selected
        // Secondary shelf is optional - user can select it or skip it
        var canCollapse = electronicTraySelected && pcbShelfSelected;
      }
      
      
      // Only collapse if all required selections are actually made
      if (canCollapse) {
        this.collapseInteriorFittingsSection();
        // Show Vent & Vacuum Control after Interior Fittings is complete
        this.showVentVacuumControl();
      } else {
      }
    },

    // Collapse Interior Fittings section
    collapseInteriorFittingsSection: function() {
      
      // Find the Interior Fittings section by looking for the section that contains both electronic-tray and pcb-shelf components
      var electronicTrayComponent = this.container.querySelector('[data-component="electronic-tray"]');
      var pcbShelfComponent = this.container.querySelector('[data-component="pcb-shelf"]');
      
      
      if (!electronicTrayComponent || !pcbShelfComponent) {
        return;
      }
      
      // Find the parent section that contains both components
      var interiorFittingsSection = electronicTrayComponent.closest('.subsea-builder__end-section');
      if (!interiorFittingsSection) {
        return;
      }
      
      var electronicTray = this.selectedComponents.interiorFittings.electronicTray;
      var pcbShelf = this.selectedComponents.interiorFittings.pcbShelf;
      var pcbShelfSecondary = this.selectedComponents.interiorFittings.pcbShelfSecondary;
      
      // Create collapsed view for Interior Fittings using the new Tube Configuration format
      var collapsedHTML = `
        <div class="subsea-builder__selected-interior-fittings">
          <style>
            .subsea-builder__selected-interior-fittings-info .subsea-builder__product-title {
              font-size: 1.1em;
              font-weight: bold;
              margin: 0 0 0.5em 0;
              color: #333;
            }
          </style>
          <div class="subsea-builder__selected-interior-fittings-info">
            <h4>${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectedInteriorFittingsConfiguration) || "SELECTED INTERIOR FITTINGS CONFIGURATION")}</h4>
            
            ${electronicTray ? `
              <!-- Electronic Tray Set Product Box -->
              <div class="subsea-builder__product-item" style="margin-top: 1em;">
                <div class="subsea-builder__product-image">
                  <a href="${this.createProductUrl(electronicTray)}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                    <img src="${this.getImageWithFallback(electronicTray)}" alt="${electronicTray.metafields?.custom_tube_builder_product_title || electronicTray.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.electronicTraySetLabel) || "Electronic Tray Set")}" class="subsea-builder__image">
                  </a>
            </div>
                <div class="subsea-builder__product-info">
                  <div class="subsea-builder__product-title">${electronicTray.metafields?.custom_tube_builder_product_title || electronicTray.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.electronicTraySetLabel) || "Electronic Tray Set")}</div>
                  ${(() => {
                    var customDescription = electronicTray.metafields?.custom_tube_builder_product_description || '';
                    if (customDescription) {
                      customDescription = String(customDescription);
                      if (this.isBase64(customDescription)) {
                        try {
                          customDescription = atob(customDescription);
                        } catch (e) {
                        }
                      }
                      return `<div class="subsea-builder__product-description">${customDescription}</div>`;
                    }
                    return '';
                  })()}
                  <div class="subsea-builder__spec-grid">
                    <div class="subsea-builder__spec">${this.formatPrice(electronicTray.price) ? `<strong>${this.formatPrice(electronicTray.price)}</strong>` : 'N/A'}</div>
                    <div class="subsea-builder__spec">${electronicTray.metafields?.custom_bsub_depth_m ? `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(electronicTray.metafields.custom_bsub_depth_m)}m / ${this.formatNumber(Math.round(electronicTray.metafields.custom_bsub_depth_m * 3.28084))}ft` : `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`}</div>
                    <div class="subsea-builder__spec">${electronicTray.sku || 'N/A'}</div>
                  </div>
                  <div class="subsea-builder__availability">${this.createAvailabilityHtml(electronicTray, this.getProductMetafields(electronicTray).custom_in_stock_lead_time || 'N/A', this.getProductMetafields(electronicTray).custom_out_of_stock_lead_time || 'N/A')}</div>
                </div>
              </div>
            ` : `
              <!-- No Electronic Tray Set Required -->
              <div class="subsea-builder__product-item" style="margin-top: 1em; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
                <div class="subsea-builder__product-info">
                  <div class="subsea-builder__product-title" style="color: #6c757d;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noElectronicTraySet) || "No Electronic Tray Set")}</div>
                  <div class="subsea-builder__product-description" style="color: #6c757d; font-style: italic;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.optionalComponentNotSelected) || "Optional component - not selected")}</div>
                </div>
              </div>
            `}
            
            ${pcbShelf ? `
              <!-- PCB Shelf Product Box -->
              <div class="subsea-builder__product-item" style="margin-top: 1em;">
                <div class="subsea-builder__product-image">
                  <a href="${this.createProductUrl(pcbShelf)}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                    <img src="${this.getImageWithFallback(pcbShelf)}" alt="${pcbShelf.metafields?.custom_tube_builder_product_title || pcbShelf.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.pcbShelfLabel) || 'PCB Shelf')}" class="subsea-builder__image">
                  </a>
                </div>
                <div class="subsea-builder__product-info">
                  <div class="subsea-builder__product-title">${pcbShelf.metafields?.custom_tube_builder_product_title || pcbShelf.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.unknownPcbShelf) || "Unknown PCB Shelf")}</div>
                  ${(() => {
                    var customDescription = pcbShelf.metafields?.custom_tube_builder_product_description || '';
                    if (customDescription) {
                      customDescription = String(customDescription);
                      if (this.isBase64(customDescription)) {
                        try {
                          customDescription = atob(customDescription);
                        } catch (e) {
                        }
                      }
                      return `<div class="subsea-builder__product-description">${customDescription}</div>`;
                    }
                    return '';
                  })()}
                  <div class="subsea-builder__spec-grid">
                    <div class="subsea-builder__spec">${this.formatPrice(pcbShelf.price) ? `<strong>${this.formatPrice(pcbShelf.price)}</strong>` : 'N/A'}</div>
                    <div class="subsea-builder__spec">${pcbShelf.metafields?.custom_bsub_depth_m ? `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(pcbShelf.metafields.custom_bsub_depth_m)}m / ${this.formatNumber(Math.round(pcbShelf.metafields.custom_bsub_depth_m * 3.28084))}ft` : `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`}</div>
                    <div class="subsea-builder__spec">${pcbShelf.sku || 'N/A'}</div>
                  </div>
                  <div class="subsea-builder__availability">${this.createAvailabilityHtml(pcbShelf, this.getProductMetafields(pcbShelf).custom_in_stock_lead_time || 'N/A', this.getProductMetafields(pcbShelf).custom_out_of_stock_lead_time || 'N/A')}</div>
                </div>
              </div>
            ` : `
              <!-- No PCB Shelf Required -->
              <div class="subsea-builder__product-item" style="margin-top: 1em; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
                <div class="subsea-builder__product-info">
                  <div class="subsea-builder__product-title" style="color: #6c757d;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noPcbShelf) || "No PCB Shelf"}</div>
                  <div class="subsea-builder__product-description" style="color: #6c757d; font-style: italic;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.optionalComponentNotSelected) || "Optional component - not selected")}</div>
                </div>
              </div>
            `}
            
            ${pcbShelfSecondary ? `
              <!-- PCB Shelf Secondary Product Box -->
              <div class="subsea-builder__product-item" style="margin-top: 1em;">
                <div class="subsea-builder__product-image">
                  <a href="${this.createProductUrl(pcbShelfSecondary)}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                    <img src="${this.getImageWithFallback(pcbShelfSecondary)}" alt="${pcbShelfSecondary.metafields?.custom_tube_builder_product_title || pcbShelfSecondary.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.pcbShelfSecondaryLabel) || 'PCB Shelf - Secondary')}" class="subsea-builder__image">
                  </a>
                </div>
                <div class="subsea-builder__product-info">
                  <div class="subsea-builder__product-title">${pcbShelfSecondary.metafields?.custom_tube_builder_product_title || pcbShelfSecondary.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.unknownPcbShelfSecondary) || "Unknown PCB Shelf - Secondary")}</div>
                  ${(() => {
                    var customDescription = pcbShelfSecondary.metafields?.custom_tube_builder_product_description || '';
                    if (customDescription) {
                      customDescription = String(customDescription);
                      if (this.isBase64(customDescription)) {
                        try {
                          customDescription = atob(customDescription);
                        } catch (e) {
                        }
                      }
                      return `<div class="subsea-builder__product-description">${customDescription}</div>`;
                    }
                    return '';
                  })()}
                  <div class="subsea-builder__spec-grid">
                    <div class="subsea-builder__spec">${this.formatPrice(pcbShelfSecondary.price) ? `<strong>${this.formatPrice(pcbShelfSecondary.price)}</strong>` : 'N/A'}</div>
                    <div class="subsea-builder__spec">${pcbShelfSecondary.metafields?.custom_bsub_depth_m ? `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(pcbShelfSecondary.metafields.custom_bsub_depth_m)}m / ${this.formatNumber(Math.round(pcbShelfSecondary.metafields.custom_bsub_depth_m * 3.28084))}ft` : `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`}</div>
                    <div class="subsea-builder__spec">${pcbShelfSecondary.sku || 'N/A'}</div>
                  </div>
                  <div class="subsea-builder__availability">${this.createAvailabilityHtml(pcbShelfSecondary, this.getProductMetafields(pcbShelfSecondary).custom_in_stock_lead_time || 'N/A', this.getProductMetafields(pcbShelfSecondary).custom_out_of_stock_lead_time || 'N/A')}</div>
                </div>
              </div>
            ` : `
              <!-- No PCB Shelf Secondary Required -->
              <div class="subsea-builder__product-item" style="margin-top: 1em; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
                <div class="subsea-builder__product-info">
                  <div class="subsea-builder__product-title" style="color: #6c757d;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noPcbShelfSecondary) || "No PCB Shelf - Secondary"}</div>
                  <div class="subsea-builder__product-description" style="color: #6c757d; font-style: italic;">${(() => {
                    // Check if this is the 85mm diameter scenario
                    var trayDiameter = electronicTray?.metafields?.custom_bsub_tray_diameter;
                    var trayDiameterNum = parseInt(trayDiameter, 10);
                    if (trayDiameterNum === 85) {
                      return ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.optionalComponentNotAvailableForConfiguration) || "Optional component not available for this configuration");
                    } else {
                      return ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.optionalComponentNotSelected) || "Optional component - not selected");
                    }
                  })()}</div>
                </div>
              </div>
            `}
            
            <button class="btn btn--secondary change-interior-fittings-btn" style="margin-top: 1em;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.changeInteriorFittings) || "Change Interior Fittings")}</button>
          </div>
        </div>
      `;
      
      // Use the same logic as End 2 - replace the component content instead of hiding
      
      if (electronicTrayComponent) {
        // Replace the electronic tray component content with the collapsed view (hiding step title)
        electronicTrayComponent.innerHTML = `
          <div class="subsea-builder__component-content">
            ${collapsedHTML}
          </div>
        `;
        
        // Hide the PCB shelf components
        pcbShelfComponent.style.display = 'none';
        var pcbShelfSecondaryComponent = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
        if (pcbShelfSecondaryComponent) {
          pcbShelfSecondaryComponent.style.display = 'none';
        }
        
        // Bind click event to change button
        var changeBtn = electronicTrayComponent.querySelector('.change-interior-fittings-btn');
        
        if (changeBtn) {
          changeBtn.addEventListener('click', function(e) {
            e.preventDefault();
            this.expandInteriorFittingsSection();
          }.bind(this));
        }
      } else {
      }
      
    },

    // Expand Interior Fittings section
    expandInteriorFittingsSection: function() {
      
      // Find the Interior Fittings section by looking for the section that contains both electronic-tray and pcb-shelf components
      var electronicTrayComponent = this.container.querySelector('[data-component="electronic-tray"]');
      var pcbShelfComponent = this.container.querySelector('[data-component="pcb-shelf"]');
      
      if (!electronicTrayComponent || !pcbShelfComponent) {
        return;
      }
      
      // Find the parent section that contains both components
      var interiorFittingsSection = electronicTrayComponent.closest('.subsea-builder__end-section');
      if (!interiorFittingsSection) {
        return;
      }
      
      // Remove collapsed view (check for all possible class names)
      var collapsedViewOld = interiorFittingsSection.querySelector('.subsea-builder__selected-interior-fittings');
      if (collapsedViewOld) {
        collapsedViewOld.remove();
      }
      
      var collapsedViewNew = interiorFittingsSection.querySelector('.subsea-builder__selected-end1');
      if (collapsedViewNew) {
        collapsedViewNew.remove();
      }
      
      // Show the individual components again
      electronicTrayComponent.style.display = 'block';
      
      // When expanding, always hide PCB shelf components initially - user needs to make a new Electronic Tray Set selection
      pcbShelfComponent.style.display = 'none';
      var pcbShelfSecondaryComponent = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
      if (pcbShelfSecondaryComponent) {
        pcbShelfSecondaryComponent.style.display = 'none';
      }
      
      // Reset PCB Shelf selections when expanding - user needs to make fresh selections
      this.selectedComponents.interiorFittings.pcbShelf = undefined;
      this.selectedComponents.interiorFittings.pcbShelfSecondary = undefined;
      
      // Clear Vent & Vacuum Control when expanding Interior Fittings
      this.resetAndHideVentVacuumControl();
      
      // Reload the Interior Fittings products
      this.loadInteriorFittingsProducts();
      
      // Keep PCB shelf hidden initially - it will be shown when user selects Electronic Tray Set
      pcbShelfComponent.style.display = 'none';
      
      // Re-bind events
      this.bindInteriorFittingEvents('electronic-tray');
      this.bindInteriorFittingEvents('pcb-shelf');
      this.bindInteriorFittingEvents('pcb-shelf-secondary');
      
      // Update summary and button state
      this.updateSummary();
      
    },

    updateCompatibility: function(selectedType, endNumber) {
      if (selectedType === 'tube') {
        // When tube is selected, show compatible flanges for End 1 only
        this.showCompatibleFlanges(1);
        // Don't show End 2 until End 1 is complete
      } else if (selectedType === 'flange' && endNumber) {
        // When flange is selected, show compatible end-caps for that specific end
        this.showCompatibleEndCaps(endNumber);
      }
    },

    // Helper function to check if an end is complete
    isEndComplete: function(endNumber) {
      var end = this.selectedComponents['end' + endNumber];
      if (!end) {
        return false;
      }
      
      // Check if it's an assembly (complete in one selection)
      if (end.flange && (end.flange._flangeTypeKey === 'assembly' || (end.flange.metafields && normalizeFlangeType(end.flange.metafields.custom_bsub_flange_type) === 'assembly'))) {
        return true;
      }
      
      // Check if both flange and end-cap are selected
      var isComplete = !!(end.flange && end['end-cap']);
      return isComplete;
    },

    // Helper function to show End 2
    showEnd2: function() {
      var end2Flange = this.container.querySelector('[data-component="flange"][data-end="2"]');
      if (end2Flange) {
        // Restore original structure if it was collapsed
        if (!end2Flange.querySelector('.subsea-builder__component-title')) {
          end2Flange.innerHTML = `
            <h3 class="subsea-builder__component-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectFlangeEnd2Numbered) || "4. Select Your Flange (End 2)"}</h3>
            <div class="subsea-builder__component-content">
              <p class="subsea-builder__component-note">Complete End 1 configuration first</p>
            </div>
          `;
        }
        end2Flange.style.display = 'block';
        // Populate End 2 with compatible flanges
        this.showCompatibleFlanges(2);
      } else {
      }
    },

    // Helper function to reset selections for a specific end
    resetEndSelections: function(endNumber) {
      var end = this.selectedComponents['end' + endNumber];
      if (!end) return;
      
      // Clear selections
      end.flange = null;
      end['end-cap'] = null;
      
      // Remove visual selections
      var endFlange = this.container.querySelector('[data-component="flange"][data-end="' + endNumber + '"]');
      var endEndCap = this.container.querySelector('[data-component="end-cap"][data-end="' + endNumber + '"]');
      
      if (endFlange) {
        var selectedFlange = endFlange.querySelector('.' + classes.selected);
        if (selectedFlange) {
          selectedFlange.classList.remove(classes.selected);
          var button = selectedFlange.querySelector('.select-component-btn');
          if (button) button.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisFlange) || "Select This Flange";
        }
      }
      
      if (endEndCap) {
        var selectedEndCap = endEndCap.querySelector('.' + classes.selected);
        if (selectedEndCap) {
          selectedEndCap.classList.remove(classes.selected);
          var button = selectedEndCap.querySelector('.select-component-btn');
          if (button) button.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisEndcap) || "Select This End-Cap";
        }
        endEndCap.style.display = 'none';
      }
      
      // Update summary
      this.updateSummary();
    },

    // Helper function to reset ALL downstream selections (both ends)
    resetAllDownstreamSelections: function() {
      
      // Remove any collapsed views
      var collapsedEnd1 = this.container.querySelector('.subsea-builder__selected-end1');
      if (collapsedEnd1) {
        collapsedEnd1.remove();
      }
      
      var collapsedEnd2 = this.container.querySelector('.subsea-builder__selected-end2');
      if (collapsedEnd2) {
        collapsedEnd2.remove();
      }
      
      // Remove Interior Fittings collapsed view if it exists
      // Find the Interior Fittings section by looking for the section that contains both electronic-tray and pcb-shelf components
      var electronicTrayComponent = this.container.querySelector('[data-component="electronic-tray"]');
      var pcbShelfComponent = this.container.querySelector('[data-component="pcb-shelf"]');
      
      if (electronicTrayComponent && pcbShelfComponent) {
        var interiorFittingsSection = electronicTrayComponent.closest('.subsea-builder__end-section');
        if (interiorFittingsSection) {
          // Check for all possible class names
          var collapsedInteriorFittings = interiorFittingsSection.querySelector('.subsea-builder__selected-end1');
          if (collapsedInteriorFittings) {
            collapsedInteriorFittings.remove();
          }
          
          var collapsedInteriorFittingsNew = interiorFittingsSection.querySelector('.subsea-builder__selected-interior-fittings');
          if (collapsedInteriorFittingsNew) {
            collapsedInteriorFittingsNew.remove();
          }
        }
      }
      
      // Remove Vent & Vacuum Control collapsed view if it exists
      var ventComponent = this.container.querySelector('[data-component="vent"]');
      var vacuumPlugComponent = this.container.querySelector('[data-component="vacuum-plug"]');
      
      if (ventComponent && vacuumPlugComponent) {
        var ventVacuumControlSection = ventComponent.closest('.subsea-builder__end-section');
        if (ventVacuumControlSection) {
          // Check for all possible class names
          var collapsedVentVacuumControl = ventVacuumControlSection.querySelector('.subsea-builder__selected-end1');
          if (collapsedVentVacuumControl) {
            collapsedVentVacuumControl.remove();
          }
          
          var collapsedVentVacuumControlNew = ventVacuumControlSection.querySelector('.subsea-builder__selected-vent-vacuum');
          if (collapsedVentVacuumControlNew) {
            collapsedVentVacuumControlNew.remove();
          }
          
          // Also check for the end1 class since we're now using it
          var collapsedVentVacuumControlEnd1 = ventVacuumControlSection.querySelector('.subsea-builder__selected-end1');
          if (collapsedVentVacuumControlEnd1) {
            collapsedVentVacuumControlEnd1.remove();
          }
        }
      }
      
      // Reset both ends
      this.resetEndSelections(1);
      this.resetEndSelections(2);
      
      // Clear the selectedComponents data structure
      this.selectedComponents.end1 = { flange: null, 'end-cap': null };
      this.selectedComponents.end2 = { flange: null, 'end-cap': null };
      this.selectedComponents.interiorFittings = { electronicTray: undefined, pcbShelf: undefined, pcbShelfSecondary: undefined };
      this.selectedComponents.ventVacuumControl = { vent: undefined, vacuumPlug: undefined };
      
      // Hide all downstream sections
      var end1Flange = this.container.querySelector('[data-component="flange"][data-end="1"]');
      var end1EndCap = this.container.querySelector('[data-component="end-cap"][data-end="1"]');
      var end2Flange = this.container.querySelector('[data-component="flange"][data-end="2"]');
      var end2EndCap = this.container.querySelector('[data-component="end-cap"][data-end="2"]');
      var electronicTray = this.container.querySelector('[data-component="electronic-tray"]');
      var pcbShelf = this.container.querySelector('[data-component="pcb-shelf"]');
      var pcbShelfSecondary = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
      var vent = this.container.querySelector('[data-component="vent"]');
      var vacuumPlug = this.container.querySelector('[data-component="vacuum-plug"]');
      
      if (end1Flange) end1Flange.style.display = 'none';
      if (end1EndCap) end1EndCap.style.display = 'none';
      if (end2Flange) end2Flange.style.display = 'none';
      if (end2EndCap) end2EndCap.style.display = 'none';
      if (electronicTray) electronicTray.style.display = 'none';
      if (pcbShelf) pcbShelf.style.display = 'none';
      if (pcbShelfSecondary) {
        pcbShelfSecondary.style.display = 'none';
        pcbShelfSecondary.querySelectorAll('.is-selected').forEach(function(el) {
          el.classList.remove(classes.selected);
        });
        pcbShelfSecondary.querySelectorAll('.select-component-btn').forEach(function(btn) {
          btn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || 'Select This Option';
        });
      }
      if (vent) vent.style.display = 'none';
      if (vacuumPlug) vacuumPlug.style.display = 'none';
      
      // Update summary
      this.updateSummary();
    },

    // Reset tube selection options
    resetTubeSelection: function() {
      
      // Clear tube selection options
      this.selectedTubeOptions = {};
      
      // Clear the selected tube
      this.selectedComponents.tube = null;
      
      // Reset button selections
      var diameterButtons = this.container.querySelectorAll('[data-diameter]');
      diameterButtons.forEach(function(btn) {
        btn.classList.remove('is-selected');
      });
      
      var materialButtons = this.container.querySelectorAll('[data-material]');
      materialButtons.forEach(function(btn) {
        btn.classList.remove('is-selected');
      });
      
      // Remove any selected tube elements
      var selectedTubeElements = this.container.querySelectorAll('[data-component="tube"] .is-selected');
      selectedTubeElements.forEach(function(element) {
        element.classList.remove('is-selected');
        var button = element.querySelector('.select-component-btn');
        if (button) {
          button.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisTube) || "Select This Tube";
        }
      });
      
      // Hide material and length subcategories
      var materialSubcategory = this.container.querySelector('[data-subcategory="material"]');
      var lengthSubcategory = this.container.querySelector('[data-subcategory="length"]');
      
      if (materialSubcategory) materialSubcategory.style.display = 'none';
      if (lengthSubcategory) lengthSubcategory.style.display = 'none';
      
      // Clear length grid
      var lengthGrid = this.container.querySelector('[data-subcategory="length"] .subsea-builder__product-grid');
      if (lengthGrid) lengthGrid.innerHTML = '';
      
      // Reset all downstream selections
      this.resetAllDownstreamSelections();
      
      // Update summary
      this.updateSummary();
    },

    // Reset tube selection options (partial - keeps diameter)
    resetTubeSelectionPartial: function() {
      
      // Clear material and length from options
      if (this.selectedTubeOptions) {
        this.selectedTubeOptions.material = null;
      }
      
      // Reset material button selections when diameter changes
      var materialButtons = this.container.querySelectorAll('[data-subcategory="material"] [data-material]');
      materialButtons.forEach(function(btn) {
        btn.classList.remove('is-selected');
      });
      
      // Clear any selected tube when diameter changes
      this.clearSelectedTube();
      
      // Keep diameter and material subcategories visible, only hide length
      var lengthSubcategory = this.container.querySelector('[data-subcategory="length"]');
      if (lengthSubcategory) lengthSubcategory.style.display = 'none';
      
      // Clear length grid
      var lengthGrid = this.container.querySelector('[data-subcategory="length"] .subsea-builder__product-grid');
      if (lengthGrid) lengthGrid.innerHTML = '';
    },

    // Clear tube selection options when a specific tube is selected
    clearTubeSelectionOptions: function() {
      
      // Keep the selected options for display purposes
      // Don't clear this.selectedTubeOptions completely
      
      // DON'T reset diameter and material button selections - keep them selected
      // This preserves the visual state showing what was selected
      
      // Keep diameter and material subcategories visible, only hide length
      var lengthSubcategory = this.container.querySelector('[data-subcategory="length"]');
      if (lengthSubcategory) lengthSubcategory.style.display = 'none';
      
      // Clear length grid
      var lengthGrid = this.container.querySelector('[data-subcategory="length"] .subsea-builder__product-grid');
      if (lengthGrid) lengthGrid.innerHTML = '';
    },
    // Format price using the theme's currency formatting (same as product pages)
    formatPrice: function(priceInCents) {
      // Use theme's Currency.formatMoney function - this handles multi-currency and exchange rates
      if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
        return window.theme.Currency.formatMoney(priceInCents, window.theme.settings.moneyFormat);
      }
      
      // Fallback: Use theme's money format if available
      if (window.theme && window.theme.settings && window.theme.settings.moneyFormat) {
        return window.theme.settings.moneyFormat.replace('{{amount}}', (priceInCents / 100).toFixed(2));
      }
      
      // Final fallback: Basic USD formatting
      return '$' + (priceInCents / 100).toFixed(2);
    },

    // Helper function to get image with fallback logic: variant image → product image → standard fallback
    getImageWithFallback: function(variant, productImage) {
      // Standard fallback image (base64 SVG like "No Electronic Tray Set")
      var fallbackImage = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik01MCA5MEgxNTBWMTEwSDUwVjkwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K";
      
      // Fallback logic: variant.image → variant.product_image → variant.featured_image → productImage → fallback
      return variant.image || 
             variant.product_image || 
             variant.featured_image || 
             productImage || 
             fallbackImage;
    },

    // Clear selected tube (used when diameter or material changes)
    clearSelectedTube: function() {
      
      // Clear the selected tube from the data structure
      this.selectedComponents.tube = null;
      
      // Remove selection from any selected tube elements
      var selectedTubeElements = this.container.querySelectorAll('[data-component="tube"] .is-selected');
      selectedTubeElements.forEach(function(element) {
        element.classList.remove('is-selected');
        var button = element.querySelector('.select-component-btn');
        if (button) {
          button.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisTube) || "Select This Tube";
        }
      });
      
      // Update summary to reflect the cleared tube selection
      this.updateSummary();
    },

    // Collapse tube selection to show only selected tube
    collapseTubeSelection: function() {
      var tubeSection = this.container.querySelector('[data-component="tube"]');
      if (!tubeSection) return;
      
      var selectedTube = this.selectedComponents.tube;
      if (!selectedTube) return;
      
      // Hide all subcategories and product grids
      var subcategories = tubeSection.querySelectorAll('.subsea-builder__subcategory');
      subcategories.forEach(function(subcategory) {
        subcategory.style.display = 'none';
      });
      
      // Get metafields and product data for the selected tube
      var metafields = selectedTube.metafields || {};
      var productMetafields = this.getProductMetafields(selectedTube);
      var productInStockLeadTime = productMetafields.custom_in_stock_lead_time || 'N/A';
      var productOutOfStockLeadTime = productMetafields.custom_out_of_stock_lead_time || 'N/A';
      
      // Get custom title and description from metafields
      var customTitle = metafields.custom_tube_builder_product_title || 
                       selectedTube.product_title || 
                       selectedTube.title || 
                       'N/A';
      
      // Handle description - should now be base64 encoded string from Liquid template
      var customDescription = metafields.custom_tube_builder_product_description || '';
      
      // If it's still an object (fallback), try to extract the value
      if (typeof customDescription === 'object' && customDescription !== null) {
        if (customDescription.value !== undefined) {
          customDescription = customDescription.value;
        } else if (customDescription.text !== undefined) {
          customDescription = customDescription.text;
        } else if (customDescription.content !== undefined) {
          customDescription = customDescription.content;
        } else if (customDescription.html !== undefined) {
          customDescription = customDescription.html;
        } else if (customDescription.raw !== undefined) {
          customDescription = customDescription.raw;
        } else {
          customDescription = '';
        }
      }
      
      // Ensure it's a string and decode if it's base64
      customDescription = String(customDescription || '');
      
      // Try to decode base64 if it looks like base64 content
      if (customDescription && this.isBase64(customDescription)) {
        try {
          customDescription = atob(customDescription);
        } catch (e) {
        }
      }
      
      // Get other required data
      var depthRatingM = (metafields.custom_bsub_depth_m != null && metafields.custom_bsub_depth_m !== '') ? metafields.custom_bsub_depth_m : 'N/A';
      var depthRating = depthRatingM !== 'N/A' ? 
        `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(depthRatingM)}m / ${this.formatNumber(Math.round(depthRatingM * 3.28084))}ft` : 
        'N/A';
      var sku = (selectedTube.sku && selectedTube.sku !== '') ? selectedTube.sku : 'N/A';
      var priceDisplay = this.formatPrice(selectedTube.price) ? `<strong>${this.formatPrice(selectedTube.price)}</strong>` : 'N/A';
      
      // Create availability HTML based on product page logic
      var availabilityHtml = this.createAvailabilityHtml(selectedTube, productInStockLeadTime, productOutOfStockLeadTime);
      
      // Create product URL with variant parameter
      var productUrl = this.createProductUrl(selectedTube);
      
      // Create collapsed view with selected tube using the same design as product selection
      var collapsedHTML = `
        <div class="subsea-builder__selected-tube">
          <style>
            .subsea-builder__selected-tube-info .subsea-builder__product-title {
              font-size: 1.1em;
              font-weight: bold;
              margin: 0 0 0.5em 0;
              color: #333;
            }
          </style>
          <div class="subsea-builder__selected-tube-info">
            <h4>${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectedTube) || "SELECTED TUBE"}</h4>
            <div class="subsea-builder__product-item" style="margin-top: 1em;">
              <div class="subsea-builder__product-image">
                <a href="${productUrl}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                  <img src="${this.getImageWithFallback(selectedTube)}" alt="${customTitle}" class="subsea-builder__image">
                </a>
              </div>
              <div class="subsea-builder__product-info">
                <div class="subsea-builder__product-title">${customTitle}</div>
                ${customDescription ? `<div class="subsea-builder__product-description">${customDescription}</div>` : ''}
                <div class="subsea-builder__spec-grid">
                  <div class="subsea-builder__spec">${priceDisplay}</div>
                  <div class="subsea-builder__spec">${depthRating}</div>
                  <div class="subsea-builder__spec">${sku}</div>
                </div>
                <div class="subsea-builder__availability">${availabilityHtml}</div>
              </div>
            </div>
            <button class="btn btn--secondary change-tube-selection-btn" style="margin-top: 1em;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.changeTubeSelection) || "Change Tube Selection"}</button>
          </div>
        </div>
      `;
      
      // Replace the tube section content (hiding step title like other collapsed sections)
      tubeSection.innerHTML = `
        <div class="subsea-builder__component-content">
          ${collapsedHTML}
        </div>
      `;
      
      // Bind the change button
      var changeBtn = tubeSection.querySelector('.change-tube-selection-btn');
      if (changeBtn) {
        changeBtn.addEventListener('click', function(e) {
          e.preventDefault();
          this.expandTubeSelection();
        }.bind(this));
      } else {
      }
    },

    // Expand tube selection to show all options again
    expandTubeSelection: function() {
      // Clear downstream state and UI first so summary never shows "no tube" while interior fittings still look selected.
      this.resetAllDownstreamSelections();
      // Clear the selected tube to reset the interface
      this.clearSelectedTube();
      
      // Get the tube section and restore the original HTML structure
      var tubeSection = this.container.querySelector('[data-component="tube"]');
      if (!tubeSection) return;
      
      // Restore the original tube section HTML structure
      tubeSection.innerHTML = `
        <h3 class="subsea-builder__component-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectTubeNumbered) || "1. Select Your Tube"}</h3>
        <div class="subsea-builder__component-content">
          <!-- Diameter Selection -->
          <div class="subsea-builder__subcategory" data-subcategory="diameter" style="display: block;">
            <h4 class="subsea-builder__subcategory-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.chooseDiameter) || "Choose Diameter"}</h4>
            <div class="subsea-builder__option-grid">
              <button class="subsea-builder__option-btn" data-diameter="85">85mm</button>
              <button class="subsea-builder__option-btn" data-diameter="115">115mm</button>
              <button class="subsea-builder__option-btn" data-diameter="165">165mm</button>
            </div>
          </div>
          
          <!-- Material Selection -->
          <div class="subsea-builder__subcategory" data-subcategory="material" style="display: none;">
            <h4 class="subsea-builder__subcategory-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.chooseMaterial) || "Choose Material"}</h4>
            <div class="subsea-builder__option-grid">
              <button class="subsea-builder__option-btn" data-material="Aluminum">Aluminum</button>
              <button class="subsea-builder__option-btn" data-material="Acrylic">Acrylic</button>
            </div>
          </div>
          
          <!-- Length Selection -->
          <div class="subsea-builder__subcategory" data-subcategory="length" style="display: none;">
            <h4 class="subsea-builder__subcategory-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.chooseLength) || "Choose Length"}</h4>
            <div class="subsea-builder__product-grid"></div>
          </div>
        </div>
      `;
      
      // Re-initialize the tube selection interface
      this.initializeTubeSelection();
      
      // Update summary and button state
      this.updateSummary();
    },

    // Collapse End 1 configuration to show only selected components
            collapseEnd1Configuration: function() {
          var end1Flange = this.container.querySelector('[data-component="flange"][data-end="1"]');
          var end1EndCap = this.container.querySelector('[data-component="end-cap"][data-end="1"]');

          if (!end1Flange || !end1EndCap) return;

          var end1 = this.selectedComponents.end1;
          if (!end1.flange) return;

      // Create collapsed view for End 1 using the new Tube Configuration format
          var collapsedHTML = `
            <div class="subsea-builder__selected-end1">
              <style>
            .subsea-builder__selected-end1-info .subsea-builder__product-title {
              font-size: 1.1em;
              font-weight: bold;
              margin: 0 0 0.5em 0;
              color: #333;
                }
              </style>
              <div class="subsea-builder__selected-end1-info">
            <h4>${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectedEnd1Configuration) || "SELECTED END 1 CONFIGURATION")}</h4>
                
                            <!-- Flange Product Box -->
            <div class="subsea-builder__product-item" style="margin-top: 1em;">
              <div class="subsea-builder__product-image">
                <a href="${this.createProductUrl(end1.flange)}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                  <img src="${this.getImageWithFallback(end1.flange)}" alt="${end1.flange.metafields?.custom_tube_builder_product_title || end1.flange.product_title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.flangeLabel) || 'Flange')}" class="subsea-builder__image">
                </a>
              </div>
              <div class="subsea-builder__product-info">
                <div class="subsea-builder__product-title">${end1.flange.metafields?.custom_tube_builder_product_title || end1.flange.product_title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.unknownFlange) || "Unknown Flange")}</div>
                ${(() => {
                  var customDescription = end1.flange.metafields?.custom_tube_builder_product_description || '';
                  if (customDescription) {
                    customDescription = String(customDescription);
                    if (this.isBase64(customDescription)) {
                      try {
                        customDescription = atob(customDescription);
                      } catch (e) {
                      }
                    }
                    return `<div class="subsea-builder__product-description">${customDescription}</div>`;
                  }
                  return '';
                })()}
                <div class="subsea-builder__spec-grid">
                  <div class="subsea-builder__spec">${this.formatPrice(end1.flange.price) ? `<strong>${this.formatPrice(end1.flange.price)}</strong>` : 'N/A'}</div>
                  <div class="subsea-builder__spec">${end1.flange.metafields?.custom_bsub_depth_m ? `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(end1.flange.metafields.custom_bsub_depth_m)}m / ${this.formatNumber(Math.round(end1.flange.metafields.custom_bsub_depth_m * 3.28084))}ft` : `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`}</div>
                  <div class="subsea-builder__spec">${end1.flange.sku || 'N/A'}</div>
                </div>
                <div class="subsea-builder__availability">${this.createAvailabilityHtml(end1.flange, this.getProductMetafields(end1.flange).custom_in_stock_lead_time || 'N/A', this.getProductMetafields(end1.flange).custom_out_of_stock_lead_time || 'N/A')}</div>
              </div>
            </div>
                
                  ${end1['end-cap'] ? `
                  <!-- End-Cap Product Box -->
                  <div class="subsea-builder__product-item" style="margin-top: 1em;">
                    <div class="subsea-builder__product-image">
                      <a href="${this.createProductUrl(end1['end-cap'])}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                        <img src="${this.getImageWithFallback(end1['end-cap'])}" alt="${end1['end-cap'].metafields?.custom_tube_builder_product_title || end1['end-cap'].product_title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.endcapLabel) || 'End-Cap')}" class="subsea-builder__image">
                      </a>
                </div>
                    <div class="subsea-builder__product-info">
                      <div class="subsea-builder__product-title">${end1['end-cap'].metafields?.custom_tube_builder_product_title || end1['end-cap'].product_title || 'Unknown End-Cap'}</div>
                      ${(() => {
                        var customDescription = end1['end-cap'].metafields?.custom_tube_builder_product_description || '';
                        if (customDescription) {
                          customDescription = String(customDescription);
                          if (this.isBase64(customDescription)) {
                            try {
                              customDescription = atob(customDescription);
                            } catch (e) {
                            }
                          }
                          return `<div class="subsea-builder__product-description">${customDescription}</div>`;
                        }
                        return '';
                      })()}
                      <div class="subsea-builder__spec-grid">
                        <div class="subsea-builder__spec">${this.formatPrice(end1['end-cap'].price) ? `<strong>${this.formatPrice(end1['end-cap'].price)}</strong>` : 'N/A'}</div>
                        <div class="subsea-builder__spec">${end1['end-cap'].metafields?.custom_bsub_depth_m ? `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(end1['end-cap'].metafields.custom_bsub_depth_m)}m / ${this.formatNumber(Math.round(end1['end-cap'].metafields.custom_bsub_depth_m * 3.28084))}ft` : `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`}</div>
                        <div class="subsea-builder__spec">${end1['end-cap'].sku || 'N/A'}</div>
                      </div>
                      <div class="subsea-builder__availability">${this.createAvailabilityHtml(end1['end-cap'], this.getProductMetafields(end1['end-cap']).custom_in_stock_lead_time || 'N/A', this.getProductMetafields(end1['end-cap']).custom_out_of_stock_lead_time || 'N/A')}</div>
                    </div>
                  </div>
                ` : `
                  <!-- No End-Cap Required -->
                  <div class="subsea-builder__product-item" style="margin-top: 1em; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
                    <div class="subsea-builder__product-info">
                      <div class="subsea-builder__product-title" style="color: #6c757d;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noEndcapRequired) || "No End-Cap Required"}</div>
                      <div class="subsea-builder__product-description" style="color: #6c757d; font-style: italic;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.assemblyIncludesIntegratedEndcap) || "Assembly includes integrated end-cap"}</div>
                    </div>
                  </div>
                `}
                
                <button class="btn btn--secondary change-end1-selection-btn" style="margin-top: 1em;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.changeEnd1Configuration) || "Change End 1 Configuration"}</button>
              </div>
            </div>
          `;

          // Replace the End 1 flange section content with the collapsed view (hiding step title)
          end1Flange.innerHTML = `
            <div class="subsea-builder__component-content">
              ${collapsedHTML}
            </div>
          `;

          // Hide the end-cap section
          end1EndCap.style.display = 'none';

          // Bind the change button
          var changeBtn = end1Flange.querySelector('.change-end1-selection-btn');
          if (changeBtn) {
            changeBtn.addEventListener('click', function(e) {
              e.preventDefault();
              // Hide Interior Fittings and Vent & Vacuum Control immediately when changing End 1
              this.resetAndHideInteriorFittings();
              this.resetAndHideVentVacuumControl();
              this.expandEnd1Configuration();
            }.bind(this));
          }
        },

    // Expand End 1 configuration to show all options again
    expandEnd1Configuration: function() {
      
      // Reset ALL downstream selections (End 2 and beyond)
      this.resetEndSelections(2);
      
      // Hide End 2 sections
      var end2Flange = this.container.querySelector('[data-component="flange"][data-end="2"]');
      var end2EndCap = this.container.querySelector('[data-component="end-cap"][data-end="2"]');
      
      if (end2Flange) end2Flange.style.display = 'none';
      if (end2EndCap) end2EndCap.style.display = 'none';
      
      // Restore the original End 1 flange section structure
      var end1Flange = this.container.querySelector('[data-component="flange"][data-end="1"]');
      var end1EndCap = this.container.querySelector('[data-component="end-cap"][data-end="1"]');
      
      if (end1Flange) {
        end1Flange.innerHTML = `
          <h3 class="subsea-builder__component-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectFlangeEnd1Numbered) || "2. Select Your Flange (End 1)"}</h3>
          <div class="subsea-builder__component-content">
            <p class="subsea-builder__component-note">Select a tube first to see compatible flanges</p>
          </div>
        `;
      }
      
      if (end1EndCap) {
        end1EndCap.style.display = 'block';
      }
      
      // Reset End 1 selections
      this.resetEndSelections(1);
      
      // Re-populate with compatible flanges
      this.showCompatibleFlanges(1);
      
      // Update summary and button state
      this.updateSummary();
    },

    // Collapse End 2 configuration to show only selected components
            collapseEnd2Configuration: function() {
          var end2Flange = this.container.querySelector('[data-component="flange"][data-end="2"]');
          var end2EndCap = this.container.querySelector('[data-component="end-cap"][data-end="2"]');

          if (!end2Flange || !end2EndCap) return;

          var end2 = this.selectedComponents.end2;
          if (!end2.flange) return;

          // Create collapsed view for End 2 using the new Tube Configuration format
          var collapsedHTML = `
            <div class="subsea-builder__selected-end2">
              <style>
                .subsea-builder__selected-end2-info .subsea-builder__product-title {
                  font-size: 1.1em;
                  font-weight: bold;
                  margin: 0 0 0.5em 0;
                  color: #333;
                }
              </style>
              <div class="subsea-builder__selected-end2-info">
                <h4>${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectedEnd2Configuration) || "SELECTED END 2 CONFIGURATION")}</h4>
                
                            <!-- Flange Product Box -->
            <div class="subsea-builder__product-item" style="margin-top: 1em;">
              <div class="subsea-builder__product-image">
                <a href="${this.createProductUrl(end2.flange)}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                  <img src="${this.getImageWithFallback(end2.flange)}" alt="${end2.flange.metafields?.custom_tube_builder_product_title || end2.flange.product_title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.flangeLabel) || 'Flange')}" class="subsea-builder__image">
                </a>
              </div>
              <div class="subsea-builder__product-info">
                <div class="subsea-builder__product-title">${end2.flange.metafields?.custom_tube_builder_product_title || end2.flange.product_title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.unknownFlange) || "Unknown Flange")}</div>
                ${(() => {
                  var customDescription = end2.flange.metafields?.custom_tube_builder_product_description || '';
                  if (customDescription) {
                    customDescription = String(customDescription);
                    if (this.isBase64(customDescription)) {
                      try {
                        customDescription = atob(customDescription);
                      } catch (e) {
                      }
                    }
                    return `<div class="subsea-builder__product-description">${customDescription}</div>`;
                  }
                  return '';
                })()}
                <div class="subsea-builder__spec-grid">
                  <div class="subsea-builder__spec">${this.formatPrice(end2.flange.price) ? `<strong>${this.formatPrice(end2.flange.price)}</strong>` : 'N/A'}</div>
                  <div class="subsea-builder__spec">${end2.flange.metafields?.custom_bsub_depth_m ? `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(end2.flange.metafields.custom_bsub_depth_m)}m / ${this.formatNumber(Math.round(end2.flange.metafields.custom_bsub_depth_m * 3.28084))}ft` : `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`}</div>
                  <div class="subsea-builder__spec">${end2.flange.sku || 'N/A'}</div>
                </div>
                <div class="subsea-builder__availability">${this.createAvailabilityHtml(end2.flange, this.getProductMetafields(end2.flange).custom_in_stock_lead_time || 'N/A', this.getProductMetafields(end2.flange).custom_out_of_stock_lead_time || 'N/A')}</div>
              </div>
            </div>
                
                  ${end2['end-cap'] ? `
                  <!-- End-Cap Product Box -->
                  <div class="subsea-builder__product-item" style="margin-top: 1em;">
                    <div class="subsea-builder__product-image">
                      <a href="${this.createProductUrl(end2['end-cap'])}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                        <img src="${this.getImageWithFallback(end2['end-cap'])}" alt="${end2['end-cap'].metafields?.custom_tube_builder_product_title || end2['end-cap'].product_title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.endcapLabel) || 'End-Cap')}" class="subsea-builder__image">
                      </a>
                </div>
                    <div class="subsea-builder__product-info">
                      <div class="subsea-builder__product-title">${end2['end-cap'].metafields?.custom_tube_builder_product_title || end2['end-cap'].product_title || 'Unknown End-Cap'}</div>
                      ${(() => {
                        var customDescription = end2['end-cap'].metafields?.custom_tube_builder_product_description || '';
                        if (customDescription) {
                          customDescription = String(customDescription);
                          if (this.isBase64(customDescription)) {
                            try {
                              customDescription = atob(customDescription);
                            } catch (e) {
                            }
                          }
                          return `<div class="subsea-builder__product-description">${customDescription}</div>`;
                        }
                        return '';
                      })()}
                      <div class="subsea-builder__spec-grid">
                        <div class="subsea-builder__spec">${this.formatPrice(end2['end-cap'].price) ? `<strong>${this.formatPrice(end2['end-cap'].price)}</strong>` : 'N/A'}</div>
                        <div class="subsea-builder__spec">${end2['end-cap'].metafields?.custom_bsub_depth_m ? `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(end2['end-cap'].metafields.custom_bsub_depth_m)}m / ${this.formatNumber(Math.round(end2['end-cap'].metafields.custom_bsub_depth_m * 3.28084))}ft` : `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`}</div>
                        <div class="subsea-builder__spec">${end2['end-cap'].sku || 'N/A'}</div>
                      </div>
                      <div class="subsea-builder__availability">${this.createAvailabilityHtml(end2['end-cap'], this.getProductMetafields(end2['end-cap']).custom_in_stock_lead_time || 'N/A', this.getProductMetafields(end2['end-cap']).custom_out_of_stock_lead_time || 'N/A')}</div>
                    </div>
                  </div>
                ` : `
                  <!-- No End-Cap Required -->
                  <div class="subsea-builder__product-item" style="margin-top: 1em; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
                    <div class="subsea-builder__product-info">
                      <div class="subsea-builder__product-title" style="color: #6c757d;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noEndcapRequired) || "No End-Cap Required"}</div>
                      <div class="subsea-builder__product-description" style="color: #6c757d; font-style: italic;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.assemblyIncludesIntegratedEndcap) || "Assembly includes integrated end-cap"}</div>
                    </div>
                  </div>
                `}
                
                <button class="btn btn--secondary change-end2-selection-btn" style="margin-top: 1em;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.changeEnd2Configuration) || "Change End 2 Configuration"}</button>
              </div>
            </div>
          `;

          // Replace the End 2 flange section content with the collapsed view (hiding step title)
          end2Flange.innerHTML = `
            <div class="subsea-builder__component-content">
              ${collapsedHTML}
            </div>
          `;

          // Hide the end-cap section
          end2EndCap.style.display = 'none';

          // Bind the change button
          var changeBtn = end2Flange.querySelector('.change-end2-selection-btn');
          if (changeBtn) {
            changeBtn.addEventListener('click', function(e) {
              e.preventDefault();
              // Hide Interior Fittings and Vent & Vacuum Control immediately when changing End 2
              this.resetAndHideInteriorFittings();
              this.resetAndHideVentVacuumControl();
              this.expandEnd2Configuration();
            }.bind(this));
          }
        },

    // Expand End 2 configuration to show all options again
    expandEnd2Configuration: function() {
      
      // Restore the original End 2 flange section structure
      var end2Flange = this.container.querySelector('[data-component="flange"][data-end="2"]');
      var end2EndCap = this.container.querySelector('[data-component="end-cap"][data-end="2"]');
      
      if (end2Flange) {
        end2Flange.innerHTML = `
          <h3 class="subsea-builder__component-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectFlangeEnd2Numbered) || "4. Select Your Flange (End 2)"}</h3>
          <div class="subsea-builder__component-content">
            <p class="subsea-builder__component-note">Complete End 1 configuration first</p>
          </div>
        `;
      }
      
      if (end2EndCap) {
        end2EndCap.style.display = 'block';
      }
      
      // Reset End 2 selections
      this.resetEndSelections(2);
      
      // Re-populate with compatible flanges
      this.showCompatibleFlanges(2);
      
      // Update summary and button state
      this.updateSummary();
    },

    // Helper function to reset end-cap selection for a specific end
    resetEndCapSelection: function(endNumber) {
      
      var end = this.selectedComponents['end' + endNumber];
      if (!end) return;
      
      // Clear end-cap selection
      end['end-cap'] = null;
      
      // Remove visual selection for end-cap
      var endEndCap = this.container.querySelector('[data-component="end-cap"][data-end="' + endNumber + '"]');
      if (endEndCap) {
        var selectedEndCap = endEndCap.querySelector('.' + classes.selected);
        if (selectedEndCap) {
          selectedEndCap.classList.remove(classes.selected);
          var button = selectedEndCap.querySelector('.select-component-btn');
          if (button) button.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisEndcap) || "Select This End-Cap";
        }
      }
      
      // Update summary
      this.updateSummary();
    },

    // Helper function to reset downstream components from a specific component
    resetDownstreamFromComponent: function(componentType, endNumber) {
      
      if (componentType === 'tube') {
        // Reset everything downstream
        this.resetAllDownstreamSelections();
      } else if (componentType === 'flange') {
        // Reset end-cap for this end
        this.resetEndCapSelection(endNumber);
        
        // If this is End 1, also reset and hide End 2, Interior Fittings, and Vent & Vacuum Control
        if (endNumber === 1) {
          this.resetAndHideEnd2();
          this.resetAndHideInteriorFittings();
          this.resetAndHideVentVacuumControl();
        }
        // If this is End 2, always reset Interior Fittings and Vent & Vacuum Control
        if (endNumber === 2) {
          this.resetAndHideInteriorFittings();
          this.resetAndHideVentVacuumControl();
        }
      } else if (componentType === 'end-cap') {
        // If this is End 1, also reset and hide End 2, Interior Fittings, and Vent & Vacuum Control
        if (endNumber === 1) {
          this.resetAndHideEnd2();
          this.resetAndHideInteriorFittings();
          this.resetAndHideVentVacuumControl();
        }
        // If this is End 2, always reset Interior Fittings and Vent & Vacuum Control
        if (endNumber === 2) {
          this.resetAndHideInteriorFittings();
          this.resetAndHideVentVacuumControl();
        }
      }
    },
    // Helper function to reset and hide Interior Fittings completely
    resetAndHideInteriorFittings: function() {
      
      // Clear selections
      this.selectedComponents.interiorFittings = { electronicTray: undefined, pcbShelf: undefined, pcbShelfSecondary: undefined };
      
      // Clear visual state - remove any collapsed views and reset button states
      // Find the Interior Fittings section by looking for the section that contains both electronic-tray and pcb-shelf components
      var electronicTrayComponent = this.container.querySelector('[data-component="electronic-tray"]');
      var pcbShelfComponent = this.container.querySelector('[data-component="pcb-shelf"]');
      
      if (electronicTrayComponent && pcbShelfComponent) {
        // Find the parent section that contains both components
        var interiorFittingsSection = electronicTrayComponent.closest('.subsea-builder__end-section');
        
        if (interiorFittingsSection) {
          // Remove any collapsed view HTML - look for the Interior Fittings collapsed view specifically
          var collapsedView = interiorFittingsSection.querySelector('.subsea-builder__selected-end1');
          if (collapsedView) {
            collapsedView.remove();
          }
          
          // Show the original title and structure
          var title = interiorFittingsSection.querySelector('h3');
          if (title) {
            title.style.display = 'block';
          }
        }
      }
      
      // Clear button states in all sections
      var electronicTray = this.container.querySelector('[data-component="electronic-tray"]');
      var pcbShelf = this.container.querySelector('[data-component="pcb-shelf"]');
      var pcbShelfSecondary = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
      
      if (electronicTray) {
        // Clear any selected button states
        var selectedButtons = electronicTray.querySelectorAll('.is-selected');
        selectedButtons.forEach(function(btn) {
          btn.classList.remove('is-selected');
        });
        
        // Reset button text
        var buttons = electronicTray.querySelectorAll('.select-component-btn');
        buttons.forEach(function(btn) {
          btn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option";
        });
        
        electronicTray.style.display = 'none';
      }
      
      if (pcbShelf) {
        // Clear any selected button states
        var selectedButtons = pcbShelf.querySelectorAll('.is-selected');
        selectedButtons.forEach(function(btn) {
          btn.classList.remove('is-selected');
        });
        
        // Reset button text
        var buttons = pcbShelf.querySelectorAll('.select-component-btn');
        buttons.forEach(function(btn) {
          btn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option";
        });
        
        pcbShelf.style.display = 'none';
      }
      
      if (pcbShelfSecondary) {
        // Clear any selected button states
        var selectedButtons = pcbShelfSecondary.querySelectorAll('.is-selected');
        selectedButtons.forEach(function(btn) {
          btn.classList.remove('is-selected');
        });
        
        // Reset button text
        var buttons = pcbShelfSecondary.querySelectorAll('.select-component-btn');
        buttons.forEach(function(btn) {
          btn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option";
        });
        
        pcbShelfSecondary.style.display = 'none';
      }
      
      // Update summary
      this.updateSummary();
    },

    // Helper function to reset Interior Fittings
    resetInteriorFittings: function() {
      
      // Clear selections
      this.selectedComponents.interiorFittings = { electronicTray: undefined, pcbShelf: undefined, pcbShelfSecondary: undefined };
      
      // Hide sections
      var electronicTray = this.container.querySelector('[data-component="electronic-tray"]');
      var pcbShelf = this.container.querySelector('[data-component="pcb-shelf"]');
      var pcbShelfSecondary = this.container.querySelector('[data-component="pcb-shelf-secondary"]');
      
      if (electronicTray) electronicTray.style.display = 'none';
      if (pcbShelf) pcbShelf.style.display = 'none';
      if (pcbShelfSecondary) pcbShelfSecondary.style.display = 'none';
      
      // Update summary
      this.updateSummary();
    },

    // ===== VENT & VACUUM CONTROL FUNCTIONS =====

    // Create Vent & Vacuum Control element (matching Interior Fittings format)
    createVentVacuumControlElement: function(variant, componentType, productImage) {
      
      // Handle Default Title variant case - check if we need to access product-level metafields
      var mf = (variant && variant.metafields) ? variant.metafields : {};
      
      // For Default Title variants, try to get metafields from product level
      if (variant.title === ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.defaultTitle) || "Default Title") && (!mf.custom_tube_builder_product_title || !mf.custom_tube_builder_product_description)) {
        // This will be handled in the Liquid template - we'll access product-level metafields there
      }
      
      // Get product URL
      var productUrl = this.createProductUrl(variant);
      
      // Get image with fallback
      var imageSrc = this.getImageWithFallback(variant, productImage);
      
      // Get custom title with fallback order (same as Interior Fittings)
      var customTitle = mf.custom_tube_builder_product_title || variant.product_title || variant.title || 'N/A';
      
      // Get custom description with base64 decoding (same as Interior Fittings)
      var customDescription = mf.custom_tube_builder_product_description || '';
      if (customDescription) {
        customDescription = String(customDescription);
        if (this.isBase64(customDescription)) {
          try {
            customDescription = atob(customDescription);
          } catch (e) {
          }
        }
      }
      
      // Get other required data (same as Interior Fittings)
      var depthRatingM = (mf.custom_bsub_depth_m != null && mf.custom_bsub_depth_m !== '') ? mf.custom_bsub_depth_m : 'N/A';
      var depthRating = depthRatingM !== 'N/A' ? 
        `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(depthRatingM)}m / ${this.formatNumber(Math.round(depthRatingM * 3.28084))}ft` : 
        `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`;
      var sku = (variant.sku && variant.sku !== '') ? variant.sku : 'N/A';
      var priceDisplay = this.formatPrice(variant.price) ? `<strong>${this.formatPrice(variant.price)}</strong>` : 'N/A';
      
      // Create availability HTML based on product page logic (same as Interior Fittings)
      var availabilityHtml = this.createAvailabilityHtml(variant, mf.custom_in_stock_lead_time || 'N/A', mf.custom_out_of_stock_lead_time || 'N/A');
      
      return `
        <div class="subsea-builder__product-item" data-variant-id="${variant.id}" data-product-type="${componentType}" data-component-type="${componentType}">
          <div class="subsea-builder__product-image">
            <a href="${productUrl}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
              <img src="${imageSrc}" alt="${customTitle}" class="subsea-builder__image">
            </a>
          </div>
          <div class="subsea-builder__product-info">
            <div class="subsea-builder__product-title">${customTitle}</div>
            ${customDescription ? `<div class="subsea-builder__product-description">${customDescription}</div>` : ''}
            <div class="subsea-builder__spec-grid">
              <div class="subsea-builder__spec">${priceDisplay}</div>
              <div class="subsea-builder__spec">${depthRating}</div>
              <div class="subsea-builder__spec">${sku}</div>
          </div>
            <div class="subsea-builder__availability">${availabilityHtml}</div>
          <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}</button>
          </div>
        </div>
      `;
    },

    // Show Vent & Vacuum Control sections when Interior Fittings is complete
    showVentVacuumControl: function() {
      
      var ventSection = this.container.querySelector('[data-component="vent"]');
      var vacuumPlugSection = this.container.querySelector('[data-component="vacuum-plug"]');
      
            if (ventSection) {
        // Always recreate the header with blue dot (regardless of whether it exists)
        ventSection.innerHTML = `
          <h3 class="subsea-builder__component-title" style="position: relative;">
            <span style="display: inline-block; width: 8px; height: 8px; background: #007cba; border-radius: 50%; margin-right: 10px; flex-shrink: 0;"></span>
            ${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.ventSelectionNumbered) || "8. Vent Selection (Highly Recommended)"}
          </h3>
          <style>
            .subsea-builder__component[data-component="vent"] .subsea-builder__component-title::before {
              display: none !important;
            }
          </style>
          <div class="subsea-builder__component-content">
            <div class="subsea-builder__component-note" style="text-align: left;">
              <h4 style="margin: 0 0 0.5em 0; font-weight: bold;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.whyIncludeVentVacuumPlug) || "Why Include a Vent & Vacuum Plug (Optional but Recommended)"}</h4>
              <p style="margin: 0 0 1em 0;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.ventVacuumPlugDescription) || "While not strictly required, adding a vent (M8 or M10) to your subsea enclosure is highly recommended. A vent allows you to:"}</p>
              <ul style="margin: 0 0 1em 0; padding-left: 1.5em; text-align: left;">
                <li>${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.performVacuumLeakTesting) || "Perform vacuum leak testing before deployment to confirm watertight integrity."}</li>
                <li>${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.safelyEqualizePressure) || "Safely equalize pressure for easier disassembly after use."}</li>
                <li>${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.reduceCondensationRisk) || "Reduce the risk of condensation or slow leaks by maintaining slight negative pressure (M10 option)."}</li>
              </ul>
              <p style="margin: 0;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.toIntegrateVentOption) || "To integrate either option, your enclosure must include a dedicated mounting hole—M8 or M10 depending on which vent system you select."}</p>
            </div>
            <div class="subsea-builder__product-grid"></div>
          </div>
        `;
        ventSection.style.display = 'block';
      }
      
      if (vacuumPlugSection) {
        // Restore original structure if it was collapsed
        if (!vacuumPlugSection.querySelector('.subsea-builder__component-title')) {
          vacuumPlugSection.innerHTML = `
            <h3 class="subsea-builder__component-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.vacuumPlugSelection) || "9. Vacuum Plug Selection"}</h3>
            <div class="subsea-builder__component-content">
              <div class="subsea-builder__product-grid"></div>
            </div>
          `;
        }
        vacuumPlugSection.style.display = 'none'; // Hide initially
      }
      
      // Load vent & vacuum control products
      this.loadVentVacuumControlProducts();
    },

    // Load Vent & Vacuum Control products
    loadVentVacuumControlProducts: function() {
      
      // Load Vent Selection
      this.loadVentSelection();
      
      // Hide Vacuum Plug initially - it will only show after M8 Vent is selected
      var vacuumPlugSection = this.container.querySelector('[data-component="vacuum-plug"]');
      if (vacuumPlugSection) {
        vacuumPlugSection.style.display = 'none';
      }
    },

    // Load Vent Selection products
    loadVentSelection: function() {
      var ventSection = this.container.querySelector('[data-component="vent"]');
      if (!ventSection) {
        return;
      }

      var productGrid = ventSection.querySelector('.subsea-builder__product-grid');
      if (!productGrid) {
        return;
      }

      // Clear existing content
      productGrid.innerHTML = '';

      // Add "No Vent" option
      this.loadNoVentOption(productGrid);

      // Add M10 Self-Sealing Vent & Vacuum Plug (no variants)
      this.loadM10SelfSealingVent(productGrid);

      // Add M8 Vent & Vacuum Plug (only the Vent variant)
      this.loadM8Vent(productGrid);

      // Bind events
      this.bindVentVacuumControlEvents();
    },

    // Load no vent option
    loadNoVentOption: function(container) {
      var noVentHtml = `
        <div class="subsea-builder__product-item" data-variant-id="none" data-product-type="vent" data-component-type="vent">
          <div class="subsea-builder__product-image">
            <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik01MCA5MEgxNTBWMTEwSDUwVjkwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K" alt="${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noVent) || "No Vent")}" class="subsea-builder__image">
          </div>
          <div class="subsea-builder__product-info">
            <div class="subsea-builder__product-title">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noVent) || "No Vent")}</div>
            <div class="subsea-builder__spec-grid">
              <div class="subsea-builder__spec"><strong>$0.00</strong></div>
              <div class="subsea-builder__spec">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A</div>
              <div class="subsea-builder__spec">N/A</div>
            </div>
            <div class="subsea-builder__product-description">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.skipThisOptional) || "Skip this optional component"}</div>
            <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}</button>
          </div>
        </div>
      `;
      
      container.insertAdjacentHTML('beforeend', noVentHtml);
    },

    // Load M10 Self-Sealing Vent & Vacuum Plug
    loadM10SelfSealingVent: function(container) {
      // Debug: Check what's in productsData
      // Get product data
      var productData = this.productsData.vent_vacuum_control?.m10_self_sealing_vent;
      
      if (!productData || !productData.debug_product_exists) {
        // Create fallback data for testing
        productData = {
          title: ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.subseaM10SelfSealingVentVacuumPlug) || "Subsea M10 Self-Sealing Vent & Vacuum Plug"),
          featured_image: 'https://blu-sub.com/cdn/shop/files/subsea-m10-self-sealing-vacuum-vent-plug.jpg',
          variants: [
            {
              id: 'm10-self-sealing',
              title: ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.subseaM10SelfSealingVentVacuumPlug) || "Subsea M10 Self-Sealing Vent & Vacuum Plug"),
              price: 25000, // $250.00
              product_handle: 'subsea-m10-self-sealing-vacuum-vent-plug',
              metafields: {
                custom_bsub_depth_m: 1000,
                custom_in_stock_lead_time: 'Quick ship available',
                custom_out_of_stock_lead_time: 'Contact for lead time'
              }
            }
          ]
        };
      }
      
      // Use the actual variant data from the product
      var variant = productData.variants?.[0] || {
        id: 'm10-self-sealing',
        title: productData.title,
        price: 25000,
        product_handle: 'subsea-m10-self-sealing-vacuum-vent-plug',
        metafields: {
          custom_bsub_depth_m: 1000,
          custom_in_stock_lead_time: 'Quick ship available',
          custom_out_of_stock_lead_time: 'Contact for lead time'
        },
        isVentVacuumControl: true
      };
      
      // Ensure product_handle is set for URL generation
      if (!variant.product_handle) {
        variant.product_handle = 'subsea-m10-self-sealing-vacuum-vent-plug';
      }
      
      // Fix "Default Title" issue by using product title when variant title is "Default Title"
      if (variant.title === ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.defaultTitle) || "Default Title") || variant.title === ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.defaultLabel) || "Default")) {
        variant.title = productData.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.subseaM10SelfSealingVentVacuumPlug) || "Subsea M10 Self-Sealing Vent & Vacuum Plug");
      }
      
      // Provide fallback values for null metafields
      if (!variant.metafields) {
        variant.metafields = {};
      }
      if (!variant.metafields.custom_bsub_depth_m) {
        variant.metafields.custom_bsub_depth_m = 1000;
      }
      if (!variant.metafields.custom_in_stock_lead_time) {
        variant.metafields.custom_in_stock_lead_time = 'Quick ship available';
      }
      if (!variant.metafields.custom_out_of_stock_lead_time) {
        variant.metafields.custom_out_of_stock_lead_time = 'Contact for lead time';
      }
      
      var m10VentHtml = this.createVentVacuumControlElement(variant, 'vent', productData.featured_image);
      container.insertAdjacentHTML('beforeend', m10VentHtml);
    },

    // Load M8 Vent & Vacuum Plug (only the Vent variant)
    loadM8Vent: function(container) {
      // Debug: Check what's in productsData
      // Get product data
      var productData = this.productsData.vent_vacuum_control?.m8_vent_vacuum_plug;
      
      if (!productData || !productData.debug_product_exists) {
        // Create fallback data for testing
        productData = {
          title: 'Subsea M8 Vent & Vacuum Plug',
          featured_image: 'https://blu-sub.com/cdn/shop/files/subsea-m8-enclosure-vent-vacuum-plug.jpg',
          variants: [
            {
              id: 45726820696222,
              title: ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.ventLabel) || "Vent"),
              price: 15000, // $150.00
              product_handle: 'subsea-m8-enclosure-vent-vacuum-plug',
              metafields: {
                custom_bsub_depth_m: 1000,
                custom_in_stock_lead_time: 'Quick ship available',
                custom_out_of_stock_lead_time: 'Contact for lead time'
              }
            }
          ]
        };
      }
      
      // Find the Vent variant (45726820696222)
      var ventVariant = productData.variants?.find(function(v) { return v.id === 45726820696222; });
      if (!ventVariant) {
        return;
      }
      
      // Ensure product_handle is set for URL generation
      if (!ventVariant.product_handle) {
        ventVariant.product_handle = 'subsea-m8-enclosure-vent-vacuum-plug';
      }
      
      // Provide fallback values for null metafields
      if (!ventVariant.metafields) {
        ventVariant.metafields = {};
      }
      if (!ventVariant.metafields.custom_bsub_depth_m) {
        ventVariant.metafields.custom_bsub_depth_m = 1000;
      }
      if (!ventVariant.metafields.custom_in_stock_lead_time) {
        ventVariant.metafields.custom_in_stock_lead_time = 'Quick ship available';
      }
      if (!ventVariant.metafields.custom_out_of_stock_lead_time) {
        ventVariant.metafields.custom_out_of_stock_lead_time = 'Contact for lead time';
      }
      
      var m8VentHtml = this.createVentVacuumControlElement(ventVariant, 'vent', productData.featured_image);
      container.insertAdjacentHTML('beforeend', m8VentHtml);
    },

    // Load Vacuum Plug Selection (only shows if M8 Vent is selected)
    loadVacuumPlugSelection: function() {
      var vacuumPlugSection = this.container.querySelector('[data-component="vacuum-plug"]');
      if (!vacuumPlugSection) {
        return;
      }

      var productGrid = vacuumPlugSection.querySelector('.subsea-builder__product-grid');
      if (!productGrid) {
        return;
      }

      // Clear existing content
      productGrid.innerHTML = '';

      // Add no vacuum plug option
      this.loadNoVacuumPlugOption(productGrid);

      // Add M8 Vacuum Plug (only the Plug variant - 45726820728990)
      this.loadM8VacuumPlug(productGrid);

      // Bind events
      this.bindVentVacuumControlEvents();
    },

    // Load no vacuum plug option
    loadNoVacuumPlugOption: function(container) {
      var noVacuumPlugHtml = `
        <div class="subsea-builder__product-item" data-variant-id="none" data-product-type="vacuum-plug" data-component-type="vacuum-plug">
          <div class="subsea-builder__product-image">
            <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik01MCA5MEgxNTBWMTEwSDUwVjkwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K" alt="${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noVacuumPlug) || "No Vacuum Plug")}" class="subsea-builder__image">
          </div>
          <div class="subsea-builder__product-info">
            <div class="subsea-builder__product-title">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noVacuumPlug) || "No Vacuum Plug")}</div>
            <div class="subsea-builder__spec-grid">
              <div class="subsea-builder__spec"><strong>$0.00</strong></div>
              <div class="subsea-builder__spec">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A</div>
              <div class="subsea-builder__spec">N/A</div>
            </div>
            <div class="subsea-builder__product-description">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.skipThisOptional) || "Skip this optional component"}</div>
            <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option"}</button>
          </div>
        </div>
      `;
      
      container.insertAdjacentHTML('beforeend', noVacuumPlugHtml);
    },

    // Load M8 Vacuum Plug (only the Plug variant)
    loadM8VacuumPlug: function(container) {
      // Get product data
      var productData = this.productsData.vent_vacuum_control?.m8_vent_vacuum_plug;
      if (!productData || !productData.debug_product_exists) {
        // Create fallback data for testing
        productData = {
          title: 'Subsea M8 Vent & Vacuum Plug',
          featured_image: 'https://blu-sub.com/cdn/shop/files/subsea-m8-enclosure-vent-vacuum-plug.jpg',
          variants: [
            {
              id: 45726820728990,
              title: 'Plug',
              price: 12000, // $120.00
              product_handle: 'subsea-m8-enclosure-vent-vacuum-plug',
              metafields: {
                custom_bsub_depth_m: 1000,
                custom_in_stock_lead_time: 'Quick ship available',
                custom_out_of_stock_lead_time: 'Contact for lead time'
              }
            }
          ]
        };
      }
      
      // Find the Plug variant (45726820728990)
      var plugVariant = productData.variants?.find(function(v) { return v.id === 45726820728990; });
      if (!plugVariant) {
        return;
      }
      
      // Ensure product_handle is set for URL generation
      if (!plugVariant.product_handle) {
        plugVariant.product_handle = 'subsea-m8-enclosure-vent-vacuum-plug';
      }
      
      // Provide fallback values for null metafields
      if (!plugVariant.metafields) {
        plugVariant.metafields = {};
      }
      if (!plugVariant.metafields.custom_bsub_depth_m) {
        plugVariant.metafields.custom_bsub_depth_m = 1000;
      }
      if (!plugVariant.metafields.custom_in_stock_lead_time) {
        plugVariant.metafields.custom_in_stock_lead_time = 'Quick ship available';
      }
      if (!plugVariant.metafields.custom_out_of_stock_lead_time) {
        plugVariant.metafields.custom_out_of_stock_lead_time = 'Contact for lead time';
      }
      
      var m8VacuumPlugHtml = this.createVentVacuumControlElement(plugVariant, 'vacuum-plug', productData.featured_image);
      container.insertAdjacentHTML('beforeend', m8VacuumPlugHtml);
    },

    // Bind Vent & Vacuum Control events
    bindVentVacuumControlEvents: function() {
      var ventButtons = this.container.querySelectorAll('[data-component-type="vent"] .select-component-btn');
      var vacuumPlugButtons = this.container.querySelectorAll('[data-component-type="vacuum-plug"] .select-component-btn');
      
      
      // Bind vent events
      ventButtons.forEach(function(button) {
        button.addEventListener('click', function() {
          var parentDiv = button.closest('.subsea-builder__product-item');
          var variantId = parentDiv.getAttribute('data-variant-id');
          
          // Handle the selection
          this.handleVentVacuumControlSelection('vent', variantId);
        }.bind(this));
      }.bind(this));
      
      // Bind vacuum plug events
      vacuumPlugButtons.forEach(function(button) {
        button.addEventListener('click', function() {
          var parentDiv = button.closest('.subsea-builder__product-item');
          var variantId = parentDiv.getAttribute('data-variant-id');
          
          // Handle the selection
          this.handleVentVacuumControlSelection('vacuum-plug', variantId);
        }.bind(this));
      }.bind(this));
    },

    // Handle Vent & Vacuum Control selection
    handleVentVacuumControlSelection: function(componentType, variantId) {
      
      var variant = null;
      
      // Handle "none" selections
      if (variantId === 'none') {
        variant = null;
      } else if (variantId === 'm10-self-sealing') {
        // Handle M10 Self-Sealing Vent (no actual variant, just a placeholder)
        variant = {
          id: 'm10-self-sealing',
          title: ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.subseaM10SelfSealingVentVacuumPlug) || "Subsea M10 Self-Sealing Vent & Vacuum Plug"),
          price: 25000,
          product_handle: 'subsea-m10-self-sealing-vacuum-vent-plug',
          featured_image: 'https://blu-sub.com/cdn/shop/files/subsea-m10-self-sealing-vacuum-vent-plug.jpg',
          metafields: {
            custom_bsub_depth_m: 1000,
            custom_in_stock_lead_time: 'Quick ship available',
            custom_out_of_stock_lead_time: 'Contact for lead time'
          },
          isVentVacuumControl: true
        };
      } else {
        // Handle actual variant IDs
        
        // Find the actual variant from the product data
        var actualVariant = null;
        
        // Check M8 Vent & Vacuum Plug product
        var m8Product = this.productsData.vent_vacuum_control?.m8_vent_vacuum_plug;
        if (m8Product && m8Product.variants) {
          actualVariant = m8Product.variants.find(function(v) { return v.id == variantId; });
        }
        
        // Check M10 Self-Sealing Vent product
        if (!actualVariant) {
          var m10Product = this.productsData.vent_vacuum_control?.m10_self_sealing_vent;
          if (m10Product && m10Product.variants) {
            actualVariant = m10Product.variants.find(function(v) { return v.id == variantId; });
          }
        }
        
        if (actualVariant) {
          variant = actualVariant;
        } else {
          // Fallback to placeholder if variant not found
          variant = {
            id: variantId,
            title: variantId === '45726820696222' ? ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.subseaM8Vent) || "Subsea M8 Vent") : ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.subseaM8VacuumPlug) || "Subsea M8 Vacuum Plug"),
            price: 0,
            metafields: {
              custom_bsub_depth_m: 1000,
              custom_in_stock_lead_time: 'Quick ship available',
              custom_out_of_stock_lead_time: 'Contact for lead time'
            },
            isVentVacuumControl: true
          };
        }
      }
      
      // Store selection
      if (componentType === 'vent') {
        this.selectedComponents.ventVacuumControl.vent = variant;
        
        // Handle vent selection logic
        if (variant && variant.id === 45726820696222) {
          // M8 Vent selected - show Vacuum Plug section
          this.selectedComponents.ventVacuumControl.vacuumPlug = undefined; // Reset vacuum plug selection
          var vacuumPlugSection = this.container.querySelector('[data-component="vacuum-plug"]');
          if (vacuumPlugSection) {
            vacuumPlugSection.style.display = 'block';
          }
          this.loadVacuumPlugSelection();
        } else {
          // No Vent or M10 selected - hide Vacuum Plug section and set to "no plug"
          var vacuumPlugSection = this.container.querySelector('[data-component="vacuum-plug"]');
          if (vacuumPlugSection) {
            vacuumPlugSection.style.display = 'none';
          }
          // Auto-select no vacuum plug option
          this.selectedComponents.ventVacuumControl.vacuumPlug = null;
        }
      } else if (componentType === 'vacuum-plug') {
        this.selectedComponents.ventVacuumControl.vacuumPlug = variant;
      }
      
      // Update visual feedback
      this.updateVentVacuumControlSelection(componentType, variant);
      
      // Check if both Vent & Vacuum Control are complete and collapse section
      this.checkAndCollapseVentVacuumControl();
      
      // Update summary and calculations
      this.updateSummary();
      this.calculateTotalPrice();
    },
    // Update Vent & Vacuum Control visual selection
    updateVentVacuumControlSelection: function(componentType, variant) {
      var section = this.container.querySelector('[data-component="' + componentType + '"]');
      if (!section) return;
      
      // Remove previous selections
      var previousSelected = section.querySelectorAll('.is-selected');
      previousSelected.forEach(function(item) {
        item.classList.remove('is-selected');
        var btn = item.querySelector('.select-component-btn');
        if (btn) btn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option";
      });
      
      // Add selection to current item
      if (variant) {
        var variantElement = section.querySelector('[data-variant-id="' + variant.id + '"]');
        if (variantElement) {
          variantElement.classList.add('is-selected');
          var button = variantElement.querySelector('.select-component-btn');
          if (button) button.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selected) || "Selected";
        }
      } else {
        // Handle "none" selection
        var noneElement = section.querySelector('[data-variant-id="none"]');
        if (noneElement) {
          noneElement.classList.add('is-selected');
          var button = noneElement.querySelector('.select-component-btn');
          if (button) button.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selected) || "Selected";
        }
      }
    },

    // Check if Vent & Vacuum Control are complete and collapse section
    checkAndCollapseVentVacuumControl: function() {
      var vent = this.selectedComponents.ventVacuumControl.vent;
      var vacuumPlug = this.selectedComponents.ventVacuumControl.vacuumPlug;
      
      // Check if both selections are made (including "none" selections)
      var ventSelected = vent !== undefined;
      var vacuumPlugSelected = vacuumPlug !== undefined;
      
      // Special case: If Vent is "none" or M10, then Vacuum Plug is auto-selected as "none"
      if (vent === null || (vent && vent.id === 'm10-self-sealing')) {
        vacuumPlugSelected = true; // We auto-selected "none" for Vacuum Plug
      }
      
      
      // Collapse if both selections are made (including "none" selections)
      // Special case: If Vent is "none", we auto-select "none" for Vacuum Plug and should collapse
      if (ventSelected && vacuumPlugSelected) {
        this.collapseVentVacuumControlSection();
      } else {
      }
    },

    // Collapse Vent & Vacuum Control section
    collapseVentVacuumControlSection: function() {
      
      // Prevent multiple collapse calls
      if (this.isCollapsingVentVacuumControl) {
        return;
      }
      this.isCollapsingVentVacuumControl = true;
      
      // Find the Vent & Vacuum Control section
      var ventComponent = this.container.querySelector('[data-component="vent"]');
      var vacuumPlugComponent = this.container.querySelector('[data-component="vacuum-plug"]');
      
      
      if (!ventComponent || !vacuumPlugComponent) {
        this.isCollapsingVentVacuumControl = false;
        return;
      }
      
      // Find the parent section that contains both components
      var ventVacuumControlSection = ventComponent.closest('.subsea-builder__end-section');
      
      // Check if already collapsed to prevent duplication
      if (ventVacuumControlSection && ventVacuumControlSection.querySelector('.subsea-builder__selected-vent-vacuum')) {
        console.log('Already collapsed - preventing duplication');
        this.isCollapsingVentVacuumControl = false;
        return;
      }
      
      if (!ventVacuumControlSection) {
        this.isCollapsingVentVacuumControl = false;
        return;
      }
      
      // Get selected components
      var vent = this.selectedComponents.ventVacuumControl.vent;
      var vacuumPlug = this.selectedComponents.ventVacuumControl.vacuumPlug;
      
      // Create collapsed view HTML (following consistent pattern)
      var collapsedHtml = `
        <div class="subsea-builder__selected-vent-vacuum">
          <style>
            .subsea-builder__selected-vent-vacuum-info .subsea-builder__product-title {
              font-size: 1.1em;
              font-weight: bold;
              margin: 0 0 0.5em 0;
              color: #333;
            }
          </style>
          <div class="subsea-builder__selected-vent-vacuum-info">
            <h4>${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectedVentVacuumControlConfiguration) || "SELECTED VENT & VACUUM CONTROL CONFIGURATION")}</h4>
          
          ${vent ? `
            <!-- Vent Product Box -->
            <div class="subsea-builder__product-item" style="margin-top: 1em;">
              <div class="subsea-builder__product-image">
                <a href="${this.createProductUrl(vent)}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                  <img src="${this.getImageWithFallback(vent)}" alt="${vent.metafields?.custom_tube_builder_product_title || vent.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.ventLabel) || 'Vent')}" class="subsea-builder__image">
                </a>
              </div>
              <div class="subsea-builder__product-info">
                <div class="subsea-builder__product-title">${vent.metafields?.custom_tube_builder_product_title || vent.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.unknownVent) || "Unknown Vent")}</div>
                ${(() => {
                  var customDescription = vent.metafields?.custom_tube_builder_product_description || '';
                  if (customDescription) {
                    customDescription = String(customDescription);
                    if (this.isBase64(customDescription)) {
                      try {
                        customDescription = atob(customDescription);
                      } catch (e) {
                      }
                    }
                    return `<div class="subsea-builder__product-description">${customDescription}</div>`;
                  }
                  return '';
                })()}
                <div class="subsea-builder__spec-grid">
                  <div class="subsea-builder__spec">${this.formatPrice(vent.price) ? `<strong>${this.formatPrice(vent.price)}</strong>` : 'N/A'}</div>
                  <div class="subsea-builder__spec">${vent.metafields?.custom_bsub_depth_m ? `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(vent.metafields.custom_bsub_depth_m)}m / ${this.formatNumber(Math.round(vent.metafields.custom_bsub_depth_m * 3.28084))}ft` : `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`}</div>
                  <div class="subsea-builder__spec">${vent.sku || 'N/A'}</div>
                </div>
                <div class="subsea-builder__availability">${this.createAvailabilityHtml(vent, this.getProductMetafields(vent).custom_in_stock_lead_time || 'N/A', this.getProductMetafields(vent).custom_out_of_stock_lead_time || 'N/A')}</div>
              </div>
            </div>
          ` : `
            <!-- No Vent Required -->
            <div class="subsea-builder__product-item" style="margin-top: 1em; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
              <div class="subsea-builder__product-info">
                <div class="subsea-builder__product-title" style="color: #6c757d;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noVent) || "No Vent")}</div>
                <div class="subsea-builder__product-description" style="color: #6c757d; font-style: italic;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.optionalComponentNotSelected) || "Optional component - not selected")}</div>
              </div>
            </div>
          `}
          
          ${(() => {
            // Check if M10 Self-Sealing Vent is selected (which includes vacuum plug)
            var isM10Vent = vent && (
              vent.title === ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.subseaM10SelfSealingVentVacuumPlug) || "Subsea M10 Self-Sealing Vent & Vacuum Plug") || 
              vent.id === 'm10-self-sealing' ||
              (vent.metafields && vent.metafields.custom_tube_builder_product_title && 
               vent.metafields.custom_tube_builder_product_title.includes('M10 Self-Sealing'))
            );
            
            // Check if M8 Vent is selected (which allows vacuum plug selection)
            var isM8Vent = vent && (
              vent.id === 45726820696222 ||
              (vent.metafields && vent.metafields.custom_tube_builder_product_title && 
               vent.metafields.custom_tube_builder_product_title.includes('M8'))
            );
            
            if (isM10Vent) {
              // M10 Self-Sealing Vent includes vacuum plug
              return `
                <!-- Vacuum Plug Included in M10 -->
                <div class="subsea-builder__product-item" style="margin-top: 1em; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
                  <div class="subsea-builder__product-info">
                    <div class="subsea-builder__product-title" style="color: #28a745; font-weight: bold;">Vacuum Plug Included in Subsea M10 Self-Sealing Vent & Vacuum Plug</div>
                    <div class="subsea-builder__product-description" style="color: #6c757d; font-style: italic;">No separate vacuum plug required</div>
                  </div>
                </div>
              `;
            } else if (vacuumPlug) {
              // Regular vacuum plug selected
              return `
                <!-- Vacuum Plug Product Box -->
                <div class="subsea-builder__product-item" style="margin-top: 1em;">
                  <div class="subsea-builder__product-image">
                    <a href="${this.createProductUrl(vacuumPlug)}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                      <img src="${this.getImageWithFallback(vacuumPlug)}" alt="${vacuumPlug.metafields?.custom_tube_builder_product_title || vacuumPlug.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.vacuumPlugLabel) || "Vacuum Plug")}" class="subsea-builder__image">
                    </a>
                  </div>
                  <div class="subsea-builder__product-info">
                    <div class="subsea-builder__product-title">${vacuumPlug.metafields?.custom_tube_builder_product_title || vacuumPlug.title || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.vacuumPlugLabel) || "Vacuum Plug")}</div>
                    ${(() => {
                      var customDescription = vacuumPlug.metafields?.custom_tube_builder_product_description || '';
                      if (customDescription) {
                        customDescription = String(customDescription);
                        if (this.isBase64(customDescription)) {
                          try {
                            customDescription = atob(customDescription);
                          } catch (e) {
                          }
                        }
                        return `<div class="subsea-builder__product-description">${customDescription}</div>`;
                      }
                      return '';
                    })()}
                    <div class="subsea-builder__spec-grid">
                      <div class="subsea-builder__spec">${this.formatPrice(vacuumPlug.price) ? `<strong>${this.formatPrice(vacuumPlug.price)}</strong>` : 'N/A'}</div>
                      <div class="subsea-builder__spec">${vacuumPlug.metafields?.custom_bsub_depth_m ? `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${this.formatNumber(vacuumPlug.metafields.custom_bsub_depth_m)}m / ${this.formatNumber(Math.round(vacuumPlug.metafields.custom_bsub_depth_m * 3.28084))}ft` : `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} N/A`}</div>
                      <div class="subsea-builder__spec">${vacuumPlug.sku || 'N/A'}</div>
                    </div>
                    <div class="subsea-builder__availability">${this.createAvailabilityHtml(vacuumPlug, this.getProductMetafields(vacuumPlug).custom_in_stock_lead_time || 'N/A', this.getProductMetafields(vacuumPlug).custom_out_of_stock_lead_time || 'N/A')}</div>
                  </div>
                </div>
              `;
            } else if (isM8Vent) {
              // M8 Vent selected but no vacuum plug selected
              return `
                <!-- No Vacuum Plug with M8 Vent -->
                <div class="subsea-builder__product-item" style="margin-top: 1em; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
                  <div class="subsea-builder__product-info">
                    <div class="subsea-builder__product-title" style="color: #6c757d;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noVacuumPlug) || "No Vacuum Plug")}</div>
                    <div class="subsea-builder__product-description" style="color: #6c757d; font-style: italic;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.optionalComponentNotSelected) || "Optional component - not selected")}</div>
                  </div>
                </div>
              `;
            } else {
              // No vent selected (so no vacuum plug possible)
              return `
                <!-- No Vacuum Plug Required -->
                <div class="subsea-builder__product-item" style="margin-top: 1em; background: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
                  <div class="subsea-builder__product-info">
                    <div class="subsea-builder__product-title" style="color: #6c757d;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noVacuumPlug) || "No Vacuum Plug")}</div>
                    <div class="subsea-builder__product-description" style="color: #6c757d; font-style: italic;">${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.optionalComponentNotSelected) || "Optional component - not selected")}</div>
                  </div>
                </div>
              `;
            }
          })()}
          
          <button class="btn btn--secondary change-vent-vacuum-control-btn" style="margin-top: 1em;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.changeVentVacuumControl) || "CHANGE VENT & VACUUM CONTROL"}</button>
          </div>
        </div>
      `;
      
      if (ventComponent) {
        // Replace the vent component content with the collapsed view (hiding step title)
        ventComponent.innerHTML = `
          <div class="subsea-builder__component-content">
            ${collapsedHtml}
          </div>
        `;
        
        // Hide the vacuum plug component
        vacuumPlugComponent.style.display = 'none';
        
        // Bind change button event
        var changeButton = ventComponent.querySelector('.change-vent-vacuum-control-btn');
        if (changeButton) {
          changeButton.addEventListener('click', this.expandVentVacuumControlSection.bind(this));
        }
      }
      
      // Reset the flag
      this.isCollapsingVentVacuumControl = false;
      
    },

    // Expand Vent & Vacuum Control section
    expandVentVacuumControlSection: function() {
      
      // Prevent multiple simultaneous expand calls
      if (this.isExpandingVentVacuumControl) {
        console.log('Already expanding - preventing duplicate call');
        return;
      }
      this.isExpandingVentVacuumControl = true;
      
      // Find the Vent & Vacuum Control section by looking for the section that contains both vent and vacuum-plug components
      var ventComponent = this.container.querySelector('[data-component="vent"]');
      var vacuumPlugComponent = this.container.querySelector('[data-component="vacuum-plug"]');
      
      if (!ventComponent || !vacuumPlugComponent) {
      this.isExpandingVentVacuumControl = false;
        return;
      }
      
        // Find the parent section that contains both components
        var ventVacuumControlSection = ventComponent.closest('.subsea-builder__end-section');
      if (!ventVacuumControlSection) {
        console.log('Vent & Vacuum Control section not found');
        this.isExpandingVentVacuumControl = false;
        return;
      }
      
      // Remove collapsed view (check for all possible class names)
      var collapsedViewOld = ventVacuumControlSection.querySelector('.subsea-builder__selected-vent-vacuum');
      if (collapsedViewOld) {
        collapsedViewOld.remove();
      }
      
      var collapsedViewNew = ventVacuumControlSection.querySelector('.subsea-builder__selected-end1');
          if (collapsedViewNew) {
            collapsedViewNew.remove();
          }
          
      var collapsedViewVentVacuum = ventVacuumControlSection.querySelector('.subsea-builder__selected-vent-vacuum');
      if (collapsedViewVentVacuum) {
        console.log('Removing vent vacuum control collapsed view');
        collapsedViewVentVacuum.remove();
      }
      
      // Restore the original HTML structure for vent component
      console.log('Restoring original vent component structure');
      ventComponent.innerHTML = `
        <h3 class="subsea-builder__component-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.ventSelectionNumbered) || "8. Vent Selection (Highly Recommended)"}</h3>
        <div class="subsea-builder__component-content">
          <div class="subsea-builder__component-note" style="text-align: left;">
            <h4 style="margin: 0 0 0.5em 0; font-weight: bold;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.whyIncludeVentVacuumPlug) || "Why Include a Vent & Vacuum Plug (Optional but Recommended)"}</h4>
            <p style="margin: 0 0 1em 0;">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.ventVacuumPlugDescription) || "While not strictly required, adding a vent (M8 or M10) to your subsea enclosure is highly recommended. A vent allows you to:"}</p>
            <ul style="margin: 0 0 1em 0; padding-left: 1.5em; text-align: left;">
              <li>Perform vacuum leak testing before deployment to confirm watertight integrity.</li>
              <li>Safely equalize pressure for easier disassembly after use.</li>
              <li>Reduce the risk of condensation or slow leaks by maintaining slight negative pressure (M10 option).</li>
            </ul>
            <p style="margin: 0;">To integrate either option, your enclosure must include a dedicated mounting hole—M8 or M10 depending on which vent system you select.</p>
          </div>
          <div class="subsea-builder__product-grid"></div>
        </div>
      `;
      ventComponent.style.display = 'block';
      
      // Restore the original HTML structure for vacuum plug component
      console.log('Restoring original vacuum plug component structure');
      vacuumPlugComponent.innerHTML = `
        <h3 class="subsea-builder__component-title">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.vacuumPlugSelection) || "9. Vacuum Plug Selection"}</h3>
        <div class="subsea-builder__component-content">
          <div class="subsea-builder__product-grid"></div>
        </div>
      `;
      vacuumPlugComponent.style.display = 'none'; // Hide initially - will show after M8 Vent selection
      
      // Reset vacuum plug selection when expanding
      this.selectedComponents.ventVacuumControl.vacuumPlug = undefined;
      
      // Reload the Vent & Vacuum Control products
      console.log('Reloading Vent & Vacuum Control products');
      this.loadVentVacuumControlProducts();
      
      // Re-bind events
      this.bindVentVacuumControlEvents();
      
      // Update summary and button state
      this.updateSummary();
      
      // Reset the expanding flag
      this.isExpandingVentVacuumControl = false;
      
    },

    // Helper function to reset and hide Vent & Vacuum Control completely
    resetAndHideVentVacuumControl: function() {
      
      // Clear selections
      this.selectedComponents.ventVacuumControl = { vent: undefined, vacuumPlug: undefined };
      
      // Clear visual state - remove any collapsed views and reset button states
      var ventComponent = this.container.querySelector('[data-component="vent"]');
      var vacuumPlugComponent = this.container.querySelector('[data-component="vacuum-plug"]');
      
      if (ventComponent && vacuumPlugComponent) {
        // Find the parent section that contains both components
        var ventVacuumControlSection = ventComponent.closest('.subsea-builder__end-section');
        
        if (ventVacuumControlSection) {
          // Remove collapsed view if it exists (check for both old and new class names)
          var collapsedView = ventVacuumControlSection.querySelector('.subsea-builder__selected-end1');
          if (collapsedView) {
            collapsedView.remove();
          }
          
          var collapsedViewNew = ventVacuumControlSection.querySelector('.subsea-builder__selected-vent-vacuum');
          if (collapsedViewNew) {
            collapsedViewNew.remove();
          }
          
          // Show the original title
          var title = ventVacuumControlSection.querySelector('h3');
          if (title) {
            title.style.display = 'block';
          }
        }
        
        // Clear selected button states for vent
        var selectedVentButtons = ventComponent.querySelectorAll('.is-selected');
        selectedVentButtons.forEach(function(btn) {
          btn.classList.remove('is-selected');
        });
        
        // Reset vent button text
        var ventButtons = ventComponent.querySelectorAll('.select-component-btn');
        ventButtons.forEach(function(btn) {
          btn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option";
        });
        
        // Clear selected button states for vacuum plug
        var selectedVacuumPlugButtons = vacuumPlugComponent.querySelectorAll('.is-selected');
        selectedVacuumPlugButtons.forEach(function(btn) {
          btn.classList.remove('is-selected');
        });
        
        // Reset vacuum plug button text
        var vacuumPlugButtons = vacuumPlugComponent.querySelectorAll('.select-component-btn');
        vacuumPlugButtons.forEach(function(btn) {
          btn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisOption) || "Select This Option";
        });
      }
      
      // Hide sections
      if (ventComponent) ventComponent.style.display = 'none';
      if (vacuumPlugComponent) vacuumPlugComponent.style.display = 'none';
      
      // Update summary
      this.updateSummary();
    },

    // Helper function to reset Vent & Vacuum Control
    resetVentVacuumControl: function() {
      console.log('Resetting Vent & Vacuum Control');
      
      // Clear selections
      this.selectedComponents.ventVacuumControl = { vent: undefined, vacuumPlug: undefined };
      
      // Hide sections
      var vent = this.container.querySelector('[data-component="vent"]');
      var vacuumPlug = this.container.querySelector('[data-component="vacuum-plug"]');
      
      if (vent) vent.style.display = 'none';
      if (vacuumPlug) vacuumPlug.style.display = 'none';
      
      // Update summary
      this.updateSummary();
    },

    // Helper function to reset and hide End 2 completely
    resetAndHideEnd2: function() {
      
      // Reset End 2 selections
      this.resetEndSelections(2);
      
      // Hide End 2 sections
      var end2Flange = this.container.querySelector('[data-component="flange"][data-end="2"]');
      var end2EndCap = this.container.querySelector('[data-component="end-cap"][data-end="2"]');
      
      if (end2Flange) {
        end2Flange.style.display = 'none';
      } else {
      }
      
      if (end2EndCap) {
        end2EndCap.style.display = 'none';
      } else {
        console.log('End 2 end-cap section not found');
      }
      
      // Update summary
      this.updateSummary();
    },

    showCompatibleFlanges: function(endNumber) {
      var tube = this.selectedComponents.tube;
      if (!tube) return;

      var flangeComponent = this.container.querySelector('[data-component="flange"][data-end="' + endNumber + '"]');
      if (flangeComponent) {
        var content = flangeComponent.querySelector('.subsea-builder__component-content');
        if (!content) return;

        // Read preloaded products
        var productsDataElement = document.getElementById('subsea-builder-products-data');
        if (!productsDataElement) return;
        var data;
        try { data = JSON.parse(productsDataElement.textContent); } catch(e) { return; }

        var fe = data.flanges_endcaps || {};
        var tubeMf = (tube && tube.metafields) || {};
        var tubeMaterial = normalizeMaterial(tubeMf.custom_bsub_material);
        var tubeDiameter = parseInt(tubeMf.custom_bsub_size);

        // Collect all variants from provided products
        function collectVariants(node) {
          var list = [];
          if (node && node.variants) {
            node.variants.forEach(function(v){
              v.product_title = node.title;
              v.product_handle = node.handle;
              v.product_image = node.featured_image;
              list.push(v);
            });
          }
          return list;
        }

        var flangeOnly = collectVariants(fe.subsea_aluminum_flange, 'aluminumFlange');
        var aluminumEndCap = collectVariants(fe.subsea_aluminum_end_cap);
        var domeEndCap = collectVariants(fe.subsea_optical_dome_end_cap);
        var glassAssembly = collectVariants(fe.subsea_optical_glass_flange_endcap);
        var polymerAssembly = collectVariants(fe.subsea_polymer_flange_endcap, 'polymerAssembly');

        // Attach stable flange type keys independent of localized metafields
        flangeOnly.forEach(function(v){ v._flangeTypeKey = 'flange_only'; });
        glassAssembly.forEach(function(v){ v._flangeTypeKey = 'assembly'; });
        polymerAssembly.forEach(function(v){ v._flangeTypeKey = 'assembly'; });

        // Filter flange-only by material/diameter
        var compatibleFlanges = flangeOnly.filter(function(v){
          var mf = v.metafields || {};
          var fitsMat = normalizeMaterialList(mf.custom_bsub_fits_material || []);
          var fitsDia = mf.custom_bsub_fits_diameter || [];
          var flangeSize = mf.custom_bsub_size; // Get the flange's own diameter
          // normalize diameter list to integers for reliable matching
          var fitsDiaNums = Array.isArray(fitsDia) ? fitsDia.map(function(x){ return parseInt(x); }).filter(function(n){ return !isNaN(n); }) : [];
          var type = v._flangeTypeKey || normalizeFlangeType(mf.custom_bsub_flange_type || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.flangeOnlyLabel) || "Flange Only"));
          var matOk = !fitsMat.length || (tubeMaterial && fitsMat.indexOf(tubeMaterial) !== -1);
          
          // Check diameter compatibility: either fits_diameter list matches OR flange size matches tube diameter
          var diaOk = false;
          if (fitsDiaNums.length > 0) {
            // Use fits_diameter list if available
            diaOk = tubeDiameter && fitsDiaNums.indexOf(tubeDiameter) !== -1;
          } else if (flangeSize) {
            // Fall back to direct size matching
            diaOk = tubeDiameter && parseInt(flangeSize) === tubeDiameter;
          } else {
            // No diameter restrictions
            diaOk = true;
          }
          
          
          return type === 'flange_only' && matOk && diaOk;
        });

        // Assemblies: match diameter only (no flange required)
        function filterAssemblies(list){
          return list.filter(function(v){
            var mf = v.metafields || {};
            var type = v._flangeTypeKey || normalizeFlangeType(mf.custom_bsub_flange_type || 'Assembly');
            var fitsDia = mf.custom_bsub_fits_diameter || [];
            var assemblySize = mf.custom_bsub_size; // Get the assembly's own diameter
            var fitsDiaNums = Array.isArray(fitsDia) ? fitsDia.map(function(x){ return parseInt(x); }).filter(function(n){ return !isNaN(n); }) : [];
            
            // Check diameter compatibility: either fits_diameter list matches OR assembly size matches tube diameter
            var diaOk = false;
            if (fitsDiaNums.length > 0) {
              // Use fits_diameter list if available
              diaOk = tubeDiameter && fitsDiaNums.indexOf(tubeDiameter) !== -1;
            } else if (assemblySize) {
              // Fall back to direct size matching
              diaOk = tubeDiameter && parseInt(assemblySize) === tubeDiameter;
            } else {
              // No diameter restrictions
              diaOk = true;
            }
            
            
            return type === 'assembly' && diaOk;
          });
        }

        var compatibleAssemblies = []
          .concat(filterAssemblies(glassAssembly))
          .concat(filterAssemblies(polymerAssembly));

        // Render
        var html = '<div class="subsea-builder__product-grid">';
        var self = this;

        function renderVariant(v, labelType){
          var price = self.formatPrice(v.price);
          var mf = v.metafields || {};
          var depth = mf.custom_bsub_depth_m || '—';
          var img = self.getImageWithFallback(v);
          
          // Get product metafields for availability
          var productMetafields = self.getProductMetafields(v);
          var inStockLeadTime = productMetafields.custom_in_stock_lead_time || 'N/A';
          var outOfStockLeadTime = productMetafields.custom_out_of_stock_lead_time || 'N/A';
          var availabilityHtml = self.createAvailabilityHtml(v, inStockLeadTime, outOfStockLeadTime);
          
          // Create product URL
          var productUrl = self.createProductUrl(v);
          
          // Get custom title and description from metafields (same as Tube Configuration)
          var customTitle = mf.custom_tube_builder_product_title || 
                           v.product_title || 
                           v.title || 
                           'N/A';
          
          // Handle description - should be base64 encoded string from Liquid template
          var customDescription = mf.custom_tube_builder_product_description || '';
          
          // If it's still an object (fallback), try to extract the value
          if (typeof customDescription === 'object' && customDescription !== null) {
            if (customDescription.value !== undefined) {
              customDescription = customDescription.value;
            } else if (customDescription.text !== undefined) {
              customDescription = customDescription.text;
            } else if (customDescription.content !== undefined) {
              customDescription = customDescription.content;
            } else if (customDescription.html !== undefined) {
              customDescription = customDescription.html;
            } else if (customDescription.raw !== undefined) {
              customDescription = customDescription.raw;
            } else {
              customDescription = '';
            }
          }
          
          // Ensure it's a string and decode if it's base64
          customDescription = String(customDescription || '');
          
          // Try to decode base64 if it looks like base64 content
          if (customDescription && self.isBase64(customDescription)) {
            try {
              customDescription = atob(customDescription);
            } catch (e) {
            }
          }
          
          // Get other required data (same as Tube Configuration)
          var depthRatingM = (mf.custom_bsub_depth_m != null && mf.custom_bsub_depth_m !== '') ? mf.custom_bsub_depth_m : 'N/A';
          var depthRating = depthRatingM !== 'N/A' ? 
            `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${self.formatNumber(depthRatingM)}m / ${self.formatNumber(Math.round(depthRatingM * 3.28084))}ft` : 
            'N/A';
          var sku = (v.sku && v.sku !== '') ? v.sku : 'N/A';
          var priceDisplay = self.formatPrice(v.price) ? `<strong>${self.formatPrice(v.price)}</strong>` : 'N/A';
          
          html += `
            <div class="subsea-builder__product-item" data-product-type="flange" data-variant-id="${v.id}" data-end="${endNumber}">
              <div class="subsea-builder__product-image">
                <a href="${productUrl}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                  <img src="${img}" alt="${customTitle}" class="subsea-builder__image">
                </a>
              </div>
              <div class="subsea-builder__product-info">
                <div class="subsea-builder__product-title">${customTitle}</div>
                ${customDescription ? `<div class="subsea-builder__product-description">${customDescription}</div>` : ''}
                <div class="subsea-builder__spec-grid">
                  <div class="subsea-builder__spec">${priceDisplay}</div>
                  <div class="subsea-builder__spec">${depthRating}</div>
                  <div class="subsea-builder__spec">${sku}</div>
                </div>
                <div class="subsea-builder__availability">${availabilityHtml}</div>
                <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisFlange) || "Select This Flange"}</button>
              </div>
            </div>`;
        }

        compatibleFlanges.forEach(function(v){ renderVariant(v, ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.flangeOnlyLabel) || "Flange Only")); });
        compatibleAssemblies.forEach(function(v){ renderVariant(v, ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.assemblyLabel) || "Assembly")); });

        if (!compatibleFlanges.length && !compatibleAssemblies.length) {
          try {
            console.groupCollapsed('[SubseaBuilder] No compatible flanges/assemblies');
            console.log('Tube diameter:', tubeDiameter, 'Tube material (key):', tubeMaterial);
            console.log('Inspecting flange-only variants:', flangeOnly.length);
            flangeOnly.forEach(function(v){
              var mf = v.metafields || {};
              var type = normalizeFlangeType(mf.custom_bsub_flange_type || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.flangeOnlyLabel) || "Flange Only"));
              var fitsMat = normalizeMaterialList(mf.custom_bsub_fits_material || []);
              var fitsDia = mf.custom_bsub_fits_diameter || [];
              var flangeSize = mf.custom_bsub_size;
              var fitsDiaNums = Array.isArray(fitsDia) ? fitsDia.map(function(x){ return parseInt(x); }).filter(function(n){ return !isNaN(n); }) : [];
              var matOk = !fitsMat.length || (tubeMaterial && fitsMat.indexOf(tubeMaterial) !== -1);
              var diaOk = false;
              if (fitsDiaNums.length > 0) {
                diaOk = tubeDiameter && fitsDiaNums.indexOf(tubeDiameter) !== -1;
              } else if (flangeSize) {
                diaOk = tubeDiameter && parseInt(flangeSize) === tubeDiameter;
              } else {
                diaOk = true;
              }
              console.log({
                id: v.id,
                title: v.title,
                type: type,
                fitsMat: fitsMat,
                tubeMaterial: tubeMaterial,
                matOk: matOk,
                fitsDiaNums: fitsDiaNums,
                flangeSize: flangeSize,
                tubeDiameter: tubeDiameter,
                diaOk: diaOk
              });
            });
            console.groupEnd();
          } catch(e) {}
          html += '<p>No compatible flange or assemblies found for this tube.</p>';
        }

        html += '</div>';
        content.innerHTML = html;

        // Bind select buttons inside flange grid
        var buttons = content.querySelectorAll('.select-component-btn');
        buttons.forEach(function(btn){
          btn.addEventListener('click', function(){
            var parentDiv = btn.closest('[data-variant-id]');
            var vid = parentDiv ? parentDiv.getAttribute('data-variant-id') : null;
            var parentEnd = parentDiv ? parentDiv.getAttribute('data-end') : null;
            
            // Verify this button belongs to the correct end
            if (parentEnd !== String(endNumber)) {
              console.log('Button click ignored - wrong end. Expected:', endNumber, 'Got:', parentEnd);
              return;
            }
            
            var all = compatibleFlanges.concat(compatibleAssemblies);
            var chosen = all.find(function(v){ return String(v.id) === String(vid); });
            if (chosen) {
              this.handleComponentSelection(chosen, 'flange', endNumber);
            }
          }.bind(this));
        }.bind(this));
      }
    },

    showCompatibleEndCaps: function(endNumber) {
      var tube = this.selectedComponents.tube;
      var flange = this.selectedComponents['end' + endNumber].flange;
      if (!tube || !flange) return;

      // Check if the selected flange is an assembly
      var isAssembly = (flange && flange._flangeTypeKey === 'assembly') || (flange.metafields && normalizeFlangeType(flange.metafields.custom_bsub_flange_type) === 'assembly');

      var endCapComponent = this.container.querySelector('[data-component="end-cap"][data-end="' + endNumber + '"]');
      if (endCapComponent) {
        var content = endCapComponent.querySelector('.subsea-builder__component-content');
        if (!content) return;

        // Read preloaded products
        var productsDataElement = document.getElementById('subsea-builder-products-data');
        if (!productsDataElement) return;
        var data;
        try { data = JSON.parse(productsDataElement.textContent); } catch(e) { return; }

        var fe = data.flanges_endcaps || {};
        var tubeMf = (tube && tube.metafields) || {};
        var flangeMf = (flange && flange.metafields) || {};
        var tubeDiameter = parseInt(tubeMf.custom_bsub_size);
        var flangeType = flange._flangeTypeKey || normalizeFlangeType(flangeMf.custom_bsub_flange_type);

        // Collect end-cap variants with whitelist filtering
        function collectVariants(node, whitelistKey) {
          var list = [];
          if (node && node.variants) {
            node.variants.forEach(function(v){
              // Apply whitelist if specified
              if (whitelistKey && ALLOWED_VARIANT_IDS[whitelistKey]) {
                var vIdNumber = Number(v.id);
                if (ALLOWED_VARIANT_IDS[whitelistKey].indexOf(vIdNumber) === -1) {
                  return; // Skip this variant if not in whitelist
                }
              }
              
              v.product_title = node.title;
              v.product_handle = node.handle;
              v.product_image = node.featured_image;
              list.push(v);
            });
          }
          return list;
        }

        var aluminumEndCap = collectVariants(fe.subsea_aluminum_end_cap, 'aluminumEndCap');
        var acrylicEndCap = collectVariants(fe.subsea_acrylic_end_cap, 'acrylicEndCap');
        var domeEndCap = collectVariants(fe.subsea_optical_dome_end_cap, 'opticalDomeEndCap');

        // Filter end-caps based on flange type and tube diameter
        var compatibleEndCaps = []
          .concat(aluminumEndCap)
          .concat(acrylicEndCap)
          .concat(domeEndCap)
          .filter(function(v){
            var mf = v.metafields || {};
            var requiresFlangeTypeRaw = mf.custom_requires_flange_type || [];
            var requiresFlangeType = Array.isArray(requiresFlangeTypeRaw)
              ? requiresFlangeTypeRaw.map(function(x){ return normalizeFlangeType(x); })
              : (typeof requiresFlangeTypeRaw === 'string' ? [normalizeFlangeType(requiresFlangeTypeRaw)] : []);
            var fitsDia = mf.custom_bsub_fits_diameter || [];
            var endCapSize = mf.custom_bsub_size;
            
            // Handle case where requiresFlangeType is an error object
            if (requiresFlangeType && typeof requiresFlangeType === 'object' && requiresFlangeType.error) {
              requiresFlangeType = [];
            }
            
            // Check if end-cap requires the selected flange type
            var flangeTypeOk = !requiresFlangeType.length || (flangeType && requiresFlangeType.indexOf(flangeType) !== -1);
            
            // Check diameter compatibility
            var diaOk = false;
            if (fitsDia && fitsDia.length > 0) {
              var fitsDiaNums = Array.isArray(fitsDia) ? fitsDia.map(function(x){ return parseInt(x); }).filter(function(n){ return !isNaN(n); }) : [];
              diaOk = tubeDiameter && fitsDiaNums.indexOf(tubeDiameter) !== -1;
            } else if (endCapSize) {
              diaOk = tubeDiameter && parseInt(endCapSize) === tubeDiameter;
            } else {
              diaOk = true;
            }
            
            
            return flangeTypeOk && diaOk;
          });

        // Render
        var html = '<div class="subsea-builder__product-grid">';
        var self = this;

        // If this is an assembly, show assembly message and skip end-cap filtering
        if (isAssembly) {
          html += '<p>No end-cap required - this is a complete assembly.</p>';
        } else {
          // Only show end-caps if it's not an assembly
          function renderEndCap(v){
            var price = self.formatPrice(v.price);
            var mf = v.metafields || {};
            var depth = mf.custom_bsub_depth_m || '—';
            var img = self.getImageWithFallback(v);
            
            // Get product metafields for availability
            var productMetafields = self.getProductMetafields(v);
            var inStockLeadTime = productMetafields.custom_in_stock_lead_time || 'N/A';
            var outOfStockLeadTime = productMetafields.custom_out_of_stock_lead_time || 'N/A';
            var availabilityHtml = self.createAvailabilityHtml(v, inStockLeadTime, outOfStockLeadTime);
            
            // Create product URL
            var productUrl = self.createProductUrl(v);
            
            // Get custom title and description from metafields (same as Tube Configuration)
            var customTitle = mf.custom_tube_builder_product_title || 
                             v.product_title || 
                             v.title || 
                             'N/A';
            
            // Handle description - should be base64 encoded string from Liquid template
            var customDescription = mf.custom_tube_builder_product_description || '';
            
            // If it's still an object (fallback), try to extract the value
            if (typeof customDescription === 'object' && customDescription !== null) {
              if (customDescription.value !== undefined) {
                customDescription = customDescription.value;
              } else if (customDescription.text !== undefined) {
                customDescription = customDescription.text;
              } else if (customDescription.content !== undefined) {
                customDescription = customDescription.content;
              } else if (customDescription.html !== undefined) {
                customDescription = customDescription.html;
              } else if (customDescription.raw !== undefined) {
                customDescription = customDescription.raw;
              } else {
                customDescription = '';
              }
            }
            
            // Ensure it's a string and decode if it's base64
            customDescription = String(customDescription || '');
            
            // Try to decode base64 if it looks like base64 content
            if (customDescription && self.isBase64(customDescription)) {
              try {
                customDescription = atob(customDescription);
              } catch (e) {
              }
            }
            
            // Get other required data (same as Tube Configuration)
            var depthRatingM = (mf.custom_bsub_depth_m != null && mf.custom_bsub_depth_m !== '') ? mf.custom_bsub_depth_m : 'N/A';
            var depthRating = depthRatingM !== 'N/A' ? 
              `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.depthRatingLabel) || "Depth Rating:")} ${self.formatNumber(depthRatingM)}m / ${self.formatNumber(Math.round(depthRatingM * 3.28084))}ft` : 
              'N/A';
            var sku = (v.sku && v.sku !== '') ? v.sku : 'N/A';
            var priceDisplay = self.formatPrice(v.price) ? `<strong>${self.formatPrice(v.price)}</strong>` : 'N/A';
            
            html += `
              <div class="subsea-builder__product-item" data-product-type="end-cap" data-variant-id="${v.id}" data-end="${endNumber}">
                <div class="subsea-builder__product-image">
                  <a href="${productUrl}" target="_blank" rel="noopener noreferrer" class="subsea-builder__product-image-link">
                    <img src="${img}" alt="${customTitle}" class="subsea-builder__image">
                  </a>
                </div>
                <div class="subsea-builder__product-info">
                  <div class="subsea-builder__product-title">${customTitle}</div>
                  ${customDescription ? `<div class="subsea-builder__product-description">${customDescription}</div>` : ''}
                  <div class="subsea-builder__spec-grid">
                    <div class="subsea-builder__spec">${priceDisplay}</div>
                    <div class="subsea-builder__spec">${depthRating}</div>
                    <div class="subsea-builder__spec">${sku}</div>
                  </div>
                  <div class="subsea-builder__availability">${availabilityHtml}</div>
                  <button class="btn btn--secondary select-component-btn">${(window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectThisEndcap) || "Select This End-Cap"}</button>
                </div>
              </div>`;
          }

          compatibleEndCaps.forEach(function(v){ renderEndCap(v); });

          if (!compatibleEndCaps.length) {
            console.log('Showing no compatible end-caps message for end', endNumber);
            html += '<p>' + ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noCompatibleEndcaps) || "No compatible end-caps found for this tube and flange combination.") + '</p>';
          }
        }

        html += '</div>';
        content.innerHTML = html;

        // Bind select buttons inside end-cap grid (only if not an assembly)
        if (!isAssembly) {
          var buttons = content.querySelectorAll('.select-component-btn');
          buttons.forEach(function(btn){
            btn.addEventListener('click', function(){
              var parentDiv = btn.closest('[data-variant-id]');
              var vid = parentDiv ? parentDiv.getAttribute('data-variant-id') : null;
              var parentEnd = parentDiv ? parentDiv.getAttribute('data-end') : null;
              
              // Verify this button belongs to the correct end
              if (parentEnd !== String(endNumber)) {
                console.log('Button click ignored - wrong end. Expected:', endNumber, 'Got:', parentEnd);
                return;
              }
              
              var chosen = compatibleEndCaps.find(function(v){ return String(v.id) === String(vid); });
              if (chosen) {
                this.handleComponentSelection(chosen, 'end-cap', endNumber);
              }
            }.bind(this));
          }.bind(this));
        }
      }
    },

    updateSummary: function() {
      // Update tube selection
      var tube = this.selectedComponents.tube;
      var tubeSummary = this.container.querySelector('[data-summary="tube"]');
      if (tubeSummary) {
        var tubeValue = tubeSummary.querySelector(selectors.summaryValue);
        var tubeSummaryDetails = tubeSummary.querySelector('.subsea-builder__summary-details');
        var tubeSummaryText = tubeSummary.querySelector('.subsea-builder__tube-summary');
        
        if (tubeValue) {
          if (tube) {
            var metafields = tube.metafields || {};
            var tubeBuilderSummary = metafields.custom_tube_builder_product_summary;
            
            // Show metafield content directly in main summary
            if (tubeBuilderSummary && typeof tubeBuilderSummary === 'string' && tubeBuilderSummary.trim()) {
              tubeValue.textContent = tubeBuilderSummary;
              tubeValue.setAttribute('data-tube-metafield', 'true');
            } else {
              tubeValue.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notConfigured) || "Not configured";
              tubeValue.removeAttribute('data-tube-metafield');
            }
            
            // Hide details box since we're showing content directly
            if (tubeSummaryDetails) {
              tubeSummaryDetails.style.display = 'none';
            }
          } else {
            tubeValue.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notSelected) || "Not selected";
            tubeValue.removeAttribute('data-tube-metafield');
            // Hide tube summary details when no tube selected
            if (tubeSummaryDetails) {
              tubeSummaryDetails.style.display = 'none';
            }
          }
        }
      }

      // Update End 1 selection
      var end1 = this.selectedComponents.end1;
      var end1Summary = this.container.querySelector('[data-summary="end1"]');
      if (end1Summary) {
        var end1Value = end1Summary.querySelector(selectors.summaryValue);
        var end1SummaryDetails = end1Summary.querySelector('.subsea-builder__summary-details');
        var productSummary1 = end1Summary.querySelector('.subsea-builder__product-summary');
        var productSummary2 = end1Summary.querySelector('.subsea-builder__product-summary-2');
        var productSeparator = end1Summary.querySelector('.subsea-builder__product-separator');
        
        if (end1Value) {
          if (end1.flange && end1['end-cap']) {
            // Both flange and end-cap selected
            var flangeMf = end1.flange.metafields || {};
            var endCapMf = end1['end-cap'].metafields || {};
            var flangeType = flangeMf.custom_bsub_flange_type || 'Unknown';
            var endCapType = endCapMf.custom_bsub_cap_type || 'Unknown';
            
            // Check if there's any metafield content
            var flangeSummary = flangeMf.custom_tube_builder_product_summary;
            var endCapSummary = endCapMf.custom_tube_builder_product_summary;
            var hasFlangeContent = flangeSummary && typeof flangeSummary === 'string' && flangeSummary.trim();
            var hasEndCapContent = endCapSummary && typeof endCapSummary === 'string' && endCapSummary.trim();
            var hasAnyContent = hasFlangeContent || hasEndCapContent;
            
            // Show metafield content directly in main summary
            if (hasAnyContent) {
              // Combine metafield content if both products have it
              var combinedContent = '';
              if (hasFlangeContent && hasEndCapContent) {
                combinedContent = flangeSummary + '\n' + endCapSummary;
              } else if (hasFlangeContent) {
                combinedContent = flangeSummary;
              } else if (hasEndCapContent) {
                combinedContent = endCapSummary;
              }
              end1Value.innerHTML = combinedContent.replace(/\n/g, '<br>');
              end1Value.setAttribute('data-tube-metafield', 'true');
            } else {
              end1Value.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notConfigured) || "Not configured";
              end1Value.removeAttribute('data-tube-metafield');
            }
            
            // Hide details box since we're showing content directly
            if (end1SummaryDetails) {
                end1SummaryDetails.style.display = 'none';
            }
          } else if (end1.flange) {
            // Only flange selected
            var flangeMf = end1.flange.metafields || {};
            var flangeType = flangeMf.custom_bsub_flange_type || 'Unknown';
            var flangeSummary = flangeMf.custom_tube_builder_product_summary;
            var hasFlangeContent = flangeSummary && typeof flangeSummary === 'string' && flangeSummary.trim();
            
            // Show metafield content directly in main summary
            if (hasFlangeContent) {
              end1Value.innerHTML = flangeSummary.replace(/\n/g, '<br>');
              end1Value.setAttribute('data-tube-metafield', 'true');
            } else {
              end1Value.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notConfigured) || "Not configured";
              end1Value.removeAttribute('data-tube-metafield');
            }
            
            // Hide details box since we're showing content directly
            if (end1SummaryDetails) {
                end1SummaryDetails.style.display = 'none';
            }
          } else {
            end1Value.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notConfigured) || "Not configured";
            end1Value.removeAttribute('data-tube-metafield');
            // Hide all product summaries
            if (end1SummaryDetails) {
              end1SummaryDetails.style.display = 'none';
            }
          }
        }
      }

      // Update End 2 selection
      var end2 = this.selectedComponents.end2;
      var end2Summary = this.container.querySelector('[data-summary="end2"]');
      if (end2Summary) {
        var end2Value = end2Summary.querySelector(selectors.summaryValue);
        var end2SummaryDetails = end2Summary.querySelector('.subsea-builder__summary-details');
        var productSummary1 = end2Summary.querySelector('.subsea-builder__product-summary');
        var productSummary2 = end2Summary.querySelector('.subsea-builder__product-summary-2');
        var productSeparator = end2Summary.querySelector('.subsea-builder__product-separator');
        
        if (end2Value) {
          if (end2.flange && end2['end-cap']) {
            // Both flange and end-cap selected
            var flangeMf = end2.flange.metafields || {};
            var endCapMf = end2['end-cap'].metafields || {};
            var flangeType = flangeMf.custom_bsub_flange_type || 'Unknown';
            var endCapType = endCapMf.custom_bsub_cap_type || 'Unknown';
            
            // Check if there's any metafield content
            var flangeSummary = flangeMf.custom_tube_builder_product_summary;
            var endCapSummary = endCapMf.custom_tube_builder_product_summary;
            var hasFlangeContent = flangeSummary && typeof flangeSummary === 'string' && flangeSummary.trim();
            var hasEndCapContent = endCapSummary && typeof endCapSummary === 'string' && endCapSummary.trim();
            var hasAnyContent = hasFlangeContent || hasEndCapContent;
            
            // Show metafield content directly in main summary
            if (hasAnyContent) {
              // Combine metafield content if both products have it
              var combinedContent = '';
              if (hasFlangeContent && hasEndCapContent) {
                combinedContent = flangeSummary + '\n' + endCapSummary;
              } else if (hasFlangeContent) {
                combinedContent = flangeSummary;
              } else if (hasEndCapContent) {
                combinedContent = endCapSummary;
              }
              end2Value.innerHTML = combinedContent.replace(/\n/g, '<br>');
              end2Value.setAttribute('data-tube-metafield', 'true');
            } else {
              end2Value.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notConfigured) || "Not configured";
              end2Value.removeAttribute('data-tube-metafield');
            }
            
            // Hide details box since we're showing content directly
            if (end2SummaryDetails) {
                end2SummaryDetails.style.display = 'none';
            }
          } else if (end2.flange) {
            // Only flange selected
            var flangeMf = end2.flange.metafields || {};
            var flangeType = flangeMf.custom_bsub_flange_type || 'Unknown';
            var flangeSummary = flangeMf.custom_tube_builder_product_summary;
            var hasFlangeContent = flangeSummary && typeof flangeSummary === 'string' && flangeSummary.trim();
            
            // Show metafield content directly in main summary
            if (hasFlangeContent) {
              end2Value.innerHTML = flangeSummary.replace(/\n/g, '<br>');
              end2Value.setAttribute('data-tube-metafield', 'true');
            } else {
              end2Value.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notConfigured) || "Not configured";
              end2Value.removeAttribute('data-tube-metafield');
            }
            
            // Hide details box since we're showing content directly
            if (end2SummaryDetails) {
                end2SummaryDetails.style.display = 'none';
            }
          } else {
            end2Value.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notConfigured) || "Not configured";
            end2Value.removeAttribute('data-tube-metafield');
            // Hide all product summaries
            if (end2SummaryDetails) {
              end2SummaryDetails.style.display = 'none';
            }
          }
        }
      }

      // Update Interior Fittings selection
      var interiorFittings = this.selectedComponents.interiorFittings;
      var interiorFittingsSummary = this.container.querySelector('[data-summary="interior-fittings"]');
      if (interiorFittingsSummary) {
        var interiorFittingsValue = interiorFittingsSummary.querySelector(selectors.summaryValue);
        var interiorFittingsSummaryDetails = interiorFittingsSummary.querySelector('.subsea-builder__summary-details');
        var productSummary1 = interiorFittingsSummary.querySelector('.subsea-builder__product-summary');
        var productSummary2 = interiorFittingsSummary.querySelector('.subsea-builder__product-summary-2');
        var productSummary3 = interiorFittingsSummary.querySelector('.subsea-builder__product-summary-3');
        var productSeparator = interiorFittingsSummary.querySelector('.subsea-builder__product-separator');
        var productSeparator3 = interiorFittingsSummary.querySelector('.subsea-builder__product-separator-3');
        
        if (interiorFittingsValue) {
          var selectedItems = [];
          
          if (interiorFittings.electronicTray) {
            selectedItems.push((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.electronicTraySetLabel) || "Electronic Tray Set");
          }
          
          if (interiorFittings.pcbShelf) {
            selectedItems.push('PCB Shelf');
          }
          
          if (interiorFittings.pcbShelfSecondary) {
            selectedItems.push('PCB Shelf - Secondary');
          }
          
          if (selectedItems.length > 0) {
            // Check if there's any metafield content
            var traySummary = '';
            var shelfSummary = '';
            var shelfSecondarySummary = '';
            var hasTrayContent = false;
            var hasShelfContent = false;
            var hasShelfSecondaryContent = false;
            
            if (interiorFittings.electronicTray) {
              var trayMf = interiorFittings.electronicTray.metafields || {};
              traySummary = trayMf.custom_tube_builder_product_summary;
              hasTrayContent = traySummary && typeof traySummary === 'string' && traySummary.trim();
            }
            
            if (interiorFittings.pcbShelf) {
              var shelfMf = interiorFittings.pcbShelf.metafields || {};
              shelfSummary = shelfMf.custom_tube_builder_product_summary;
              hasShelfContent = shelfSummary && typeof shelfSummary === 'string' && shelfSummary.trim();
            }
            
            if (interiorFittings.pcbShelfSecondary) {
              var shelfSecondaryMf = interiorFittings.pcbShelfSecondary.metafields || {};
              shelfSecondarySummary = shelfSecondaryMf.custom_tube_builder_product_summary;
              hasShelfSecondaryContent = shelfSecondarySummary && typeof shelfSecondarySummary === 'string' && shelfSecondarySummary.trim();
            }
            
            var hasAnyContent = hasTrayContent || hasShelfContent || hasShelfSecondaryContent;
            
            // Show metafield content directly in main summary
            if (hasAnyContent) {
              // Combine metafield content if products have it
              var combinedContent = '';
              var contentParts = [];
              if (hasTrayContent) contentParts.push(traySummary);
              if (hasShelfContent) contentParts.push(shelfSummary);
              if (hasShelfSecondaryContent) contentParts.push(shelfSecondarySummary);
              combinedContent = contentParts.join('\n');
              
              interiorFittingsValue.innerHTML = combinedContent.replace(/\n/g, '<br>');
              interiorFittingsValue.setAttribute('data-tube-metafield', 'true');
                  } else {
              interiorFittingsValue.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notConfigured) || "Not configured";
              interiorFittingsValue.removeAttribute('data-tube-metafield');
            }
            
            // Hide details box since we're showing content directly
            if (interiorFittingsSummaryDetails) {
                interiorFittingsSummaryDetails.style.display = 'none';
            }
          } else {
            interiorFittingsValue.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noneSelected) || "None selected";
            interiorFittingsValue.removeAttribute('data-tube-metafield');
            // Hide all product summaries
            if (interiorFittingsSummaryDetails) {
              interiorFittingsSummaryDetails.style.display = 'none';
            }
          }
        }
      }

      // Update Vent & Vacuum Control selection
      var ventVacuumControl = this.selectedComponents.ventVacuumControl;
      var ventVacuumControlSummary = this.container.querySelector('[data-summary="vent-vacuum-control"]');
      if (ventVacuumControlSummary) {
        var ventVacuumControlValue = ventVacuumControlSummary.querySelector(selectors.summaryValue);
        var ventVacuumControlSummaryDetails = ventVacuumControlSummary.querySelector('.subsea-builder__summary-details');
        var productSummary1 = ventVacuumControlSummary.querySelector('.subsea-builder__product-summary');
        var productSummary2 = ventVacuumControlSummary.querySelector('.subsea-builder__product-summary-2');
        var productSeparator = ventVacuumControlSummary.querySelector('.subsea-builder__product-separator');
        
        if (ventVacuumControlValue) {
          var selectedItems = [];
          
          if (ventVacuumControl.vent) {
            selectedItems.push((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.ventLabel) || "Vent");
          }
          
          if (ventVacuumControl.vacuumPlug) {
            selectedItems.push((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.vacuumPlugLabel) || "Vacuum Plug");
          }
          
          if (selectedItems.length > 0) {
            // Check if there's any metafield content
            var ventSummary = '';
            var plugSummary = '';
            var hasVentContent = false;
            var hasPlugContent = false;
            
            if (ventVacuumControl.vent) {
              var ventMf = ventVacuumControl.vent.metafields || {};
              ventSummary = ventMf.custom_tube_builder_product_summary;
              hasVentContent = ventSummary && typeof ventSummary === 'string' && ventSummary.trim();
            }
            
            if (ventVacuumControl.vacuumPlug) {
              var plugMf = ventVacuumControl.vacuumPlug.metafields || {};
              plugSummary = plugMf.custom_tube_builder_product_summary;
              hasPlugContent = plugSummary && typeof plugSummary === 'string' && plugSummary.trim();
            }
            
            var hasAnyContent = hasVentContent || hasPlugContent;
            
            // Show metafield content directly in main summary
            if (hasAnyContent) {
              // Combine metafield content if both products have it
              var combinedContent = '';
              if (hasVentContent && hasPlugContent) {
                combinedContent = ventSummary + '\n' + plugSummary;
              } else if (hasVentContent) {
                combinedContent = ventSummary;
              } else if (hasPlugContent) {
                combinedContent = plugSummary;
              }
              ventVacuumControlValue.innerHTML = combinedContent.replace(/\n/g, '<br>');
              ventVacuumControlValue.setAttribute('data-tube-metafield', 'true');
                  } else {
              ventVacuumControlValue.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notConfigured) || "Not configured";
              ventVacuumControlValue.removeAttribute('data-tube-metafield');
            }
            
            // Hide details box since we're showing content directly
            if (ventVacuumControlSummaryDetails) {
                ventVacuumControlSummaryDetails.style.display = 'none';
            }
          } else {
            ventVacuumControlValue.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.noneSelected) || "None selected";
            ventVacuumControlValue.removeAttribute('data-tube-metafield');
            // Hide all product summaries
            if (ventVacuumControlSummaryDetails) {
              ventVacuumControlSummaryDetails.style.display = 'none';
            }
          }
        }
      }

      // Update total price
      this.calculateTotalPrice();
      
      // Update lead time

      // Update locking-end status
      this.updateLockingEnd();
      
      // Update depth rating
      this.updateDepthRating();
      
      // Update add to cart button
      this.updateAddToCartButton();
    },

    updateLockingEnd: function() {
      var tube = this.selectedComponents.tube;
      var summaryItem = this.container.querySelector('[data-summary="locking-end"]');
      if (!summaryItem) return;
      var valueEl = summaryItem.querySelector('.subsea-builder__summary-value');
      if (!valueEl) return;

      if (!tube) {
        valueEl.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notApplicable) || "Not applicable";
        return;
      }
      
      // Check if we have any flange/assembly selected in either end
      var end1Flange = this.selectedComponents.end1.flange;
      var end2Flange = this.selectedComponents.end2.flange;
      
      if (!end1Flange && !end2Flange) {
        valueEl.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notApplicable) || "Not applicable";
        return;
      }

      var tubeMf = (tube.metafields || {});
      
      // Handle different possible formats for custom_locking_end
      var tubeLocking = false;
      
      if (tubeMf.custom_locking_end === true || tubeMf.custom_locking_end === 'true') {
        tubeLocking = true;
      } else if (tubeMf.custom_locking_end && typeof tubeMf.custom_locking_end === 'object') {
        // Check if it's a JSON error object
        if (tubeMf.custom_locking_end.error) {
          tubeLocking = false; // Treat JSON errors as false
        } else {
          // If it's an object, check if it has a value property or is truthy
          tubeLocking = !!tubeMf.custom_locking_end.value || !!tubeMf.custom_locking_end;
        }
      }
      
      // Check flange/assembly locking for each end that has a selection
      var end1Locking = false;
      var end2Locking = false;
      
      if (end1Flange) {
        var end1Mf = (end1Flange.metafields || {});
        if (end1Mf.custom_locking_end === true || end1Mf.custom_locking_end === 'true') {
          end1Locking = true;
        } else if (end1Mf.custom_locking_end && typeof end1Mf.custom_locking_end === 'object') {
          if (end1Mf.custom_locking_end.error) {
            end1Locking = false;
          } else {
            end1Locking = !!end1Mf.custom_locking_end.value || !!end1Mf.custom_locking_end;
          }
        }
      }
      
      if (end2Flange) {
        var end2Mf = (end2Flange.metafields || {});
        if (end2Mf.custom_locking_end === true || end2Mf.custom_locking_end === 'true') {
          end2Locking = true;
        } else if (end2Mf.custom_locking_end && typeof end2Mf.custom_locking_end === 'object') {
          if (end2Mf.custom_locking_end.error) {
            end2Locking = false;
          } else {
            end2Locking = !!end2Mf.custom_locking_end.value || !!end2Mf.custom_locking_end;
          }
        }
      }
      
      // Assembly is considered "locking" only if both the tube AND the flange/assembly have locking_end = true
      var isLocking = false;
      if (end1Flange && end2Flange) {
        // Both ends configured - both must be locking
        isLocking = !!(tubeLocking && end1Locking && end2Locking);
      } else if (end1Flange) {
        // Only End 1 configured
        isLocking = !!(tubeLocking && end1Locking);
      } else if (end2Flange) {
        // Only End 2 configured
        isLocking = !!(tubeLocking && end2Locking);
      }
      
      
      valueEl.textContent = isLocking ? ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.yes) || "Yes") : ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.no) || "No");
    },

    updateDepthRating: function() {
      var summaryItem = this.container.querySelector('[data-summary="depth-rating"]');
      if (!summaryItem) return;
      var valueEl = summaryItem.querySelector('.subsea-builder__summary-value');
      if (!valueEl) return;

      // Collect ALL selected components including nested end components
      var allSelectedComponents = [];
      
      // Add tube if selected
      if (this.selectedComponents.tube) {
        allSelectedComponents.push(this.selectedComponents.tube);
      }
      
      // Add End 1 components
      if (this.selectedComponents.end1.flange) {
        allSelectedComponents.push(this.selectedComponents.end1.flange);
      }
      if (this.selectedComponents.end1['end-cap']) {
        allSelectedComponents.push(this.selectedComponents.end1['end-cap']);
      }
      
      // Add End 2 components
      if (this.selectedComponents.end2.flange) {
        allSelectedComponents.push(this.selectedComponents.end2.flange);
      }
      if (this.selectedComponents.end2['end-cap']) {
        allSelectedComponents.push(this.selectedComponents.end2['end-cap']);
      }
      
      // Add Interior Fittings
      if (this.selectedComponents.interiorFittings.electronicTray) {
        allSelectedComponents.push(this.selectedComponents.interiorFittings.electronicTray);
      }
      if (this.selectedComponents.interiorFittings.pcbShelf) {
        allSelectedComponents.push(this.selectedComponents.interiorFittings.pcbShelf);
      }

      // Add Vent & Vacuum Control
      if (this.selectedComponents.ventVacuumControl.vent) {
        allSelectedComponents.push(this.selectedComponents.ventVacuumControl.vent);
      }
      if (this.selectedComponents.ventVacuumControl.vacuumPlug) {
        allSelectedComponents.push(this.selectedComponents.ventVacuumControl.vacuumPlug);
      }

      if (allSelectedComponents.length === 0) {
        valueEl.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notCalculated) || "Not calculated";
        return;
      }

      // Use the existing lead time logic - find the minimum depth rating (excluding Interior Fittings and Vent & Vacuum Control)
      var depthRatingComponents = allSelectedComponents.filter(function(component) {
        // Exclude Interior Fittings and Vent & Vacuum Control from depth rating calculation
        return !component.isInteriorFitting && !component.isVentVacuumControl;
      });
      
      var minDepthRating = Math.min.apply(null, depthRatingComponents.map(function(component) {
        var metafields = component.metafields || {};
        return parseInt(metafields.custom_bsub_depth_m) || 999999;
      }));

      if (minDepthRating === 999999) {
        valueEl.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.notCalculated) || "Not calculated";
      } else {
        var depthRatingText = `${this.formatNumber(minDepthRating)}m / ${this.formatNumber(Math.round(minDepthRating * 3.28084))}ft`;
        valueEl.textContent = depthRatingText;
      }
    },

    calculateTotalPrice: function() {
      this.totalPrice = 0;
      
      // Add tube price
      if (this.selectedComponents.tube) {
        this.totalPrice += this.selectedComponents.tube.price;
      }
      
      // Add End 1 components
      if (this.selectedComponents.end1.flange) {
        this.totalPrice += this.selectedComponents.end1.flange.price;
      }
      if (this.selectedComponents.end1['end-cap']) {
        this.totalPrice += this.selectedComponents.end1['end-cap'].price;
      }
      
      // Add End 2 components
      if (this.selectedComponents.end2.flange) {
        this.totalPrice += this.selectedComponents.end2.flange.price;
      }
      if (this.selectedComponents.end2['end-cap']) {
        this.totalPrice += this.selectedComponents.end2['end-cap'].price;
      }
      
      // Add Interior Fittings
      if (this.selectedComponents.interiorFittings.electronicTray) {
        this.totalPrice += this.selectedComponents.interiorFittings.electronicTray.price;
      }
      if (this.selectedComponents.interiorFittings.pcbShelf) {
        this.totalPrice += this.selectedComponents.interiorFittings.pcbShelf.price;
      }
      if (this.selectedComponents.interiorFittings.pcbShelfSecondary) {
        this.totalPrice += this.selectedComponents.interiorFittings.pcbShelfSecondary.price;
      }

      // Add Vent & Vacuum Control
      if (this.selectedComponents.ventVacuumControl.vent) {
        this.totalPrice += this.selectedComponents.ventVacuumControl.vent.price;
      }
      if (this.selectedComponents.ventVacuumControl.vacuumPlug) {
        this.totalPrice += this.selectedComponents.ventVacuumControl.vacuumPlug.price;
      }

      var totalPriceElement = this.container.querySelector(selectors.totalPrice);
      if (totalPriceElement) {
        totalPriceElement.textContent = this.formatPrice(this.totalPrice);
      }
    },

    calculateLeadTime: function() {
      var leadTimeElement = this.container.querySelector(selectors.leadTime);
      if (!leadTimeElement) return;

      // Collect ALL selected components including nested end components
      var allSelectedComponents = [];
      
      // Add tube if selected
      if (this.selectedComponents.tube) {
        allSelectedComponents.push(this.selectedComponents.tube);
      }
      
      // Add End 1 components
      if (this.selectedComponents.end1.flange) {
        allSelectedComponents.push(this.selectedComponents.end1.flange);
      }
      if (this.selectedComponents.end1['end-cap']) {
        allSelectedComponents.push(this.selectedComponents.end1['end-cap']);
      }
      
      // Add End 2 components
      if (this.selectedComponents.end2.flange) {
        allSelectedComponents.push(this.selectedComponents.end2.flange);
      }
      if (this.selectedComponents.end2['end-cap']) {
        allSelectedComponents.push(this.selectedComponents.end2['end-cap']);
      }
      
      // Add Interior Fittings
      if (this.selectedComponents.interiorFittings.electronicTray) {
        allSelectedComponents.push(this.selectedComponents.interiorFittings.electronicTray);
      }
      if (this.selectedComponents.interiorFittings.pcbShelf) {
        allSelectedComponents.push(this.selectedComponents.interiorFittings.pcbShelf);
      }

      // Add Vent & Vacuum Control
      if (this.selectedComponents.ventVacuumControl.vent) {
        allSelectedComponents.push(this.selectedComponents.ventVacuumControl.vent);
      }
      if (this.selectedComponents.ventVacuumControl.vacuumPlug) {
        allSelectedComponents.push(this.selectedComponents.ventVacuumControl.vacuumPlug);
      }

      if (allSelectedComponents.length === 0) {
        leadTimeElement.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.selectComponentsToSeeLeadTime) || "Select components to see lead time";
        return;
      }

      // Use your existing lead time logic - find the minimum depth rating (excluding Interior Fittings and Vent & Vacuum Control)
      var depthRatingComponents = allSelectedComponents.filter(function(component) {
        // Exclude Interior Fittings and Vent & Vacuum Control from depth rating calculation
        return !component.isInteriorFitting && !component.isVentVacuumControl;
      });
      
      var minDepthRating = Math.min.apply(null, depthRatingComponents.map(function(component) {
        var metafields = component.metafields || {};
        return parseInt(metafields.custom_bsub_depth_m) || 999999;
      }));

      // Show lead time based on inventory status
      var hasInStock = allSelectedComponents.some(function(component) {
        return component.inventory_quantity > 0;
      });

      var hasOutOfStock = allSelectedComponents.some(function(component) {
        return component.inventory_quantity <= 0 && component.inventory_policy === 'continue';
      });

      var leadTimeText = '';
      if (hasInStock && hasOutOfStock) {
        leadTimeText = `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.mixedAvailabilityLabel) || "Mixed availability")} - ${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.minDepthRatingLabel) || "Min depth rating:")} ${minDepthRating}m`;
      } else if (hasInStock) {
        var labels = (window.theme && window.theme.strings && window.theme.strings.leadTime) || {};
        var quickShip = (labels.quickShip || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.quickShip) || "Quick Ship"));
        var madeToOrder = (labels.madeToOrder || ((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.madeToOrder) || "Made to Order"));
        leadTimeText = `${quickShip} ${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.availableLabel) || "available")} - ${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.minDepthRatingLabel) || "Min depth rating:")} ${minDepthRating}m`;
      } else if (hasOutOfStock) {
        leadTimeText = `${madeToOrder} - ${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.minDepthRatingLabel) || "Min depth rating:")} ${minDepthRating}m`;
      } else {
        leadTimeText = `${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.contactForAvailabilityLabel) || "Contact for availability")} - ${((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.minDepthRatingLabel) || "Min depth rating:")} ${minDepthRating}m`;
      }

      leadTimeElement.textContent = leadTimeText;
    },

    updateAddToCartButton: function() {
      var addToCartBtn = this.container.querySelector(selectors.addToCartBtn);
      if (addToCartBtn) {
        // Check if configuration is complete
        var isComplete = this.isConfigurationComplete();
        
        // Remove all state classes
        addToCartBtn.classList.remove('subsea-builder__add-to-cart--incomplete', 'subsea-builder__add-to-cart--complete');
        
        if (isComplete) {
          // Configuration is complete - green button, enabled
          addToCartBtn.classList.add('subsea-builder__add-to-cart--complete');
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.addAllToCart) || "✓ Add All to Cart";
          
          // Highlight important final values
          this.highlightFinalValues();
        } else {
          // Configuration is incomplete - grey button, disabled
          addToCartBtn.classList.add('subsea-builder__add-to-cart--incomplete');
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.completeConfigurationBeforeAdding) || "Complete configuration before adding to cart";
          
          // Remove highlighting
          this.removeHighlighting();
        }
      }
    },

    highlightFinalValues: function() {
      // Check if highlighting is already applied
      var existingContainer = this.container.querySelector('.subsea-builder__final-values-container');
      if (existingContainer) {
        return; // Already highlighted, don't create duplicate
      }
      
      // Create a single container for both Depth Rating and Locking End
      var depthRatingItem = this.container.querySelector('[data-summary="depth-rating"]');
      var lockingEndItem = this.container.querySelector('[data-summary="locking-end"]');
      
      if (depthRatingItem && lockingEndItem) {
        // Check if items are already inside a container (safety check)
        if (depthRatingItem.closest('.subsea-builder__final-values-container') || 
            lockingEndItem.closest('.subsea-builder__final-values-container')) {
          return; // Items are already in a container, don't create duplicate
        }
        
        // Create a wrapper container
        var container = document.createElement('div');
        container.className = 'subsea-builder__final-values-container';
        
        // Insert the container before the depth rating item
        depthRatingItem.parentNode.insertBefore(container, depthRatingItem);
        
        // Move both items into the container
        container.appendChild(depthRatingItem);
        container.appendChild(lockingEndItem);
      }
    },

    removeHighlighting: function() {
      // Find and remove ALL containers (in case of duplicates)
      var containers = this.container.querySelectorAll('.subsea-builder__final-values-container');
      
      for (var i = 0; i < containers.length; i++) {
        var container = containers[i];
        var depthRatingItem = container.querySelector('[data-summary="depth-rating"]');
        var lockingEndItem = container.querySelector('[data-summary="locking-end"]');
        
        // Move items back to their original positions
        if (depthRatingItem) {
          container.parentNode.insertBefore(depthRatingItem, container);
        }
        if (lockingEndItem) {
          container.parentNode.insertBefore(lockingEndItem, container);
        }
        
        // Remove the container
        container.remove();
      }
    },

    isConfigurationComplete: function() {
      // Check if all required sections are collapsed (which indicates completion)
      
      // Must have tube selected and collapsed
      var tubeCollapsed = this.container.querySelector('.subsea-builder__selected-tube');
      if (!tubeCollapsed) return false;
      
      // Must have at least one end configured and collapsed
      var end1Collapsed = this.container.querySelector('.subsea-builder__selected-end1');
      var end2Collapsed = this.container.querySelector('.subsea-builder__selected-end2');
      if (!end1Collapsed && !end2Collapsed) return false;
      
      // Must have vent & vacuum control configured and collapsed
      var ventVacuumCollapsed = this.container.querySelector('.subsea-builder__selected-vent-vacuum');
      if (!ventVacuumCollapsed) return false;
      
      return true;
    },

    getMissingConfigurationItems: function() {
      var missing = [];
      
      // Check for collapsed sections (which indicate completion)
      var tubeCollapsed = this.container.querySelector('.subsea-builder__selected-tube');
      var end1Collapsed = this.container.querySelector('.subsea-builder__selected-end1');
      var end2Collapsed = this.container.querySelector('.subsea-builder__selected-end2');
      var ventVacuumCollapsed = this.container.querySelector('.subsea-builder__selected-vent-vacuum');
      
      if (!tubeCollapsed) {
        missing.push('Tube');
      } else {
        if (!end1Collapsed && !end2Collapsed) {
          missing.push((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.endConfiguration) || "End Configuration");
        }
        
        if (!ventVacuumCollapsed) {
          missing.push('Vent & Vacuum Control');
        }
      }
      
      return missing;
    },

    handleAddToCart: function() {
      // Check if configuration is complete
      if (!this.isConfigurationComplete()) {
        console.log('Configuration not complete, cannot add to cart');
        return;
      }

      // Show loading state
      this.setAddToCartLoading(true);

      // Collect all selected components
      var componentsToAdd = this.collectSelectedComponents();
      
      if (componentsToAdd.length === 0) {
        this.setAddToCartLoading(false);
        this.showCartMessage('No components selected', 'error');
        return;
      }


      // Add components to cart sequentially
      this.addComponentsToCart(componentsToAdd, 0);
    },

    collectSelectedComponents: function() {
      var components = [];

      // Add tube
      if (this.selectedComponents.tube) {
        components.push({
          variantId: this.selectedComponents.tube.id,
          quantity: 1,
          properties: this.buildComponentProperties('tube', this.selectedComponents.tube)
        });
      }

      // Add end1 components
      if (this.selectedComponents.end1) {
        if (this.selectedComponents.end1.flange) {
          components.push({
            variantId: this.selectedComponents.end1.flange.id,
            quantity: 1,
            properties: this.buildComponentProperties('end1-flange', this.selectedComponents.end1.flange)
          });
        }
        if (this.selectedComponents.end1['end-cap']) {
          components.push({
            variantId: this.selectedComponents.end1['end-cap'].id,
            quantity: 1,
            properties: this.buildComponentProperties('end1-end-cap', this.selectedComponents.end1['end-cap'])
          });
        }
      }

      // Add end2 components
      if (this.selectedComponents.end2) {
        if (this.selectedComponents.end2.flange) {
          components.push({
            variantId: this.selectedComponents.end2.flange.id,
            quantity: 1,
            properties: this.buildComponentProperties('end2-flange', this.selectedComponents.end2.flange)
          });
        }
        if (this.selectedComponents.end2['end-cap']) {
          components.push({
            variantId: this.selectedComponents.end2['end-cap'].id,
            quantity: 1,
            properties: this.buildComponentProperties('end2-end-cap', this.selectedComponents.end2['end-cap'])
          });
        }
      }

      // Add interior fittings
      if (this.selectedComponents.interiorFittings) {
        if (this.selectedComponents.interiorFittings.electronicTray) {
          components.push({
            variantId: this.selectedComponents.interiorFittings.electronicTray.id,
            quantity: 1,
            properties: this.buildComponentProperties('interior-fittings-electronic-tray', this.selectedComponents.interiorFittings.electronicTray)
          });
        }
        if (this.selectedComponents.interiorFittings.pcbShelf) {
          components.push({
            variantId: this.selectedComponents.interiorFittings.pcbShelf.id,
            quantity: 1,
            properties: this.buildComponentProperties('interior-fittings-pcb-shelf', this.selectedComponents.interiorFittings.pcbShelf)
          });
        }
        if (this.selectedComponents.interiorFittings.pcbShelfSecondary) {
          components.push({
            variantId: this.selectedComponents.interiorFittings.pcbShelfSecondary.id,
            quantity: 1,
            properties: this.buildComponentProperties('interior-fittings-pcb-shelf-secondary', this.selectedComponents.interiorFittings.pcbShelfSecondary)
          });
        }
      }

      // Add vent & vacuum control
      if (this.selectedComponents.ventVacuumControl) {
        if (this.selectedComponents.ventVacuumControl.vent) {
          components.push({
            variantId: this.selectedComponents.ventVacuumControl.vent.id,
            quantity: 1,
            properties: this.buildComponentProperties('vent-vacuum-vent', this.selectedComponents.ventVacuumControl.vent)
          });
        }
        if (this.selectedComponents.ventVacuumControl.vacuumPlug) {
          components.push({
            variantId: this.selectedComponents.ventVacuumControl.vacuumPlug.id,
            quantity: 1,
            properties: this.buildComponentProperties('vent-vacuum-vacuum-plug', this.selectedComponents.ventVacuumControl.vacuumPlug)
          });
        }
      }

      return components;
    },

    buildComponentProperties: function(componentType, variant) {
      // Get lead times from product level metafields (matching product form logic)
      var inStockLeadTime = '2 Business Days';
      var outOfStockLeadTime = '3 Weeks';
      
      // Use the existing getProductMetafields function to get product-level metafields
      var productMetafields = this.getProductMetafields(variant);
      if (productMetafields.custom_in_stock_lead_time) {
        inStockLeadTime = productMetafields.custom_in_stock_lead_time;
      }
      if (productMetafields.custom_out_of_stock_lead_time) {
        outOfStockLeadTime = productMetafields.custom_out_of_stock_lead_time;
      }

      // Handle inventory policy exactly like product form (continue / deny strings)
      var continueSelling = variant.inventory_policy === 'continue' ? 'continue' : 'deny';

      var rawInv = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : parseInt(variant.inventory_quantity, 10);
      if (!Number.isFinite(rawInv)) {
        rawInv = 0;
      }

      var properties = {
        '_component_type': componentType,
        '_configuration_id': this.generateConfigurationId(),
        '_inventory_quantity': Math.max(0, rawInv),
        '_continue_selling': continueSelling,
        '_in_stock_lead_time': inStockLeadTime,
        '_out_of_stock_lead_time': outOfStockLeadTime
      };

      // Add component-specific properties
      if (variant.metafields) {
        if (variant.metafields.custom_bsub_material) {
          properties['_material'] = variant.metafields.custom_bsub_material;
        }
        if (variant.metafields.custom_bsub_size) {
          properties['_size'] = variant.metafields.custom_bsub_size;
        }
        if (variant.metafields.custom_bsub_length_mm) {
          properties['_length_mm'] = variant.metafields.custom_bsub_length_mm;
        }
        if (variant.metafields.custom_bsub_depth_m) {
          properties['_depth_rating'] = variant.metafields.custom_bsub_depth_m;
        }
        if (variant.metafields.custom_locking_end) {
          properties['_locking_end'] = variant.metafields.custom_locking_end;
        }
        if (variant.metafields.custom_tube_builder_product_title) {
          properties['_product_title'] = variant.metafields.custom_tube_builder_product_title;
        }
        if (variant.metafields.custom_tube_builder_product_summary) {
          properties['_product_summary'] = variant.metafields.custom_tube_builder_product_summary;
        }
      }

      return properties;
    },

    generateConfigurationId: function() {
      return 'subsea-config-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    },

    addComponentsToCart: function(components, index) {
      if (index >= components.length) {
        // All components added successfully
        this.setAddToCartLoading(false);
        this.showCartMessage((window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.allComponentsAddedToCartSuccessfully) || 'All components added to cart successfully!', 'success');
        
        // Dispatch cart updated event
        document.dispatchEvent(new CustomEvent('cart:updated'));
        return;
      }

      var component = components[index];
      
      // Create form data exactly like product form does
      var formData = new FormData();
      formData.append('id', component.variantId);
      formData.append('quantity', component.quantity);
      
      // Add properties exactly like product form
      Object.keys(component.properties).forEach(function(key) {
        formData.append('properties[' + key + ']', component.properties[key]);
      });

      // Use the same fetch format as product form
      fetch(theme.routes.cartAdd, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
        if (data.status === 422) {
          // Error adding this component
          console.error('Error adding component to cart:', data);
          this.setAddToCartLoading(false);
          this.showCartMessage('Error adding ' + component.properties._component_type + ' to cart: ' + data.message, 'error');
        } else {
          // Success, add next component
          this.addComponentsToCart(components, index + 1);
        }
      }.bind(this))
      .catch(function(error) {
        console.error('Network error adding component to cart:', error);
        this.setAddToCartLoading(false);
        this.showCartMessage('Network error adding components to cart', 'error');
      }.bind(this));
    },

    setAddToCartLoading: function(loading) {
      var addToCartBtn = this.container.querySelector(selectors.addToCartBtn);
      if (addToCartBtn) {
        if (loading) {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.addingToCart) || "Adding to Cart...";
          addToCartBtn.classList.add('is-loading');
        } else {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = (window.theme && window.theme.strings && window.theme.strings.builder && window.theme.strings.builder.addAllToCart) || "✓ Add All to Cart";
          addToCartBtn.classList.remove('is-loading');
        }
      }
    },

    showCartMessage: function(message, type) {
      // Create or update message element
      var messageEl = this.container.querySelector('.subsea-builder__cart-message');
      if (!messageEl) {
        messageEl = document.createElement('div');
        messageEl.className = 'subsea-builder__cart-message';
        var addToCartBtn = this.container.querySelector(selectors.addToCartBtn);
        if (addToCartBtn) {
          addToCartBtn.parentNode.insertBefore(messageEl, addToCartBtn.nextSibling);
        }
      }

      messageEl.textContent = message;
      messageEl.className = 'subsea-builder__cart-message subsea-builder__cart-message--' + type;
      
      // Auto-hide after 5 seconds
      setTimeout(function() {
        if (messageEl && messageEl.parentNode) {
          messageEl.parentNode.removeChild(messageEl);
        }
      }, 5000);
    }
  };

  return SubseaBuilder;
})();

// Initialize the builder when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  var builderContainers = document.querySelectorAll('[data-section-type="subsea-enclosure-builder"]');
  builderContainers.forEach(function(container) {
    new theme.SubseaBuilder(container);
  });
});